// BUILD r2.4 — "Needs You" → "Needs attention"; queue rows carry richer context (asking-about + recap snippet) — 2026-08-07
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Phone,
  Copy,
  Check,
  MessageSquare,
  Clock,
  PhoneForwarded,
  PhoneCall,
  AlertCircle,
  Loader2,
  HelpCircle,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  PlayCircle,
  ExternalLink,
  PhoneIncoming,
  RefreshCw,
  BookOpen,
  X,
  Plus,
  Trash2,
  CalendarDays,
  Sparkles,
  ArrowRight,
  FileText,
  Pencil,
  AlertTriangle,
  User,
  Tag,
  Image as ImageIcon,
  Upload,
  Smartphone,
  Building2,
  CheckCircle2,
  Power,
  PowerOff,
  ShieldAlert,
  Package,
  Volume2,
  Wand2,
  LayoutGrid,
  Truck,
  Moon,
  Languages,
  Ban,
  ListChecks,
  MoreHorizontal,
  Settings,
  Search,
  ImageOff,
  Camera,
  FileSpreadsheet,
} from "lucide-react";
import { DEAL_CATEGORIES, dealCategoryLabel } from "../utils/dealCategories";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { supabase } from "../supabaseClient";
import FlyerBuilder from "./FlyerBuilder";
import DemoCatalogSeeder from "./DemoCatalogSeeder";
import StoreCatalogPanel from "./StoreCatalogPanel";

// ============================================================
// "Current Deal" flyer format — the same 600px canvas the retired
// Marketing Studio used for this template. FlyerBuilder reads
// id / label / width / height / aspectRatio / platform / deliveryMode /
// isCurrentDeal from this object. isCurrentDeal flips the primary CTA
// to "Save deal" and makes every save POST /api/designs with
// is_current_deal=true → the design lands in the Current Deals panel,
// the agent's 🏷️ ACTIVE DEALS prompt, and gets preferred by send_flyer
// when callers ask about sales/specials.
// ============================================================
const CURRENT_DEAL_FORMAT = {
  id: "current-deal",
  label: "Current Deal",
  sublabel: "Auto-publishes to your phone agent",
  width: 600,
  height: null, // variable height — classic flyer canvas
  aspectRatio: "3/4",
  platform: "general",
  deliveryMode: "sms",
  live: true,
  isCurrentDeal: true,
};

/**
 * PhoneAgentTab
 * AI Phone Agent dashboard — bento layout + Knowledge drawer
 *
 * Layout (desktop ≥lg):
 *   - Compact header: title + inline stats
 *   - Optional escalations banner (if unresolved escalations exist)
 *   - Bento body:
 *       LEFT (2/5): Number, Notifications (multi-recipient), Knowledge card
 *       RIGHT (3/5): Call history feed
 *   - Knowledge Drawer: slides from right, full height, 4 tabs
 *       (Hours & Closures / Custom Instructions / Q&A Library / Current Deals)
 *
 * Tiers:
 *   - tier="integrated" → hours pulled from store settings (read-only),
 *     receipt linking on call rows
 *   - tier="standalone" → hours fully editable, no receipt linkage
 *
 * Business type only affects Q&A seed templates:
 *   businessType: "furniture" | "dmv" | "general"
 */
const PhoneAgentTab = (props) => {
  const {
    tier = "standalone",                       // "integrated" | "standalone"
    businessType = "general",                  // "furniture" | "dmv" | "general"
    currentStore = { name: "Your Store" },
    addNotification = null,                    // optional (msg, type) => void — toast passthrough (Flyer Builder overlay uses it)
    agentNumber = "",                          // bland_phone_number
    onFetchCallDetail = null,                  // (callId) => call row WITH transcript; desktop detail pane
    onFetchLiveCalls = null,                   // () => [{call_control_id, caller_phone, started_at, transcript}]

    // ===== Stats =====
    stats = {
      callsToday: 0,
      avgDurationSec: 0,
      escalations: 0,
      smsSent: 0,
      unresolvedEscalations: 0,
    },

    // ===== Notifications (multi-recipient) =====
    initialRecipients = [],                    // [{id, name, phone}]
    onSaveRecipients = async (_list) => {},
    // (removed onSendTestSms — Test SMS button no longer in UI)

    // ===== Call Forwarding (multi-contact) =====
    // People the AI is allowed to transfer live calls to. Shape:
    //   [{ id, name, phone, role? }]
    // The agent uses `name` and `role` to route ("Is Mike there?", "the owner")
    // and dials `phone` via Bland's transfer_list. See bland-inbound.py for
    // how these are wired into the call.
    initialTransferContacts = [],
    onSaveTransferContacts = async (_list) => {},

    // ===== Voice =====
    initialVoice = "june",                     // catalog key ('june' | 'demo') or a raw Cartesia UUID
    voiceOptions = null,                       // [{id,label,description}] from GET /config voice_options; null → built-ins
    onSaveVoice = null,                        // async (voiceId) => void — POSTs { voice } to /config.
                                               // Deliberately NO no-op default: unwired must fail
                                               // visibly in the card, never fake a save.

    // ===== Number actions =====
    // (removed onTestCall — Test call button no longer in UI)

    // ===== Emergency kill switch =====
    // Master on/off for the live phone agent. When OFF, the backend should
    // stop answering inbound calls on the agent line (e.g. flip the Telnyx/
    // Pipecat route to a voicemail or "we're closed" message, or release the
    // webhook). This UI is just the control surface + optimistic state; the
    // parent owns persistence via onToggleAgent.
    //   agentEnabled  — current persisted state (true = answering calls)
    //   onToggleAgent — async (nextEnabled:boolean) => void ; throw to signal
    //                   failure so we can roll back the optimistic flip.
    agentEnabled: agentEnabledProp = true,
    onToggleAgent = null,
    // ===== Knowledge =====
    initialKnowledge = null,                   // { hours, qa, customInstructions }
    onSaveKnowledge = async (_knowledge) => {},

    // ===== Current Deals =====
    // Marketing designs the store has tagged is_current_deal=true. These are
    // surfaced to the agent as "🏷️ ACTIVE DEALS" in the prompt and listed in
    // the new "Current Deals" tab in the Knowledge drawer.
    initialCurrentDeals = [],                  // [{id, description, thumbnail_url, format_label, ...}]
    onUploadCurrentDeal = null,                // async (file) => publicUrl  (image upload to Supabase storage)
    onCreateCurrentDeal = async (_payload) => {},  // async ({ thumbnail_url, description, title }) => deal
    onDeleteCurrentDeal = async (_id) => {},   // async (id, hard?) => void
    onUpdateCurrentDeal = null,                // async (id, patch) => updatedDeal — PATCH current-deals (deal_category / deal_spoken)
    onEnrichCurrentDeals = null,               // async () => ({ tagged: [{id, category}] }) — POST enrich-deals until remaining=0
    onRefreshCurrentDeals = null,              // optional () => void — parent refetch of current deals; called ONLY when the built-in Flyer Builder overlay closes (a refetch can change deals.length → the parent's key prop remounts this whole tab)

    // ===== Calls =====
    // Each call row MAY carry two structured recap fields written by the
    // post-call pipeline (both optional — old rows render fine without them):
    //
    //   sales_intel: {
    //     interests: ["sectional", ...],   // deal-category slugs preferred, free text ok
    //     items:     [{ name, brand?, sku?, price? }, ...],  // catalog items discussed
    //     budget:    "around $1,500" | { raw, max },
    //     timeline:  "this weekend",       // when they want to buy (free text)
    //     flyers:    [{ title, thumbnail_url? }, ...],       // flyers/deals SENT on the call
    //     lead_status: "new"|"contacted"|"converted"|"dismissed",  // staff-set;
    //                                      // absent/null reads as "new"
    //   }
    //   visit: {
    //     date:   "2026-07-26",            // ISO date, resolved to the STORE's timezone
    //     time:   "14:00" | null,          // 24h, if the caller gave a specific time
    //     window: "morning"|"afternoon"|"evening" | null,  // else a day-part
    //     raw:    "Saturday afternoon",    // what the caller actually said
    //     status: "planned"|"confirmed"|"done"|"no_show"|"dismissed",
    //   }
    //
    // Flyers should come from the send_flyer tool-call log (deterministic),
    // not LLM extraction. Everything else is extracted post-call.
    //
    // COMMITMENTS + call facts (migration-call-commitments.sql — all optional,
    // legacy rows render fine without them):
    //   caller_name:        "ZR"          // name given ON the call ('' if none)
    //   callback_number:    "209-555-0142"// different reach-me number ('' = use caller_phone)
    //   callback_requested: true          // caller asked / agent promised a call back
    //   callback_done_at:   iso | null    // staff stamp — null = OPEN in the queue
    //   delivery_request:   { item, date, window, constraints, raw,
    //                         status: "requested"|"confirmed"|"dismissed" }
    //                       // status 'confirmed' is a STAFF action only —
    //                       // the agent writes 'requested', always (the
    //                       // requested-vs-confirmed claims guardrail)
    //   transfer_attempts:  [{ target, result }]  // the call's transfer story
    //   after_hours:        true          // store was closed at call start
    //   blocked_reason:     "injection"   // non-empty = guardrail-ended junk
    //   call_language:      "es"          // '' | 'en' | 'es'
    onFetchCalls = null,                       // async () => [{...}]
    initialCalls = [],
    onOpenReceipt = null,                      // (receiptId) => void
    onScrollToEscalations = null,              // optional callback for banner click
    onMarkCallReviewed = null,                 // async (callId) => void — PATCHes the call's reviewed state server-side
    onUpdateVisit = null,                      // async (callId, {status}) => void — PATCHes call.visit.status server-side
    onUpdateLead = null,                       // async (callId, {status}) => void — PATCHes call.sales_intel.lead_status server-side
    onUpdateCallback = null,                   // async (callId, {done}) => void — PATCHes {callback_done} (Needs-You queue Done/Undo)
    onUpdateDelivery = null,                   // async (callId, {status}) => void — PATCHes {delivery_status} (Confirm window / Dismiss / Restore)
    onFetchOlderCalls = null,                  // async (beforeIso) => [{...}] — page of calls with created_at < beforeIso
                                               // (wire to GET /calls?before=...; empty array = history exhausted)
  } = props;

  // ============= Local state =============
  const [recipients, setRecipients] = useState(
    Array.isArray(initialRecipients) && initialRecipients.length > 0
      ? initialRecipients
      : [createDefaultRecipient()]
  );
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState(false);
  const [recipientsDirty, setRecipientsDirty] = useState(false);

  // ----- Transfer contacts (call forwarding) -----
  // Start with whatever the parent passed in. Unlike recipients, we DON'T
  // seed an empty default row — the card has its own empty state that
  // explains the feature, and an empty array round-trips cleanly to the
  // backend as "no forwarding configured".
  //
  // ⚠️ SYNC RULE (updated): this list MIRRORS VOICE_OPTIONS in
  // api/phone-agents-2/config.py. Only 'june' and 'demo' are legacy keys
  // that also hand-sync with _resolve_voice_key in the voice server's
  // bot.py — every other entry's id IS a raw Cartesia voice UUID, which
  // the voice server passes straight through to Cartesia (the same
  // power-user escape-hatch path custom ids use), so adding/removing/
  // swapping those needs NO bot.py change. Each UUID's Cartesia library
  // name is noted so you can preview/swap it at play.cartesia.ai/voices.
  // Prefer passing voiceOptions from GET /config so the backend stays the
  // source of truth; this is the fallback.
  const DEFAULT_VOICE_OPTIONS = [
    {
      id: "june",
      label: "June",
      description: "The classic agent voice — warm, clear, professional.",
    },
    {
      id: "demo", // internal key stays 'demo' — rows already saved keep working
      label: "Sienna",
      description:
        "A calm, distinctly British voice — the same voice as the exitiumlabs.com demo.",
    },
    {
      id: "156fb8d2-335b-4950-9cb3-a2d33befec77", // Cartesia: "Helpful Woman"
      label: "Grace",
      description: "Bright, friendly American voice — a natural for customer care.",
    },
    {
      id: "829ccd10-f8b3-43cd-b8a0-4aeaa81f3b30", // Cartesia: "Customer Support Lady"
      label: "Maya",
      description: "Polished and reassuring — the seasoned support-desk pro.",
    },
    {
      id: "a167e0f3-df7e-4d52-a9c3-f949145efdab", // Cartesia: "Customer Support Man"
      label: "Miles",
      description: "Clear, upbeat American male — friendly without being pushy.",
    },
    {
      id: "694f9389-aac1-45b6-b726-9d9369183238", // Cartesia: "Sarah"
      label: "Sarah",
      description: "Natural and conversational — sounds like a real person mid-chat.",
    },
    {
      id: "248be419-c632-4f23-adf1-5324ed7dbf1d", // Cartesia: "Professional Woman"
      label: "Vivian",
      description: "Crisp, confident, and businesslike — boardroom polish.",
    },
    {
      id: "63ff761f-c1e8-414b-b969-d1833d1c870c", // Cartesia: "Confident British Man"
      label: "Graham",
      description: "An assured British male — polished, steady, unhurried.",
    },
    {
      id: "820a3788-2b37-4d21-847a-b65d8a68c99a", // Cartesia: "Salesman"
      label: "Dean",
      description: "Energetic and upbeat — showroom-floor enthusiasm.",
    },
    {
      id: "a0e99841-438c-4a64-b679-ae501e7d6091", // Cartesia: "Barbershop Man"
      label: "Ray",
      description: "Deep and easygoing — relaxed neighborhood-shop warmth.",
    },
    {
      id: "f9836c6e-a0bd-460e-9d3c-f7299fa60f94", // Cartesia: "Southern Woman"
      label: "Georgia",
      description: "Warm Southern hospitality — sweet-tea friendly.",
    },
    {
      id: "846d6cb0-2301-48b6-9683-48f5618ea2f6", // Cartesia: "Spanish-speaking Lady"
      label: "Lucía",
      description: "A native Spanish accent in any language — great for Spanish-first stores.",
    },
  ];
  const effectiveVoiceOptions =
    Array.isArray(voiceOptions) && voiceOptions.length > 0
      ? voiceOptions
      : DEFAULT_VOICE_OPTIONS;

  // Voice choice — seeded once from initialVoice (same no-resync rule as
  // transfer contacts below). A raw Cartesia UUID set directly in Supabase
  // is preserved and surfaced as a "custom voice" state, never clobbered
  // unless the user actively picks a catalog option.
  const [voiceChoice, setVoiceChoice] = useState(() =>
    (initialVoice || "june").trim()
  );
  const [savedVoiceValue, setSavedVoiceValue] = useState(() =>
    (initialVoice || "june").trim()
  );
  const [savingVoice, setSavingVoice] = useState(false);
  const [savedVoice, setSavedVoice] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const voiceDirty = voiceChoice !== savedVoiceValue;
  const voiceIsCustom =
    !!savedVoiceValue &&
    !effectiveVoiceOptions.some((o) => o.id === savedVoiceValue) &&
    savedVoiceValue.length >= 32;

  const handleSaveVoice = async () => {
    if (!voiceDirty || savingVoice) return;
    setVoiceError(null);
    if (!onSaveVoice) {
      // The old default here was a silent no-op that flashed "Saved" while
      // persisting nothing. Never again: an unwired prop is a loud,
      // in-card failure the developer sees on the first click.
      setVoiceError(
        "Not connected — the dashboard isn't passing onSaveVoice, so " +
          "nothing was saved. Wire it to POST /api/phone-agents-2/config " +
          "with { store_id, voice }."
      );
      return;
    }
    setSavingVoice(true);
    try {
      await onSaveVoice(voiceChoice);
      setSavedVoiceValue(voiceChoice);
      setSavedVoice(true);
      setTimeout(() => setSavedVoice(false), 2000);
    } catch (err) {
      // Surface the server's own words IN the card (a 400 from an old
      // config.py says "No updatable fields provided" — that message is
      // the diagnosis: the endpoint predates voice support).
      setVoiceError(err?.message || "Could not save the voice — try again");
    } finally {
      setSavingVoice(false);
    }
  };

  // NOTE: Intentionally NO useEffect resync from initialTransferContacts.
  // The parent typically passes `config.transfer_contacts || []`, where the
  // `|| []` creates a brand-new array reference on every parent render.
  // A resync effect would then wipe local in-progress edits (e.g. a freshly
  // added row) whenever the parent re-rendered for unrelated reasons. We
  // mirror the recipients pattern: read from props ONCE on mount; if the
  // parent needs to force a refresh from the server, it should remount this
  // tab (or pass a changing `key` prop).
  const [transferContacts, setTransferContacts] = useState(
    Array.isArray(initialTransferContacts) ? initialTransferContacts : []
  );
  const [savingContacts, setSavingContacts] = useState(false);
  const [savedContacts, setSavedContacts] = useState(false);
  const [contactsDirty, setContactsDirty] = useState(false);

  const [copied, setCopied] = useState(false);

  const [calls, setCalls] = useState(initialCalls);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState("");
  const [expandedCallId, setExpandedCallId] = useState(null);
  // Desktop Calls view is list + detail pane; the pane's call is its own
  // state so selecting one doesn't pause the auto-refresh the way an
  // inline-expanded row does.
  const [selectedCallId, setSelectedCallId] = useState(null);
  // IDs of escalated calls the user has marked reviewed. Drives the follow-up
  // banner count down immediately on click (optimistic). The PERSISTED truth is
  // `escalation_resolved` on each call row, written by onMarkCallReviewed →
  // calls.py PATCH and read back on the next fetch/poll, so a refresh no longer
  // resurfaces an already-reviewed escalation. The local Set just bridges the
  // gap between click and the next server round-trip.
  const [locallyResolved, setLocallyResolved] = useState(() => new Set());
  const markReviewed = useCallback(async (callId) => {
    // Optimistic: hide it from the banner right away.
    setLocallyResolved((prev) => {
      const next = new Set(prev);
      next.add(callId);
      return next;
    });

    // No persistence wired up → behave like the old local-only version.
    if (!onMarkCallReviewed) return;

    try {
      await onMarkCallReviewed(callId);
      // Reflect the persisted flag on the row so subsequent polls/refreshes
      // keep it resolved even though the local Set is session-scoped.
      setCalls((prev) =>
        prev.map((c) =>
          c.id === callId ? { ...c, escalation_resolved: true } : c
        )
      );
    } catch (err) {
      // Roll back the optimistic flag so the user can retry.
      setLocallyResolved((prev) => {
        const next = new Set(prev);
        next.delete(callId);
        return next;
      });
      setCallsError(err?.message || "Could not mark reviewed — try again");
    }
  }, [onMarkCallReviewed]);

  // Single source of truth for "has this escalation been handled?"
  // A call counts as reviewed if EITHER the server says so (escalation_resolved,
  // persisted by onMarkCallReviewed and returned by onFetchCalls) OR the user
  // clicked this session (locallyResolved bridges the click → next-poll gap).
  // Previously only the local Set was consulted at the CallRow call sites, so
  // a page refresh made already-reviewed escalations look unreviewed again
  // even when the backend had persisted them correctly.
  const isCallResolved = useCallback(
    (c) => !!c?.escalation_resolved || locallyResolved.has(c?.id),
    [locallyResolved]
  );

  // ----- Visit status (optimistic overlay) -----
  // Same pattern as locallyResolved: the click updates a local Map right away,
  // onUpdateVisit persists it (PATCH calls.py → visit.status), and the next
  // poll returns the persisted value on the row. The Map only bridges the
  // click → next-fetch gap; server truth wins once it arrives.
  const [localVisitStatus, setLocalVisitStatus] = useState(() => new Map());
  const updateVisit = useCallback(async (callId, status) => {
    let prevStatus;
    setLocalVisitStatus((prev) => {
      prevStatus = prev.get(callId);
      const next = new Map(prev);
      next.set(callId, status);
      return next;
    });

    if (!onUpdateVisit) return; // local-only mode — nothing to persist

    try {
      await onUpdateVisit(callId, { status });
      // Reflect on the row so later polls that miss the overlay stay correct.
      setCalls((prev) =>
        prev.map((c) =>
          c.id === callId && c.visit ? { ...c, visit: { ...c.visit, status } } : c
        )
      );
    } catch (err) {
      // Roll back so the user can retry.
      setLocalVisitStatus((prev) => {
        const next = new Map(prev);
        if (prevStatus === undefined) next.delete(callId);
        else next.set(callId, prevStatus);
        return next;
      });
      setCallsError(err?.message || "Could not update the visit — try again");
    }
  }, [onUpdateVisit]);

  // Single source of truth for "what state is this call's visit in?"
  const visitStatusOf = useCallback(
    (c) => localVisitStatus.get(c?.id) || c?.visit?.status || "planned",
    [localVisitStatus]
  );

  // ----- Lead status (optimistic overlay) -----
  // Identical pattern to visits. Lead lifecycle lives INSIDE the sales_intel
  // JSONB (sales_intel.lead_status), so no schema change was needed — the
  // sales-recap migration already covers it. Absent reads as "new".
  const [localLeadStatus, setLocalLeadStatus] = useState(() => new Map());
  const updateLead = useCallback(async (callId, status) => {
    let prevStatus;
    setLocalLeadStatus((prev) => {
      prevStatus = prev.get(callId);
      const next = new Map(prev);
      next.set(callId, status);
      return next;
    });

    if (!onUpdateLead) return; // local-only mode — nothing to persist

    try {
      await onUpdateLead(callId, { status });
      setCalls((prev) =>
        prev.map((c) =>
          c.id === callId && c.sales_intel
            ? { ...c, sales_intel: { ...c.sales_intel, lead_status: status } }
            : c
        )
      );
    } catch (err) {
      setLocalLeadStatus((prev) => {
        const next = new Map(prev);
        if (prevStatus === undefined) next.delete(callId);
        else next.set(callId, prevStatus);
        return next;
      });
      setCallsError(err?.message || "Could not update the lead — try again");
    }
  }, [onUpdateLead]);

  const leadStatusOf = useCallback(
    (c) => localLeadStatus.get(c?.id) || c?.sales_intel?.lead_status || "new",
    [localLeadStatus]
  );

  // ----- Callback done (optimistic overlay) -----
  // The Needs-You queue's Done/Undo on the promise the agent spoke out loud
  // ("someone will reach out shortly"). Same click → local Map → persist →
  // next-poll-wins pattern as visits/leads. Server truth is callback_done_at
  // (null = open); locally we overlay a boolean.
  const [localCallbackDone, setLocalCallbackDone] = useState(() => new Map());
  const updateCallback = useCallback(async (callId, done) => {
    let prevVal;
    setLocalCallbackDone((prev) => {
      prevVal = prev.get(callId);
      const next = new Map(prev);
      next.set(callId, done);
      return next;
    });

    if (!onUpdateCallback) return; // local-only mode — nothing to persist

    try {
      await onUpdateCallback(callId, { done });
      setCalls((prev) =>
        prev.map((c) =>
          c.id === callId
            ? { ...c, callback_done_at: done ? new Date().toISOString() : null }
            : c
        )
      );
    } catch (err) {
      setLocalCallbackDone((prev) => {
        const next = new Map(prev);
        if (prevVal === undefined) next.delete(callId);
        else next.set(callId, prevVal);
        return next;
      });
      setCallsError(err?.message || "Could not update the callback — try again");
    }
  }, [onUpdateCallback]);

  const callbackDoneOf = useCallback(
    (c) => {
      const local = localCallbackDone.get(c?.id);
      if (local !== undefined) return local;
      return !!c?.callback_done_at;
    },
    [localCallbackDone]
  );

  // ----- Delivery status (optimistic overlay) -----
  // Mutates ONLY delivery_request.status via calls.py. 'confirmed' is the
  // staff claim the agent promised on the call ("the team will call to lock
  // in the exact window") — the extractor never writes it, this button does.
  const [localDeliveryStatus, setLocalDeliveryStatus] = useState(() => new Map());
  const updateDelivery = useCallback(async (callId, status) => {
    let prevStatus;
    setLocalDeliveryStatus((prev) => {
      prevStatus = prev.get(callId);
      const next = new Map(prev);
      next.set(callId, status);
      return next;
    });

    if (!onUpdateDelivery) return;

    try {
      await onUpdateDelivery(callId, { status });
      setCalls((prev) =>
        prev.map((c) =>
          c.id === callId && c.delivery_request
            ? { ...c, delivery_request: { ...c.delivery_request, status } }
            : c
        )
      );
    } catch (err) {
      setLocalDeliveryStatus((prev) => {
        const next = new Map(prev);
        if (prevStatus === undefined) next.delete(callId);
        else next.set(callId, prevStatus);
        return next;
      });
      setCallsError(err?.message || "Could not update the delivery — try again");
    }
  }, [onUpdateDelivery]);

  const deliveryStatusOf = useCallback(
    (c) =>
      localDeliveryStatus.get(c?.id) ||
      c?.delivery_request?.status ||
      "requested",
    [localDeliveryStatus]
  );
  const [callsFilter, setCallsFilter] = useState("all"); // all | escalated | after_hours | visits | leads
  // Blocked/junk calls collapse behind one row at the feed's tail; this is
  // that row's open state (shared across the mobile + desktop trees).
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [recentCallsOpen, setRecentCallsOpen] = useState(false); // desktop: collapsed on landing

  // Knowledge drawer
  // Drawer visibility survives parent remounts. Deleting/creating a catalog
  // item makes the parent page refetch, which remounts this whole tab — with
  // plain useState that slammed the drawer shut mid-session (the bug: "I
  // deleted a deal and got kicked out of the drawer"). sessionStorage (not
  // localStorage) so it resets when the browser tab closes and never leaks
  // across tabs. All writes are try/caught for private-mode browsers.
  const [knowledgeOpen, _setKnowledgeOpen] = useState(() => {
    try {
      return sessionStorage.getItem("pa_knowledge_open") === "1";
    } catch {
      return false;
    }
  });
  const setKnowledgeOpen = useCallback((v) => {
    _setKnowledgeOpen((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try {
        sessionStorage.setItem("pa_knowledge_open", next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  // Catalog & Deals drawer — SAME sessionStorage pattern as knowledgeOpen,
  // and for the same reason: CurrentDealsPanel now lives in THIS drawer, and
  // creating/deleting a deal makes the parent refetch → key change → this tab
  // remounts. Plain useState would slam the drawer shut mid-edit (the old
  // "I deleted a deal and got kicked out" bug).
  const [catalogOpen, _setCatalogOpen] = useState(() => {
    try {
      return sessionStorage.getItem("pa_catalog_open") === "1";
    } catch {
      return false;
    }
  });
  const setCatalogOpen = useCallback((v) => {
    _setCatalogOpen((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try {
        sessionStorage.setItem("pa_catalog_open", next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  // Flyer Builder overlay — the AI flyer generator that used to live in
  // Marketing Studio now lives HERE (Marketing Studio was removed from the
  // product, July 2026). Same sessionStorage pattern as knowledgeOpen: this
  // tab remounts whenever the parent's deals refetch changes the list length
  // (see the key prop on <PhoneAgentTab> in both dashboards), and plain
  // useState would slam the overlay shut mid-session.
  const [flyerBuilderOpen, _setFlyerBuilderOpen] = useState(() => {
    try {
      return sessionStorage.getItem("pa_flyer_builder_open") === "1";
    } catch {
      return false;
    }
  });
  const setFlyerBuilderOpen = useCallback((v) => {
    _setFlyerBuilderOpen((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      try {
        sessionStorage.setItem("pa_flyer_builder_open", next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);
  // Close = the ONLY moment we ask the parent to refetch deals. While the
  // overlay is open, a refetch could change deals.length → key change → this
  // whole tab remounts → the builder (and any in-progress design) unmounts.
  // FlyerBuilder persists its saves itself via /api/designs, so deferring
  // the refetch to close loses nothing.
  const handleCloseFlyerBuilder = useCallback(() => {
    setFlyerBuilderOpen(false);
    if (onRefreshCurrentDeals) onRefreshCurrentDeals();
  }, [setFlyerBuilderOpen, onRefreshCurrentDeals]);

  // Notifications + Call Forwarding are BOTH about call routing, so they
  // now share a single "Call Settings" drawer with two tabs instead of two
  // separate teaser cards competing with Agent Knowledge for attention.
  // (Product Catalog moved into the Knowledge drawer as its own tab — it's
  // agent knowledge, not a separate surface.)
  const [callSettingsOpen, setCallSettingsOpen] = useState(false);
  const [callSettingsTab, setCallSettingsTab] = useState("notifications"); // notifications | transfers

  // Agent line popover — the number + copy + forwarding guide, collapsed
  // behind a quiet header button. It's set-and-forget reference data, so it
  // no longer gets a full-width bar of its own.
  const [agentLineOpen, setAgentLineOpen] = useState(false);
  const [knowledge, setKnowledge] = useState(
    initialKnowledge || createDefaultKnowledge()
  );

  // Forwarding guide modal — reference docs for setting up call forwarding
  // on the store's existing line. Read-once-and-close; lives as a modal
  // rather than the drawer (which is for ongoing tuning surfaces).
  const [forwardingGuideOpen, setForwardingGuideOpen] = useState(false);

  // Current deals — held in component state so the drawer panel can reflect
  // adds/removes immediately without a round-trip to the parent. The parent
  // is still the source of truth via initialCurrentDeals; we sync when it
  // changes (e.g. after a fresh fetch).
  const [currentDeals, setCurrentDeals] = useState(
    Array.isArray(initialCurrentDeals) ? initialCurrentDeals : []
  );

  useEffect(() => {
    if (Array.isArray(initialCurrentDeals)) {
      setCurrentDeals(initialCurrentDeals);
    }
  }, [initialCurrentDeals]);

  // ----- Emergency kill switch -----
  // Optimistic mirror of the parent's agentEnabled, so the toggle feels
  // instant in a real "shut it off NOW" moment. We resync if the parent
  // pushes a new value (e.g. another device flipped it). `togglingAgent`
  // guards against double-taps mid-request; `killConfirmOpen` gates the
  // OFF→destructive direction behind a one-tap confirm so a stray click
  // doesn't silently take the line down — turning the agent BACK ON is
  // unguarded (you always want re-enabling to be fast and frictionless).
  const [agentEnabled, setAgentEnabled] = useState(agentEnabledProp !== false);
  const [togglingAgent, setTogglingAgent] = useState(false);
  const [killConfirmOpen, setKillConfirmOpen] = useState(false);
  const [killError, setKillError] = useState("");

  useEffect(() => {
    setAgentEnabled(agentEnabledProp !== false);
  }, [agentEnabledProp]);

  const applyAgentToggle = useCallback(
    async (nextEnabled) => {
      setKillConfirmOpen(false);
      setKillError("");
      setTogglingAgent(true);
      // Optimistic flip.
      setAgentEnabled(nextEnabled);
      try {
        if (onToggleAgent) await onToggleAgent(nextEnabled);
      } catch (err) {
        // Roll back so the UI reflects reality and the user can retry.
        setAgentEnabled(!nextEnabled);
        setKillError(
          err?.message ||
            (nextEnabled
              ? "Couldn't re-enable the agent — try again."
              : "Couldn't pause the agent — try again.")
        );
      } finally {
        setTogglingAgent(false);
      }
    },
    [onToggleAgent]
  );

  // Pausing is destructive (takes the line down) → confirm first.
  // Re-enabling is safe → fire immediately.
  const handleKillSwitchClick = useCallback(() => {
    if (togglingAgent) return;
    if (agentEnabled) {
      setKillConfirmOpen(true);
    } else {
      applyAgentToggle(true);
    }
  }, [agentEnabled, togglingAgent, applyAgentToggle]);

  // ============= Derived =============
  const hasAgentNumber = !!agentNumber;
  const displayNumber = hasAgentNumber ? agentNumber : "Not yet assigned";

  const knowledgeSummary = useMemo(() => {
    const qaCount = knowledge?.qa?.length || 0;
    const closureCount = knowledge?.hours?.closures?.length || 0;
    const hoursConfigured =
      knowledge?.hours?.weekly &&
      Object.values(knowledge.hours.weekly).some((d) => d.open);
    const hasInstructions = !!knowledge?.customInstructions?.trim();
    const dealCount = (currentDeals || []).filter(
      (d) => (d.item_kind || "deal") !== "product"
    ).length;
    const productCount = (currentDeals || []).filter(
      (d) => (d.item_kind || "deal") === "product"
    ).length;
    const untaggedDeals = (currentDeals || []).filter(
      (d) => !(d.deal_category || "").trim()
    ).length;
    return {
      qaCount,
      closureCount,
      hoursConfigured,
      hasInstructions,
      dealCount,
      productCount,
      untaggedDeals,
    };
  }, [knowledge, currentDeals]);

  // ----- r3 shell: which of the three views is showing -----
  // Today (the to-do list + recent calls), Calls (the log), Setup (the
  // checklist). Persisted per session so a refresh doesn't bounce you back.
  // A store that hasn't finished setting up lands on Setup first.
  const setupIncomplete =
    !hasAgentNumber || !knowledgeSummary.hoursConfigured || recipients.length === 0;
  const [view, _setView] = useState(() => {
    try {
      const saved = sessionStorage.getItem("pa_view");
      if (saved === "today" || saved === "calls" || saved === "setup") return saved;
    } catch {
      /* private mode */
    }
    return setupIncomplete ? "setup" : "today";
  });
  const setView = useCallback((v) => {
    _setView(v);
    try {
      sessionStorage.setItem("pa_view", v);
    } catch {
      /* ignore */
    }
  }, []);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  // Guardrail-terminated junk (prompt injection, abuse, data fishing) is
  // logged but never mixed in with real customers: everything the boards
  // and stats read comes from visibleCalls; blockedCalls collapse into one
  // quiet expandable row at the bottom of the feed.
  const visibleCalls = useMemo(
    () => calls.filter((c) => !(c?.blocked_reason || "").trim()),
    [calls]
  );
  const blockedCalls = useMemo(
    () => calls.filter((c) => (c?.blocked_reason || "").trim()),
    [calls]
  );

  const filteredCalls = useMemo(() => {
    if (callsFilter === "escalated") return visibleCalls.filter((c) => c.escalated);
    if (callsFilter === "after_hours") return visibleCalls.filter((c) => c.after_hours);
    if (callsFilter === "visits")
      return visibleCalls.filter((c) => !!normalizeVisit(c)); // any visit intent, past or future
    if (callsFilter === "leads")
      return visibleCalls.filter((c) => {
        const i = normalizeSalesIntel(c);
        return !!i && (i.interests.length > 0 || i.items.length > 0 ||
                       !!i.budget || i.flyers.length > 0);
      });
    return visibleCalls;
  }, [visibleCalls, callsFilter]);

  // Upcoming visits — the sales-floor agenda. A call lands here when the
  // caller committed to a day ("I'll come Saturday afternoon") — or, with no
  // resolved date, while the call is still fresh (isVisitUpcoming's 5-day
  // window) so a vague "this weekend" isn't lost. Staff-dismissed/completed
  // visits drop out. Sorted soonest-first; dateless intent sinks to the end.
  const upcomingVisits = useMemo(() => {
    const eligible = visibleCalls
      .map((c) => ({ call: c, visit: normalizeVisit(c) }))
      .filter(({ call, visit }) => {
        if (!visit) return false;
        const status = visitStatusOf(call);
        if (status === "dismissed" || status === "done" || status === "no_show")
          return false;
        return isVisitUpcoming(visit, call.created_at);
      });
    // One row per caller: their NEWEST call's plan supersedes older ones —
    // "I'll come Thursday" (Monday's call) + "make that Friday" (Wednesday's
    // call) is ONE visit, Friday, not two competing rows for the same person.
    const byCaller = new Map();
    for (const entry of eligible) {
      const key = normalizeDigits(entry.call.caller_phone) || entry.call.id;
      const prev = byCaller.get(key);
      if (
        !prev ||
        new Date(entry.call.created_at || 0) >
          new Date(prev.call.created_at || 0)
      ) {
        byCaller.set(key, entry);
      }
    }
    return Array.from(byCaller.values()).sort(
      (a, b) => visitSortKey(a.visit) - visitSortKey(b.visit)
    );
  }, [visibleCalls, visitStatusOf]);

  // ----- Needs-You queue: the promises the agent made out loud -----
  // Open callbacks — "someone will reach out shortly" was SAID on these
  // calls; until a human taps Done, the store's word is out. One row per
  // caller (newest promise wins), sorted MOST OVERDUE FIRST — the
  // speed-to-lead decay curve runs on the oldest promise, not the newest.
  const openCallbacks = useMemo(() => {
    const byCaller = new Map();
    for (const call of visibleCalls) {
      const cb = callbackOf(call);
      if (!cb) continue;
      if (callbackDoneOf(call)) continue;
      const key = normalizeDigits(call.caller_phone) || call.id;
      const prev = byCaller.get(key);
      if (!prev || new Date(call.created_at || 0) > new Date(prev.call.created_at || 0)) {
        byCaller.set(key, { call, cb });
      }
    }
    return Array.from(byCaller.values()).sort(
      (a, b) => new Date(a.call.created_at || 0) - new Date(b.call.created_at || 0)
    );
  }, [visibleCalls, callbackDoneOf]);

  // Pending deliveries — "the team will call to lock in the exact window".
  // status 'requested' means that promised confirmation call hasn't happened
  // yet. One row per caller (newest request wins), soonest-dated first.
  const pendingDeliveries = useMemo(() => {
    const byCaller = new Map();
    for (const call of visibleCalls) {
      const dr = normalizeDeliveryRequest(call);
      if (!dr) continue;
      if (deliveryStatusOf(call) !== "requested") continue;
      const key = normalizeDigits(call.caller_phone) || call.id;
      const prev = byCaller.get(key);
      if (!prev || new Date(call.created_at || 0) > new Date(prev.call.created_at || 0)) {
        byCaller.set(key, { call, dr });
      }
    }
    return Array.from(byCaller.values()).sort(
      (a, b) => visitSortKey(a.dr) - visitSortKey(b.dr)
    );
  }, [visibleCalls, deliveryStatusOf]);

  // Unresolved escalations, newest first — the queue's fire section.
  const unresolvedEscalationCalls = useMemo(
    () =>
      visibleCalls
        .filter((c) => c.escalated && !isCallResolved(c))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [visibleCalls, isCallResolved]
  );

  const needsYouCount =
    openCallbacks.length +
    pendingDeliveries.length +
    unresolvedEscalationCalls.length;

  // Leads — callers with real buying signal (interest, items, budget, or a
  // flyer they asked for) who are NOT on the visits board. Derived entirely
  // from the calls the feed already fetched — no extra endpoint:
  //   · deduped by phone number (three calls about sectionals = ONE lead;
  //     the newest qualifying call carries it)
  //   · an UPCOMING visit removes the caller from leads (they're committed);
  //     a no-show or dismissed visit drops them back IN (call them!)
  //   · a completed visit ("Came in") or converted/dismissed lead drops out
  //   · aged out after 14 days — follow-up value decays fast
  // Sorted: new before contacted, newest call first within each.
  const leads = useMemo(() => {
    const cutoff = Date.now() - 14 * 86400000;
    const byPhone = new Map();
    // Callers already on the visits board (via ANY of their calls) are
    // committed — not leads to chase. Without this cross-call check, a newer
    // intel-bearing call with no visit of its own put the same person in
    // Leads as "no visit set" while they sat on the visits board.
    const onBoard = new Set(
      upcomingVisits.map(
        ({ call }) => normalizeDigits(call.caller_phone) || call.id
      )
    );
    // Callers with an OPEN promised callback already sit at the top of the
    // Needs-You queue — listing them here too would render the same person
    // twice on one screen. Once the callback is marked Done they drop back
    // into Leads if their buying signal is still fresh.
    const inCallbackQueue = new Set(
      openCallbacks.map(
        ({ call }) => normalizeDigits(call.caller_phone) || call.id
      )
    );
    for (const call of visibleCalls) {
      const intel = normalizeSalesIntel(call);
      if (!intel) continue;
      if (!(intel.interests.length || intel.items.length || intel.budget ||
            intel.flyers.length)) continue;
      const t = new Date(call.created_at || 0).getTime();
      if (Number.isNaN(t) || t < cutoff) continue;

      const ls = leadStatusOf(call);
      if (ls === "dismissed" || ls === "converted") continue;

      const key0 = normalizeDigits(call.caller_phone) || call.id;
      if (onBoard.has(key0)) continue;
      if (inCallbackQueue.has(key0)) continue;

      const visit = normalizeVisit(call);
      const vs = visitStatusOf(call);
      if (visit && vs === "done") continue; // they came in — converted
      const onVisitsBoard =
        visit &&
        vs !== "dismissed" && vs !== "done" && vs !== "no_show" &&
        isVisitUpcoming(visit, call.created_at);
      if (onVisitsBoard) continue;

      const key = normalizeDigits(call.caller_phone) || call.id;
      const prev = byPhone.get(key);
      if (!prev ||
          new Date(call.created_at) > new Date(prev.call.created_at)) {
        byPhone.set(key, { call, intel });
      }
    }
    const rank = (s) => (s === "contacted" ? 1 : 0);
    return Array.from(byPhone.values()).sort((a, b) => {
      const ra = rank(leadStatusOf(a.call));
      const rb = rank(leadStatusOf(b.call));
      if (ra !== rb) return ra - rb;
      return new Date(b.call.created_at) - new Date(a.call.created_at);
    });
  }, [visibleCalls, leadStatusOf, visitStatusOf, upcomingVisits, openCallbacks]);
  const newLeadCount = useMemo(
    () => leads.filter(({ call }) => leadStatusOf(call) !== "contacted").length,
    [leads, leadStatusOf]
  );

  // ----- The Today list: every promise, one row shape -----
  // Callbacks, deliveries, escalations, visits and leads used to be five
  // differently-styled surfaces (a banner, a three-section queue, two
  // boards, a stat). They are one list now, one row pattern, one primary
  // action each. Anything older than 14 days that nobody acted on drops
  // into a collapsed "older" group instead of sitting at the top forever
  // (visits and deliveries stay by their own date, not the call's).
  const todoItems = useMemo(() => {
    const cutoff = Date.now() - 14 * 86400000;
    const items = [];
    const isOld = (iso) => {
      const t = new Date(iso || 0).getTime();
      return !Number.isNaN(t) && t < cutoff;
    };

    for (const { call, cb } of openCallbacks) {
      const intel = normalizeSalesIntel(call);
      const wants = intel?.interests?.length
        ? intel.interests.slice(0, 2).map(interestLabel).join(", ")
        : intel?.items?.length
          ? intel.items.slice(0, 2).map((i) => i.name).join(", ")
          : null;
      const d = normalizeDigits(cb.number || "");
      const tel = d.length >= 10 ? `tel:${toE164(cb.number)}` : call.caller_phone ? `tel:${call.caller_phone}` : null;
      items.push({
        key: `cb-${call.id}`, kind: "callback", call,
        who: callerLabel(call),
        meta: `${formatRelativeTime(call.created_at)}${cb.number ? ` · reach at ${cb.number}` : ""}`,
        // Written as the staff task, not as the agent's promise. The call
        // summary (agent's voice) stays in the call detail, one tap away.
        sub: [`Call ${callerLabel(call).split(" ")[0]} back`,
              wants ? `asked about ${wants}` : null,
              intel?.budget ? `budget ${intel.budget}` : null]
          .filter(Boolean).join(" · "),
        primary: tel ? { label: "Call", href: tel } : null,
        done: { label: "Called back", run: () => updateCallback(call.id, true) },
        old: isOld(call.created_at),
        sort: [0, new Date(call.created_at || 0).getTime()],
      });
    }
    for (const { call, dr } of pendingDeliveries) {
      items.push({
        key: `dr-${call.id}`, kind: "delivery", call,
        who: callerLabel(call),
        meta: formatVisitWhen(dr) || "day not set",
        // "Mark confirmed" only ticks this off; it sends nothing to the
        // customer and does not change the receipt.
        sub: [`Confirm the delivery date with ${callerLabel(call).split(" ")[0]}`,
              dr.item || null, dr.constraints || null].filter(Boolean).join(" · "),
        primary: { label: "Mark confirmed", run: () => updateDelivery(call.id, "confirmed") },
        done: { label: "Not needed", run: () => updateDelivery(call.id, "dismissed") },
        old: false,
        sort: [1, visitSortKey(dr)],
      });
    }
    for (const { call, visit } of upcomingVisits) {
      const intel = normalizeSalesIntel(call);
      const wants = intel?.interests?.length
        ? intel.interests.slice(0, 2).map(interestLabel).join(", ") : null;
      const confirmed = visitStatusOf(call) === "confirmed";
      items.push({
        key: `vs-${call.id}`, kind: "visit", call,
        who: callerLabel(call),
        meta: formatVisitWhen(visit) || "day not set",
        sub: [confirmed ? "Visit confirmed" : "Said they'd come in", wants ? `interested in ${wants}` : null,
              intel?.budget ? `budget ${intel.budget}` : null].filter(Boolean).join(" · "),
        primary: confirmed
          ? { label: "Came in", run: () => updateVisit(call.id, "done") }
          : { label: "Mark confirmed", run: () => updateVisit(call.id, "confirmed") },
        done: { label: "Not needed", run: () => updateVisit(call.id, "dismissed") },
        old: false,
        sort: [2, visitSortKey(visit)],
      });
    }
    for (const call of unresolvedEscalationCalls) {
      items.push({
        key: `es-${call.id}`, kind: "review", call,
        who: callerLabel(call),
        meta: formatRelativeTime(call.created_at),
        sub: [`Read this call · ${formatEscalationReason(call.escalation_reason)}`, (call.summary || "").trim() || null]
          .filter(Boolean).join(" — "),
        primary: { label: "Read call", jump: true },
        done: { label: "Reviewed", run: () => markReviewed(call.id) },
        old: isOld(call.created_at),
        sort: [3, -new Date(call.created_at || 0).getTime()],
      });
    }
    for (const { call, intel } of leads) {
      const wants = intel.interests.length
        ? intel.interests.slice(0, 2).map(interestLabel).join(", ")
        : intel.items.length ? intel.items.slice(0, 2).map((i) => i.name).join(", ") : null;
      const contacted = leadStatusOf(call) === "contacted";
      items.push({
        key: `ld-${call.id}`, kind: "lead", call,
        who: callerLabel(call),
        meta: `${formatRelativeTime(call.created_at)}${contacted ? " · contacted" : " · new"}`,
        sub: [wants ? `Interested in ${wants}` : null, intel.budget ? `budget ${intel.budget}` : null,
              intel.flyers?.length ? "sent a flyer" : null].filter(Boolean).join(" · ") || "Showed buying interest",
        primary: call.caller_phone ? { label: "Call", href: `tel:${call.caller_phone}` } : null,
        secondary: contacted
          ? { label: "Came in", run: () => updateLead(call.id, "converted") }
          : { label: "Contacted", run: () => updateLead(call.id, "contacted") },
        done: { label: "Not needed", run: () => updateLead(call.id, "dismissed") },
        old: isOld(call.created_at),
        sort: [4, contacted ? 1 : 0, -new Date(call.created_at || 0).getTime()],
      });
    }
    items.sort((a, b) => {
      for (let i = 0; i < Math.max(a.sort.length, b.sort.length); i++) {
        const d = (a.sort[i] || 0) - (b.sort[i] || 0);
        if (d) return d;
      }
      return 0;
    });
    return { current: items.filter((i) => !i.old), older: items.filter((i) => i.old) };
  }, [openCallbacks, pendingDeliveries, upcomingVisits, unresolvedEscalationCalls, leads,
      visitStatusOf, leadStatusOf, updateCallback, updateDelivery, updateVisit, updateLead, markReviewed]);
  const [olderOpen, setOlderOpen] = useState(false);

  // Header stats derived live from the fetched calls so they update with the
  // auto-refresh poll. "Today" is the local calendar day. smsSent isn't on the
  // call object, so it falls back to whatever the parent passed in.
  const derivedStats = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todays = visibleCalls.filter((c) => {
      if (!c.created_at) return false;
      const t = new Date(c.created_at).getTime();
      return !Number.isNaN(t) && t >= startOfToday.getTime();
    });

    const callsToday = todays.length;
    const totalDuration = todays.reduce(
      (sum, c) => sum + (Number(c.duration_sec) || 0),
      0
    );
    const avgDurationSec = callsToday > 0
      ? Math.round(totalDuration / callsToday)
      : 0;
    const escalations = todays.filter((c) => c.escalated).length;
    const unresolvedEscalations = visibleCalls.filter(
      (c) => c.escalated && !c.escalation_resolved && !locallyResolved.has(c.id)
    ).length;
    // The rescue counter: calls the agent answered while the store was
    // CLOSED — every one of these is a caller who would have hit voicemail.
    const afterHoursToday = todays.filter((c) => c.after_hours).length;

    return {
      callsToday,
      avgDurationSec,
      escalations,
      unresolvedEscalations,
      afterHoursToday,
      smsSent: stats.smsSent || 0,
    };
  }, [visibleCalls, stats.smsSent, locallyResolved]);

  // Seven-day rollup for the Today view's stat tiles and rail. Oldest day
  // first so the sparklines read left→right. Built from the loaded calls
  // (the newest page), which covers a week for any single store today.
  const weekStats = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return { date: d, calls: 0, afterHours: 0, talkSec: 0 };
    });
    const topics = new Map();
    let talkSec = 0;
    let lastCallAt = 0;
    for (const c of visibleCalls) {
      const t = new Date(c.created_at || 0).getTime();
      if (Number.isNaN(t)) continue;
      if (t > lastCallAt) lastCallAt = t;
      const idx = Math.floor((t - start.getTime()) / 86400000);
      if (idx < 0 || idx > 6) continue;
      days[idx].calls += 1;
      if (c.after_hours) days[idx].afterHours += 1;
      days[idx].talkSec += Number(c.duration_sec) || 0;
      talkSec += Number(c.duration_sec) || 0;
      const intel = normalizeSalesIntel(c);
      const tags = intel?.interests?.length
        ? intel.interests.map(interestLabel)
        : Array.isArray(c.intent_tags) ? c.intent_tags : [];
      for (const tag of new Set(tags.filter(Boolean))) {
        topics.set(tag, (topics.get(tag) || 0) + 1);
      }
    }
    return {
      days,
      calls: days.reduce((s, d) => s + d.calls, 0),
      afterHours: days.reduce((s, d) => s + d.afterHours, 0),
      talkSec,
      lastCallAt: lastCallAt ? new Date(lastCallAt).toISOString() : null,
      topics: [...topics.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5),
    };
  }, [visibleCalls]);

  const avgDurationLabel = useMemo(() => {
    const s = Math.max(0, derivedStats.avgDurationSec || 0);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, "0")}`;
  }, [derivedStats.avgDurationSec]);

  // ============= Calls fetch =============
  const loadCalls = useCallback(async () => {
    if (!onFetchCalls) return;
    setCallsLoading(true);
    setCallsError("");
    try {
      const result = await onFetchCalls();
      // MERGE, don't replace: onFetchCalls returns the newest page only, and
      // the user may have paged older calls in (loadOlderCalls). Fresh rows
      // win on id (they carry newer visit/lead status); older pages survive.
      setCalls((prev) => mergeCallsById(Array.isArray(result) ? result : [], prev));
    } catch (err) {
      setCallsError(err?.message || "Failed to load calls");
    } finally {
      setCallsLoading(false);
    }
  }, [onFetchCalls]);

  // ----- Older-page pagination -----
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderExhausted, setOlderExhausted] = useState(false);
  // Server returned rows but the cursor was ignored (every id already
  // loaded): the deployed calls endpoint predates `before` support. Stop
  // looping and say so, instead of a button that spins forever doing nothing.
  const [olderStale, setOlderStale] = useState(false);
  // Ref lock, not just state: the footer renders in BOTH layout trees, and
  // two IntersectionObservers can fire in the same frame — state hasn't
  // re-rendered yet, so both would pass a state-only guard.
  const olderLockRef = useRef(false);
  const loadOlderCalls = useCallback(async () => {
    if (!onFetchOlderCalls || olderExhausted || olderStale) return;
    if (olderLockRef.current) return;
    olderLockRef.current = true;
    // Cursor = the oldest created_at currently loaded. Computed as a min
    // rather than trusting array order, so a merge that ever mis-sorted
    // couldn't make pagination skip history.
    let oldest = null;
    for (const c of calls) {
      const t = new Date(c?.created_at || 0).getTime();
      if (!Number.isNaN(t) && (oldest === null || t < oldest)) oldest = t;
    }
    if (oldest === null) {
      olderLockRef.current = false;
      return;
    }
    setLoadingOlder(true);
    try {
      const batch = await onFetchOlderCalls(new Date(oldest).toISOString());
      const rows = Array.isArray(batch) ? batch : [];
      if (rows.length === 0) {
        setOlderExhausted(true); // clean end-of-history signal
      } else {
        const merged = mergeCallsById(rows, calls);
        if (merged.length === calls.length) {
          // Rows came back but every one was already loaded. A correct
          // cursor returns strictly-older rows, so all-duplicates can only
          // mean the endpoint ignored `before` → stale deploy.
          setOlderStale(true);
        } else {
          setCalls(merged);
        }
      }
    } catch (err) {
      setCallsError(err?.message || "Could not load older calls");
    } finally {
      setLoadingOlder(false);
      olderLockRef.current = false;
    }
  }, [onFetchOlderCalls, olderExhausted, olderStale, calls]);

  useEffect(() => {
    if (onFetchCalls && (!initialCalls || initialCalls.length === 0)) {
      loadCalls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============= Live calls (desktop banner) =============
  // Calls the agent is on right now, heartbeated by the voice server. A
  // light poll of its own (5s) so the transcript keeps up; skipped while
  // the browser tab is hidden. When a live call drops off, the finished
  // call lands in the log a few seconds later (post-call), so pull the
  // list then.
  const [liveCalls, setLiveCalls] = useState([]);
  const liveCountRef = useRef(0);
  const silentlyRefreshCallsRef = useRef(null); // set below, once defined
  const liveFetchRef = useRef(onFetchLiveCalls);
  liveFetchRef.current = onFetchLiveCalls;
  const hasLiveFetch = !!onFetchLiveCalls;
  useEffect(() => {
    if (!hasLiveFetch) return;
    let stopped = false;
    let inFlight = false;
    const tick = async () => {
      if (stopped || inFlight || document.hidden) return;
      inFlight = true;
      try {
        const rows = await liveFetchRef.current();
        if (stopped) return;
        const list = Array.isArray(rows) ? rows : [];
        if (list.length < liveCountRef.current) {
          setTimeout(() => silentlyRefreshCallsRef.current?.(), 6000);
        }
        liveCountRef.current = list.length;
        setLiveCalls(list);
      } catch {
        // A failed poll keeps the last state; the next tick retries.
      } finally {
        inFlight = false;
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [hasLiveFetch]);

  // ============= Auto-refresh recent calls =============
  // Quietly re-fetch calls on an interval so summaries appear without a
  // manual refresh. The silent fetch skips the loading spinner (no flicker)
  // and bails if a fetch is already in flight, the tab is backgrounded, or
  // the user is mid-read with a call expanded.
  const silentFetchInFlight = useRef(false);
  const expandedCallIdRef = useRef(expandedCallId);
  useEffect(() => {
    expandedCallIdRef.current = expandedCallId;
  }, [expandedCallId]);

  const silentlyRefreshCalls = useCallback(async () => {
    if (!onFetchCalls) return;
    if (silentFetchInFlight.current) return;
    if (document.hidden) return;            // pause when tab not visible
    if (expandedCallIdRef.current) return;  // don't yank the list out from under a reader
    silentFetchInFlight.current = true;
    try {
      const result = await onFetchCalls();
      setCalls((prev) => mergeCallsById(Array.isArray(result) ? result : [], prev));
      setCallsError("");
    } catch {
      // Swallow errors on silent polls — the manual Refresh button surfaces
      // errors; a transient poll failure shouldn't flash an error state.
    } finally {
      silentFetchInFlight.current = false;
    }
  }, [onFetchCalls]);
  silentlyRefreshCallsRef.current = silentlyRefreshCalls;

  useEffect(() => {
    if (!onFetchCalls) return;
    const POLL_MS = 10000; // 10s
    const id = setInterval(silentlyRefreshCalls, POLL_MS);
    // Refresh the moment the tab regains focus, too.
    const onVisible = () => {
      if (!document.hidden) silentlyRefreshCalls();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [onFetchCalls, silentlyRefreshCalls]);

  // Lock background scroll when any drawer, the forwarding guide modal, or
  // the Flyer Builder overlay is open
  useEffect(() => {
    if (knowledgeOpen || catalogOpen || forwardingGuideOpen || flyerBuilderOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [knowledgeOpen, catalogOpen, forwardingGuideOpen, flyerBuilderOpen]);

  // ============= Handlers =============
  const handleCopyNumber = async () => {
    if (!hasAgentNumber) return;
    try {
      await navigator.clipboard.writeText(agentNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };

  const handleAddRecipient = () => {
    setRecipients((curr) => [...curr, createDefaultRecipient()]);
    setRecipientsDirty(true);
  };

  const handleUpdateRecipient = (id, patch) => {
    setRecipients((curr) =>
      curr.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
    setRecipientsDirty(true);
  };

  const handleRemoveRecipient = (id) => {
    setRecipients((curr) => curr.filter((r) => r.id !== id));
    setRecipientsDirty(true);
  };

  const handleSaveRecipients = async () => {
    setSavingRecipients(true);
    try {
      // Strip empty rows (no phone)
      const cleaned = recipients
        .filter((r) => normalizeDigits(r.phone).length >= 10)
        .map((r) => ({
          ...r,
          name: (r.name || "").trim(),
          phone: toE164(r.phone),
        }));
      await onSaveRecipients(cleaned);
      setRecipients(cleaned.length > 0 ? cleaned : [createDefaultRecipient()]);
      setSavedRecipients(true);
      setRecipientsDirty(false);
      setTimeout(() => setSavedRecipients(false), 1800);
    } finally {
      setSavingRecipients(false);
    }
  };

  // ----- Transfer contact handlers -----

  const handleAddTransferContact = () => {
    setTransferContacts((curr) => [...curr, createDefaultTransferContact()]);
    setContactsDirty(true);
  };

  const handleUpdateTransferContact = (id, patch) => {
    setTransferContacts((curr) =>
      curr.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
    setContactsDirty(true);
  };

  const handleRemoveTransferContact = (id) => {
    setTransferContacts((curr) => curr.filter((c) => c.id !== id));
    setContactsDirty(true);
  };

  const handleSaveTransferContacts = async () => {
    setSavingContacts(true);
    try {
      // Same validation as the backend's coerce_transfer_contacts:
      // name AND a 10+ digit phone required. Anything else is dropped so
      // the user never persists an unroutable row.
      const cleaned = transferContacts
        .filter(
          (c) =>
            (c.name || "").trim().length > 0 &&
            normalizeDigits(c.phone).length >= 10
        )
        .map((c) => ({
          ...c,
          name: c.name.trim(),
          phone: toE164(c.phone),
          role: (c.role || "").trim(),
          // Drop windows with no days selected — they block nothing and the
          // backend's coerce_blackouts would discard them anyway. Keeps saved
          // state tidy and the row summary honest.
          blackouts: (Array.isArray(c.blackouts) ? c.blackouts : []).filter(
            (b) => Array.isArray(b.days) && b.days.length > 0
          ),
        }));
      await onSaveTransferContacts(cleaned);
      setTransferContacts(cleaned);
      setSavedContacts(true);
      setContactsDirty(false);
      setTimeout(() => setSavedContacts(false), 1800);
    } finally {
      setSavingContacts(false);
    }
  };

  const handleSaveKnowledge = async (next) => {
    setKnowledge(next);
    await onSaveKnowledge(next);
  };

  const handleEscalationsBannerClick = () => {
    if (onScrollToEscalations) {
      onScrollToEscalations();
    } else {
      setCallsFilter("escalated");
    }
  };

  // ============= Render helpers =============
  // The Recent Calls toolbar (filter chips + refresh) and list body are
  // shared between the desktop bento (right column) and the mobile pill
  // stack (top section). Lifted out as JSX consts to keep the body code
  // readable and avoid copy-paste drift. Closures capture all relevant
  // state — no prop drilling needed.
  const callsToolbar = (
    <div className="flex items-center gap-2 overflow-x-auto">
      <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5 flex-shrink-0">
        <FilterChip
          active={callsFilter === "all"}
          onClick={() => setCallsFilter("all")}
          label="All"
        />
        <FilterChip
          active={callsFilter === "escalated"}
          onClick={() => setCallsFilter("escalated")}
          label="Escalated"
        />
        <FilterChip
          active={callsFilter === "after_hours"}
          onClick={() => setCallsFilter("after_hours")}
          label="After-hrs"
        />
        <FilterChip
          active={callsFilter === "visits"}
          onClick={() => setCallsFilter("visits")}
          label="Visits"
        />
        <FilterChip
          active={callsFilter === "leads"}
          onClick={() => setCallsFilter("leads")}
          label="Leads"
        />
      </div>

      <button
        type="button"
        onClick={loadCalls}
        data-tip="Reloads the recent calls list"
        disabled={callsLoading || !onFetchCalls}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-all disabled:opacity-40"
      >
        <RefreshCw
          className={`w-3 h-3 ${callsLoading ? "animate-spin" : ""}`}
        />
        Refresh
      </button>
    </div>
  );

  // The two sales-floor boards, rendered ABOVE Recent Calls as siblings —
  // calls are a LOG you scan; visits and leads are WORKLISTS you clear.
  // Both render null when empty, so quiet stores see no extra chrome.
  // ----- Jump-to-call: open, expand, SCROLL, flash -----
  // Opening + expanding isn't enough on a long feed — the row can expand 40
  // calls below the fold. jumpTarget drives an effect that waits for the row
  // to exist AND be visible (mobile + desktop trees both live in the DOM,
  // CSS-hidden by breakpoint, so we must pick the rendered one), centers it,
  // and flashes a ring so the eye lands. `t` re-arms the same id twice.
  const [jumpTarget, setJumpTarget] = useState(null); // { id, t }
  const [flashCallId, setFlashCallId] = useState(null);
  useEffect(() => {
    if (!jumpTarget) return;
    let attempts = 0;
    let raf;
    const tryScroll = () => {
      const els = document.querySelectorAll(
        `[data-call-id="${jumpTarget.id}"]`
      );
      let target = null;
      els.forEach((el) => {
        if (!target && el.offsetParent !== null) target = el; // visible tree only
      });
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashCallId(jumpTarget.id);
        setTimeout(() => setFlashCallId(null), 1600);
      } else if (attempts++ < 20) {
        // Section is still mounting its rows (collapse → open just fired).
        raf = requestAnimationFrame(tryScroll);
      }
    };
    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [jumpTarget]);

  const jumpToCall = (callId) => {
    setCallsFilter("all"); // guarantee the row is in the filtered list
    setRecentCallsOpen(true); // desktop section
    setView("calls"); // r3: the log is its own view
    // Desktop shows it in the detail pane; the phone opens the same pane as
    // a full-screen sheet. Both key off selectedCallId.
    setSelectedCallId(callId);
    setJumpTarget({ id: callId, t: Date.now() }); // scroll + flash
  };

  const visitsCard = (
    <VisitsCard
      visits={upcomingVisits}
      visitStatusOf={visitStatusOf}
      onUpdateVisit={updateVisit}
      onJumpToCall={jumpToCall}
    />
  );

  const leadsCard = (
    <LeadsCard
      leads={leads}
      leadStatusOf={leadStatusOf}
      onUpdateLead={updateLead}
      onJumpToCall={jumpToCall}
    />
  );

  // ONE place builds a CallRow so its (growing) prop list can't drift
  // between the four call sites (desktop list, desktop peek, mobile list,
  // mobile peek).
  // split: desktop list + detail pane — the row selects instead of
  // expanding inline.
  const renderCallRow = (call, split = false) => (
    <CallRow
      key={call.id}
      call={call}
      split={split}
      selected={split && paneCallId === call.id}
      expanded={!split && expandedCallId === call.id}
      onToggle={() =>
        split
          ? setSelectedCallId(call.id)
          : setExpandedCallId((c) => (c === call.id ? null : call.id))
      }
      onOpenReceipt={tier === "integrated" ? onOpenReceipt : null}
      onMarkReviewed={markReviewed}
      isResolved={isCallResolved(call)}
      visitStatus={visitStatusOf(call)}
      onUpdateVisit={updateVisit}
      callbackDone={callbackDoneOf(call)}
      onUpdateCallback={updateCallback}
      deliveryStatus={deliveryStatusOf(call)}
      onUpdateDelivery={updateDelivery}
      flash={flashCallId === call.id}
    />
  );

  // The Needs-You queue — the promises the agent made out loud, plus the
  // fires. Page order below it IS the rest of the priority order (visits →
  // leads → log), so the queue itself stays focused on the three classes
  // nothing else surfaces: promised callbacks, deliveries awaiting the
  // promised confirmation call, and unresolved escalations.
  const needsYouQueue = (
    <NeedsYouQueue
      callbacks={openCallbacks}
      deliveries={pendingDeliveries}
      escalations={unresolvedEscalationCalls}
      callsToday={derivedStats.callsToday}
      onUpdateCallback={updateCallback}
      onUpdateDelivery={updateDelivery}
      onMarkReviewed={markReviewed}
      onJumpToCall={jumpToCall}
    />
  );

  const todayVisitCount = upcomingVisits.filter(({ visit }) => {
    const d = parseVisitDate(visit);
    if (!d) return false;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return d.getTime() === t.getTime();
  }).length;

  const visitsSummary =
    todayVisitCount > 0
      ? `${todayVisitCount} today · ${upcomingVisits.length} upcoming`
      : `${upcomingVisits.length} upcoming`;

  const leadsSummary =
    newLeadCount > 0
      ? `${newLeadCount} new · ${leads.length} total`
      : `${leads.length} to follow up`;

  // The call the desktop pane shows: the one picked, else the newest in the
  // current filter. History is that caller's other calls, newest first.
  const paneCall =
    calls.find((c) => c.id === selectedCallId) || filteredCalls[0] || null;
  const paneCallId = paneCall?.id ?? null;
  // Phone: the detail opens as a full-screen sheet only after an explicit tap
  // (paneCall above falls back to the newest call, which is desktop-only).
  const mobileDetailCall = selectedCallId
    ? calls.find((c) => c.id === selectedCallId) || null
    : null;
  const paneHistory = paneCall?.caller_phone
    ? calls.filter((c) => c.id !== paneCall.id && c.caller_phone === paneCall.caller_phone)
    : [];

  // j/k and ↑/↓ walk the desktop list (thread primaries, plus the open
  // thread's earlier calls) and keep the picked row in view.
  const paneOrder = useMemo(
    () =>
      threadCalls(filteredCalls).flatMap((t) =>
        t.older.some((c) => c.id === paneCallId) ? [t.primary, ...t.older] : [t.primary]
      ),
    [filteredCalls, paneCallId]
  );
  useEffect(() => {
    if (view !== "calls") return;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.closest?.("input, textarea, select, [contenteditable]")) return;
      if (!window.matchMedia("(min-width: 1024px)").matches) return;
      const dir = e.key === "j" || e.key === "ArrowDown" ? 1 : e.key === "k" || e.key === "ArrowUp" ? -1 : 0;
      if (!dir || paneOrder.length === 0) return;
      e.preventDefault();
      const i = paneOrder.findIndex((c) => c.id === paneCallId);
      const next = paneOrder[Math.min(paneOrder.length - 1, Math.max(0, i + dir))];
      setSelectedCallId(next.id);
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-split-list] [data-call-id="${next.id}"]`)
          ?.scrollIntoView({ block: "nearest" });
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, paneOrder, paneCallId]);

  const renderCallsList = (split = false) => callsError ? (
    <CallsEmptyState
      icon={AlertCircle}
      title="Couldn't load calls"
      body={callsError}
      tone="error"
    />
  ) : callsLoading && calls.length === 0 ? (
    <CallsEmptyState icon={Loader2} title="Loading calls…" body="" spinning />
  ) : filteredCalls.length === 0 ? (
    <CallsEmptyState
      icon={PhoneIncoming}
      title={
        callsFilter === "all"
          ? "No calls yet"
          : callsFilter === "escalated"
            ? "No escalated calls"
            : callsFilter === "visits"
              ? "No calls with visits"
              : callsFilter === "leads"
                ? "No lead calls"
                : "No after-hours calls"
      }
      body={
        callsFilter !== "all"
          ? "Try a different filter."
          : hasAgentNumber
            ? `When a customer calls ${agentNumber}, the call summary shows up here.`
            : "Once a number is assigned and customers start calling, summaries show up here."
      }
    />
  ) : (
    <ul className="divide-y divide-slate-100">
      {/* THREADED BY CALLER — the living-receipt law ("one thread per
          customer") applied to calls. Three calls from Phillip render as
          one primary row (his newest call) with his earlier calls tucked
          behind an expander, instead of three unrelated feed rows. */}
      {threadCalls(filteredCalls).map((thread) => (
        <CallThread
          key={thread.key}
          thread={thread}
          expandedCallId={split ? paneCallId : expandedCallId}
          renderRow={(c) => renderCallRow(c, split)}
        />
      ))}
      {blockedCalls.length > 0 && callsFilter === "all" && (
        <BlockedCallsFooter
          count={blockedCalls.length}
          open={blockedOpen}
          onToggle={() => setBlockedOpen((o) => !o)}
        >
          {blockedCalls.map((call) => renderCallRow(call, split))}
        </BlockedCallsFooter>
      )}
      {/* End-of-list pagination: auto-loads older pages as the sentinel
          scrolls into view (plus the button for taps/keyboards), until the
          server returns an empty batch. Renders only when the parent wired
          onFetchOlderCalls, so older builds lose nothing. */}
      {onFetchOlderCalls && (
        <li className="border-t border-slate-100">
          <OlderCallsFooter
            loading={loadingOlder}
            exhausted={olderExhausted}
            stale={olderStale}
            onLoad={loadOlderCalls}
          />
        </li>
      )}
    </ul>
  );

  // ----- Mobile pill summaries -----
  // Each MobileSection collapses to a one-line summary. These strings are
  // computed here so the render block stays clean.
  const recipientCount = recipients.length;
  const contactCount = transferContacts.length;
  const callsCount = filteredCalls.length;

  const callsSummary = [
    callsCount === 0
      ? callsFilter === "all"
        ? "No calls yet"
        : "None match this filter"
      : `${callsCount} ${callsCount === 1 ? "call" : "calls"}${
          callsFilter !== "all" ? " · filtered" : ""
        }`,
    upcomingVisits.length > 0
      ? `${upcomingVisits.length} ${upcomingVisits.length === 1 ? "visit" : "visits"} planned`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Agent Knowledge is now purely the "what your agent SAYS" surface —
  // hours, FAQs, tone. Anything about what the store SELLS moved to the
  // Catalog & Deals surface and gets its own summary line below.
  const knowledgeSummaryLine = [
    `${knowledgeSummary.qaCount} Q&A`,
    knowledgeSummary.hoursConfigured ? "hours set" : "hours not set",
    knowledgeSummary.closureCount > 0
      ? `${knowledgeSummary.closureCount} ${knowledgeSummary.closureCount === 1 ? "closure" : "closures"}`
      : null,
    knowledgeSummary.hasInstructions ? "custom instructions" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const catalogSummaryLine =
    knowledgeSummary.dealCount === 0 && knowledgeSummary.productCount === 0
      ? "No deals or products yet"
      : [
          knowledgeSummary.dealCount > 0
            ? `${knowledgeSummary.dealCount} ${knowledgeSummary.dealCount === 1 ? "deal" : "deals"}`
            : null,
          knowledgeSummary.productCount > 0
            ? `${knowledgeSummary.productCount} product${knowledgeSummary.productCount === 1 ? "" : "s"}`
            : null,
          knowledgeSummary.untaggedDeals > 0
            ? `${knowledgeSummary.untaggedDeals} untagged`
            : null,
        ]
          .filter(Boolean)
          .join(" · ");

  const notificationsSummary =
    recipientCount === 0
      ? "No one configured"
      : `${recipientCount} ${recipientCount === 1 ? "recipient" : "recipients"}`;

  const forwardingSummary =
    contactCount === 0
      ? "No contacts — agent won't transfer"
      : `${contactCount} ${contactCount === 1 ? "contact" : "contacts"}`;

  // The three set-once surfaces as one slim line of status pills, each
  // opening the same drawer its old hero card did. Amber = click-to-fix.
  const agentSetupBar = (
    <AgentSetupBar
      summary={knowledgeSummary}
      recipientCount={recipientCount}
      contactCount={contactCount}
      voiceLabel={
        effectiveVoiceOptions.find((o) => o.id === savedVoiceValue)?.label ||
        (voiceIsCustom ? "Custom voice" : "June")
      }
      onOpenKnowledge={() => setKnowledgeOpen(true)}
      onOpenCatalog={() => setCatalogOpen(true)}
      onOpenSettings={() => {
        setCallSettingsTab("notifications");
        setCallSettingsOpen(true);
      }}
    />
  );

  // ----- Kill switch control -----
  // Single source of truth for the toggle button, reused in the desktop
  // Agent Line bar and the mobile Agent Line section. `compact` trims it
  // for the tight desktop bar; full size for mobile where it's a primary
  // emergency affordance. Disabled (with an explanatory title) until the
  // parent wires up onToggleAgent.
  const renderKillSwitch = (compact = false) => {
    const wired = !!onToggleAgent;
    const paused = !agentEnabled;
    const base =
      "inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed select-none";
    const size = compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-[13px]";
    const tone = paused
      ? "bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm"
      : "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 hover:border-rose-300 active:bg-rose-100";
    const Icon = togglingAgent
      ? Loader2
      : paused
        ? Power
        : PowerOff;
    return (
      <button
        type="button"
        onClick={handleKillSwitchClick}
        disabled={togglingAgent || !wired}
        title={
          !wired
            ? "Kill switch not configured — pass onToggleAgent to enable."
            : paused
              ? "Agent is paused — tap to resume answering calls"
              : "Pause the agent — stops answering inbound calls"
        }
        aria-pressed={paused}
        className={`${base} ${size} ${tone} ${compact ? "" : "w-full"}`}
      >
        <Icon className={`w-4 h-4 ${togglingAgent ? "animate-spin" : ""}`} />
        {togglingAgent
          ? paused
            ? "Resuming…"
            : "Pausing…"
          : paused
            ? "Resume agent"
            : compact
              ? "Pause agent"
              : "Pause agent (emergency)"}
      </button>
    );
  };

  // ----- r3 Setup checklist facts -----
  const voiceLabel =
    effectiveVoiceOptions.find((o) => o.id === savedVoiceValue)?.label ||
    (voiceIsCustom ? "Custom voice" : "June");
  const catalogHasItems =
    knowledgeSummary.dealCount + knowledgeSummary.productCount > 0;
  const setupChecks = [
    hasAgentNumber,
    !!knowledgeSummary.hoursConfigured,
    knowledgeSummary.qaCount > 0,
    catalogHasItems && knowledgeSummary.untaggedDeals === 0,
    true, // voice always has a default
    recipientCount > 0,
  ];
  const setupDone = setupChecks.filter(Boolean).length;

  // The six Setup cards and the pause box, shared by the desktop grid and
  // the phone column so the two layouts cannot drift apart again.
  const setupCards = (
    <>
                <SetupCard
                  title="Phone line"
                  attention={!hasAgentNumber}
                  status={
                    hasAgentNumber
                      ? `${displayNumber} · forward your store line here so the agent answers`
                      : "No number assigned yet — contact support"
                  }
                  actions={
                    <>
                      {hasAgentNumber && (
                        <SetupBtn onClick={handleCopyNumber} tip="Copies the agent's number">
                          {copied ? "Copied" : "Copy number"}
                        </SetupBtn>
                      )}
                      <SetupBtn
                        onClick={() => setForwardingGuideOpen(true)}
                        tip="Step-by-step forwarding for landline, iPhone, Android and VoIP"
                      >
                        Forwarding guide
                      </SetupBtn>
                    </>
                  }
                />

                <SetupCard
                  title="Hours & closures"
                  attention={!knowledgeSummary.hoursConfigured}
                  status={
                    knowledgeSummary.hoursConfigured
                      ? `Weekly hours set${
                          knowledgeSummary.closureCount > 0
                            ? ` · ${knowledgeSummary.closureCount} ${knowledgeSummary.closureCount === 1 ? "closure" : "closures"}`
                            : ""
                        }`
                      : "Not set — the agent can't tell callers when you're open"
                  }
                  actions={
                    <SetupBtn
                      primary={!knowledgeSummary.hoursConfigured}
                      onClick={() => setKnowledgeOpen(true)}
                      tip="Opens hours, closures, Q&A and instructions"
                    >
                      {knowledgeSummary.hoursConfigured ? "Edit" : "Set hours"}
                    </SetupBtn>
                  }
                />

                <SetupCard
                  title="What the agent knows"
                  attention={knowledgeSummary.qaCount === 0}
                  status={
                    knowledgeSummary.qaCount === 0
                      ? "No Q&A answers yet — add delivery, financing and return answers"
                      : `${knowledgeSummary.qaCount} Q&A ${knowledgeSummary.qaCount === 1 ? "answer" : "answers"}${
                          knowledgeSummary.hasInstructions ? " · custom instructions set" : ""
                        }`
                  }
                  actions={
                    <SetupBtn
                      primary={knowledgeSummary.qaCount === 0}
                      onClick={() => setKnowledgeOpen(true)}
                      tip="Q&A answers and custom instructions the agent follows"
                    >
                      {knowledgeSummary.qaCount === 0 ? "Add answers" : "Edit"}
                    </SetupBtn>
                  }
                />

                <SetupCard
                  title="Catalog & deals"
                  attention={knowledgeSummary.untaggedDeals > 0 || !catalogHasItems}
                  status={
                    !catalogHasItems
                      ? "Nothing loaded — the agent can't quote products or text a deal"
                      : `${catalogSummaryLine}${
                          knowledgeSummary.untaggedDeals > 0
                            ? " — untagged deals can't be offered to callers"
                            : ""
                        }`
                  }
                  actions={
                    <>
                      <SetupBtn
                        onClick={() => setFlyerBuilderOpen(true)}
                        tip="Make a flyer the agent can text to callers"
                      >
                        Flyer builder
                      </SetupBtn>
                      <SetupBtn
                        primary={knowledgeSummary.untaggedDeals > 0 || !catalogHasItems}
                        onClick={() => setCatalogOpen(true)}
                        tip="Products, deals and photos the agent can quote and text"
                      >
                        {knowledgeSummary.untaggedDeals > 0
                          ? `Fix ${knowledgeSummary.untaggedDeals} ${knowledgeSummary.untaggedDeals === 1 ? "deal" : "deals"}`
                          : catalogHasItems
                            ? "Edit"
                            : "Add products"}
                      </SetupBtn>
                    </>
                  }
                />

                <SetupCard
                  title="Voice & greeting"
                  status={`Voice: ${voiceLabel} · English and Spanish`}
                  actions={
                    <SetupBtn
                      onClick={() => {
                        setCallSettingsTab("voice");
                        setCallSettingsOpen(true);
                      }}
                      tip="Pick the voice callers hear"
                    >
                      Edit
                    </SetupBtn>
                  }
                />

                <SetupCard
                  title="Transfers & notifications"
                  attention={recipientCount === 0}
                  status={`${forwardingSummary} · ${notificationsSummary}${
                    recipientCount > 0 ? " for call recaps" : " — nobody gets the call recap texts"
                  }`}
                  actions={
                    <>
                      <SetupBtn
                        onClick={() => {
                          setCallSettingsTab("transfers");
                          setCallSettingsOpen(true);
                        }}
                        tip="Who the agent can transfer live calls to"
                      >
                        Transfers
                      </SetupBtn>
                      <SetupBtn
                        primary={recipientCount === 0}
                        onClick={() => {
                          setCallSettingsTab("notifications");
                          setCallSettingsOpen(true);
                        }}
                        tip="Who gets texted a recap after each call"
                      >
                        {recipientCount === 0 ? "Add recipient" : "Notifications"}
                      </SetupBtn>
                    </>
                  }
                />

                <div className="col-span-full mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-5 py-4 rounded-xl border border-dashed border-slate-300 bg-white">
                  <div>
                    <div className="text-[15px] font-semibold text-slate-900">
                      {agentEnabled ? "Pause the agent" : "Agent is paused"}
                    </div>
                    <div className="text-[13px] text-slate-500">
                      {agentEnabled
                        ? "Calls stop being answered until you resume. Nothing else changes."
                        : "Inbound calls are not being answered right now."}
                    </div>
                  </div>
                  <div>{renderKillSwitch(true)}</div>
                </div>
    </>
  );

  return (
    <div className="overflow-hidden max-h-[calc(100vh-3rem)] flex flex-col rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] bg-white">
      {/* ============= COMPACT HEADER =============
          Desktop-only, and now a SINGLE bar. The old layout stacked two
          full-width bars here (title+stats, then a dedicated Agent Line
          row with the number, copy button, *72 hint, and forwarding
          guide link). That gave set-and-forget reference data the same
          visual weight as the surfaces you actually tune, and ate ~90px
          of vertical space above the fold.

          Now: title + stats on the left, and on the right a quiet
          "Agent line" button that opens a popover holding the number,
          copy action, and the forwarding guide. The kill switch stays
          inline and prominent — that one IS critical.

          On mobile the title comes from the dashboard wrapper and the
          agent line has its own MobileSection below; unchanged. */}
      {/* r3 header: identity + live status + one sentence of numbers on
          the left, the three-view switch on the right, and a single ⋯ menu
          holding the rarely-used actions (copy number, forwarding guide,
          pause). The six-stat strip, the "Agent line" popover and the red
          Pause button in the corner are gone. */}
      <div className="flex-shrink-0 relative px-6 h-16 border-b border-slate-200 bg-white hidden lg:flex items-center gap-5">
        <div className="flex items-center gap-3.5 min-w-0">
          <h1 className="text-[20px] font-bold text-slate-900 tracking-[-0.01em] m-0">
            Phone Agent
          </h1>
          <AgentStatusPill
            hasNumber={hasAgentNumber}
            enabled={agentEnabled}
            displayNumber={displayNumber}
          />
          <span
            className="text-[13px] text-slate-500 whitespace-nowrap"
            data-tip="Calls answered since midnight; after-hours calls would otherwise have gone to voicemail"
          >
            {derivedStats.callsToday} {derivedStats.callsToday === 1 ? "call" : "calls"} today
            {derivedStats.afterHoursToday > 0
              ? ` · ${derivedStats.afterHoursToday} after hours`
              : ""}
          </span>
        </div>
        <div className="flex-1" />
        <ViewSwitch
          view={view}
          onChange={setView}
          todoCount={todoItems.current.length}
          setupAttention={setupIncomplete || knowledgeSummary.untaggedDeals > 0}
        />
        <HeaderMenu
          open={headerMenuOpen}
          onToggle={() => setHeaderMenuOpen((o) => !o)}
          onClose={() => setHeaderMenuOpen(false)}
          hasNumber={hasAgentNumber}
          displayNumber={displayNumber}
          copied={copied}
          onCopy={handleCopyNumber}
          onGuide={() => setForwardingGuideOpen(true)}
          killSwitch={renderKillSwitch(false)}
        />
      </div>

      {/* ============= AGENT PAUSED BANNER =============
          Loud, unmissable state when the kill switch is engaged. Sits above
          everything (incl. escalations) because "the line is down" is the
          single most important thing to know on this screen. The button here
          is the fast path back on. */}
      {!agentEnabled && (
        <div className="flex-shrink-0 w-full px-4 py-3 bg-rose-50 border-b border-rose-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
            </span>
            <ShieldAlert className="w-4 h-4 text-rose-600 flex-shrink-0" />
            <span className="text-sm text-rose-900 font-medium min-w-0">
              <span className="font-bold">Agent paused.</span> Inbound calls are
              not being answered.
            </span>
          </div>
          <div className="flex-shrink-0">{renderKillSwitch(true)}</div>
        </div>
      )}

      {/* ============= KILL SWITCH ERROR (transient) ============= */}
      {killError && (
        <div className="flex-shrink-0 w-full px-4 py-2 bg-rose-50 border-b border-rose-200 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
          <span className="text-xs text-rose-800">{killError}</span>
        </div>
      )}

      {/* ============= BODY =============
          Two layouts share the same data and callbacks:

          MOBILE (lg:hidden) — vertical stack of compact pill sections.
            Recent Calls sits on top (default open) because it's the
            primary "what just happened" surface; the rest collapse to
            one-line summaries that expand inline on tap. Agent
            Knowledge is the one exception — its editor is rich enough
            that we open it in the existing KnowledgeDrawer rather than
            expanding inline.

          DESKTOP (hidden lg:grid) — the original bento: left column of
            config cards, right column dedicated to Recent Calls. The
            agent number lives in the dedicated Agent Line bar above,
            so there's no number pill in this layout.

          Recent Calls' toolbar and list body are shared between both
          via the callsToolbar const and renderCallsList above. */}
      <div className="flex-1 overflow-y-auto bg-slate-100/70 shadow-[inset_0_2px_4px_-2px_rgba(15,23,42,0.06)]">

        {/* ===================== MOBILE LAYOUT (r3) =====================
            The same three views as desktop, in one column, built from the
            same components. The phone-only MobileSection stack that
            predated the three-view rebuild is gone. */}
        <div className="lg:hidden">
          <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 pt-3 pb-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <AgentStatusPill
                hasNumber={hasAgentNumber}
                enabled={agentEnabled}
                displayNumber={displayNumber}
              />
              <HeaderMenu
                open={headerMenuOpen}
                onToggle={() => setHeaderMenuOpen((o) => !o)}
                onClose={() => setHeaderMenuOpen(false)}
                hasNumber={hasAgentNumber}
                displayNumber={displayNumber}
                copied={copied}
                onCopy={handleCopyNumber}
                onGuide={() => setForwardingGuideOpen(true)}
                killSwitch={renderKillSwitch(false)}
              />
            </div>
            <ViewSwitch
              view={view}
              onChange={setView}
              todoCount={todoItems.current.length}
              setupAttention={setupIncomplete || knowledgeSummary.untaggedDeals > 0}
              full
            />
          </div>

          <div className="p-4 space-y-4">
            {liveCalls.map((lc) => (
              <LiveCallBanner key={lc.call_control_id} live={lc} knownCalls={calls} />
            ))}

            {view === "today" && (
              <>
                <StatStrip
                  week={weekStats}
                  callsToday={derivedStats.callsToday}
                  afterHoursToday={derivedStats.afterHoursToday}
                  todo={todoItems.current}
                />
                <TodoList
                  items={todoItems.current}
                  older={todoItems.older}
                  olderOpen={olderOpen}
                  onToggleOlder={() => setOlderOpen((o) => !o)}
                  callsToday={derivedStats.callsToday}
                  onJumpToCall={jumpToCall}
                />
                <RecentCallsPreview
                  calls={visibleCalls}
                  loading={callsLoading}
                  onOpen={jumpToCall}
                  onSeeAll={() => setView("calls")}
                />
                <AgentCard
                  hasNumber={hasAgentNumber}
                  enabled={agentEnabled}
                  displayNumber={displayNumber}
                  lastCallAt={weekStats.lastCallAt}
                  copied={copied}
                  onCopy={handleCopyNumber}
                  onGuide={() => setForwardingGuideOpen(true)}
                  onSetup={() => setView("setup")}
                  setupDone={setupDone}
                />
                <TopicsCard topics={weekStats.topics} total={weekStats.calls} />
              </>
            )}

            {view === "calls" && (
              <div className={`${CARD} overflow-hidden`}>
                <div className="px-4 py-3 border-b border-slate-200">{callsToolbar}</div>
                <div data-split-list>{renderCallsList(true)}</div>
              </div>
            )}

            {view === "setup" && (
              <div className="space-y-2.5">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-[16px] font-bold text-slate-900 m-0">Setup</h2>
                  <span className="text-[13px] text-slate-500 whitespace-nowrap">{setupDone} of 6 complete</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <span
                      className="block h-full bg-emerald-600 transition-[width]"
                      style={{ width: `${(setupDone / 6) * 100}%` }}
                    />
                  </div>
                </div>
                {setupCards}
              </div>
            )}
          </div>
        </div>

        {/* ===================== DESKTOP LAYOUT ===================== */}
        {/* Restructured (round 2 of the redesign): the screen is a to-do
            list first, a caller directory second, and a settings panel a
            distant third. The old hero row of config teaser cards (Agent
            Knowledge / Catalog & Deals / Call Settings) ate the first
            screenful with set-once surfaces; those three now live as one
            slim status-pill bar BELOW the work (see agentSetupBar), each
            pill opening the same drawer the old card did, amber when
            something needs fixing. The top of the page belongs to the
            Needs-You queue and the boards. */}
        <div
          data-agent-tab-build="r3.0"
          className="hidden lg:block px-6 py-6 min-h-full"
        >
          {/* r3.1: full width. The 1040px cap left half a wide screen
              empty; Today now spends it on a stat strip and a right rail
              (agent status, what callers asked about) beside the work. */}
          <div className="w-full max-w-[1760px] mx-auto space-y-5">
            {liveCalls.map((lc) => (
              <LiveCallBanner key={lc.call_control_id} live={lc} knownCalls={calls} />
            ))}
            {/* ---------------- TODAY ---------------- */}
            {view === "today" && (
              <>
                <StatStrip
                  week={weekStats}
                  callsToday={derivedStats.callsToday}
                  afterHoursToday={derivedStats.afterHoursToday}
                  todo={todoItems.current}
                />
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
                  <div className="space-y-5 min-w-0">
                    <TodoList
                      items={todoItems.current}
                      older={todoItems.older}
                      olderOpen={olderOpen}
                      onToggleOlder={() => setOlderOpen((o) => !o)}
                      callsToday={derivedStats.callsToday}
                      onJumpToCall={jumpToCall}
                    />
                    <RecentCallsPreview
                      calls={visibleCalls}
                      loading={callsLoading}
                      onOpen={jumpToCall}
                      onSeeAll={() => setView("calls")}
                    />
                  </div>
                  <aside className="space-y-5 min-w-0 xl:sticky xl:top-6">
                    <AgentCard
                      hasNumber={hasAgentNumber}
                      enabled={agentEnabled}
                      displayNumber={displayNumber}
                      lastCallAt={weekStats.lastCallAt}
                      copied={copied}
                      onCopy={handleCopyNumber}
                      onGuide={() => setForwardingGuideOpen(true)}
                      onSetup={() => setView("setup")}
                      setupDone={setupDone}
                    />
                    <TopicsCard topics={weekStats.topics} total={weekStats.calls} />
                  </aside>
                </div>
              </>
            )}

            {/* ---------------- CALLS ---------------- */}
            {view === "calls" && (
              // List + detail (Linear triage / Intercom inbox): the log on
              // the left selects, the pane on the right reads. j/k or ↑/↓
              // move through the list.
              <div className="grid grid-cols-[minmax(360px,440px)_minmax(0,1fr)] gap-5 items-start">
                <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] flex flex-col overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 flex flex-col gap-2">
                    <div className="flex items-baseline justify-between">
                      <h2 className="text-[16px] font-bold text-slate-900 m-0">Calls</h2>
                      <span className="text-[11px] text-slate-400">j / k to move</span>
                    </div>
                    {callsToolbar}
                  </div>
                  <div data-split-list className="overflow-y-auto max-h-[calc(100vh-13rem)]">
                    {renderCallsList(true)}
                  </div>
                </div>
                <CallDetailPane
                  call={paneCall}
                  history={paneHistory}
                  isResolved={paneCall ? isCallResolved(paneCall) : false}
                  visitStatus={paneCall ? visitStatusOf(paneCall) : "planned"}
                  callbackDone={paneCall ? callbackDoneOf(paneCall) : false}
                  deliveryStatus={paneCall ? deliveryStatusOf(paneCall) : "requested"}
                  onUpdateVisit={updateVisit}
                  onUpdateCallback={updateCallback}
                  onUpdateDelivery={updateDelivery}
                  onMarkReviewed={markReviewed}
                  onOpenReceipt={tier === "integrated" ? onOpenReceipt : null}
                  onSelect={setSelectedCallId}
                  onFetchDetail={onFetchCallDetail}
                />
              </div>
            )}

            {/* ---------------- SETUP ---------------- */}
            {view === "setup" && (
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2.5">
                <div className="col-span-full flex items-center gap-3.5 mb-1">
                  <h2 className="text-[16px] font-bold text-slate-900 m-0">Setup</h2>
                  <span className="text-[13px] text-slate-500">{setupDone} of 6 complete</span>
                  <div className="h-1.5 w-56 rounded-full bg-slate-200 overflow-hidden">
                    <span
                      className="block h-full bg-emerald-600 transition-[width]"
                      style={{ width: `${(setupDone / 6) * 100}%` }}
                    />
                  </div>
                </div>

                {setupCards}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============= PHONE CALL DETAIL (full-screen sheet) =============
          The same CallDetailPane the desktop split view uses, pushed over
          the tab. Back returns to the Calls list. */}
      {mobileDetailCall && (
        <div className="lg:hidden fixed inset-0 z-[70] bg-slate-100 flex flex-col">
          <div className="flex-shrink-0 bg-white" style={{ height: "env(safe-area-inset-top)" }} />
          <div className="flex-shrink-0 h-12 px-2 flex items-center bg-white border-b border-slate-200">
            <button
              type="button"
              onClick={() => setSelectedCallId(null)}
              className="min-w-[44px] h-11 px-2 flex items-center gap-0.5 text-blue-600 -ml-1"
            >
              <ChevronLeft className="w-6 h-6" />
              <span className="text-[16px]">Calls</span>
            </button>
            <div className="flex-1 min-w-0 text-center px-2">
              <p className="text-[16px] font-semibold text-slate-900 truncate m-0">Call</p>
            </div>
            <span className="min-w-[44px]" />
          </div>
          <div
            className="flex-1 overflow-y-auto p-3"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
          >
            <CallDetailPane
              call={mobileDetailCall}
              history={paneHistory}
              isResolved={isCallResolved(mobileDetailCall)}
              visitStatus={visitStatusOf(mobileDetailCall)}
              callbackDone={callbackDoneOf(mobileDetailCall)}
              deliveryStatus={deliveryStatusOf(mobileDetailCall)}
              onUpdateVisit={updateVisit}
              onUpdateCallback={updateCallback}
              onUpdateDelivery={updateDelivery}
              onMarkReviewed={markReviewed}
              onOpenReceipt={tier === "integrated" ? onOpenReceipt : null}
              onSelect={setSelectedCallId}
              onFetchDetail={onFetchCallDetail}
              mobile
            />
          </div>
        </div>
      )}

      {/* ============= AGENT KNOWLEDGE DRAWER =============
          Info only now: hours, Q&A, custom instructions. Everything about
          what the store SELLS moved to CatalogDrawer below. */}
      <KnowledgeDrawer
        open={knowledgeOpen}
        onClose={() => setKnowledgeOpen(false)}
        knowledge={knowledge}
        onSave={handleSaveKnowledge}
        tier={tier}
        businessType={businessType}
      />

      {/* ============= CATALOG & DEALS DRAWER =============
          The catalog import, active deals, featured products, and the flyer
          builder launcher — one surface. All the optimistic-update wiring
          below is unchanged; it just points at the new drawer. */}
      <CatalogDrawer
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        currentStore={currentStore}
        currentDeals={currentDeals}
        onUploadCurrentDeal={onUploadCurrentDeal}
        onCreateCurrentDeal={async (payload) => {
          const deal = await onCreateCurrentDeal(payload);
          // Optimistically prepend so the panel reflects the new deal even
          // if the parent's refetch is slow. The parent's next initialDeals
          // sync will reconcile.
          if (deal && deal.id) {
            setCurrentDeals((prev) => [deal, ...prev.filter((d) => d.id !== deal.id)]);
          }
          return deal;
        }}
        onDeleteCurrentDeal={async (id, hard) => {
          await onDeleteCurrentDeal(id, hard);
          setCurrentDeals((prev) => prev.filter((d) => d.id !== id));
        }}
        onUpdateCurrentDeal={
          onUpdateCurrentDeal
            ? async (id, patch) => {
                const updated = await onUpdateCurrentDeal(id, patch);
                // Optimistic merge (server row wins when returned) so the
                // card reflects the change without waiting on a refetch.
                setCurrentDeals((prev) =>
                  prev.map((d) =>
                    d.id === id ? { ...d, ...patch, ...(updated || {}) } : d
                  )
                );
                return updated;
              }
            : null
        }
        onEnrichCurrentDeals={
          onEnrichCurrentDeals
            ? async () => {
                const res = await onEnrichCurrentDeals();
                const tagged = Array.isArray(res?.tagged) ? res.tagged : [];
                if (tagged.length) {
                  setCurrentDeals((prev) =>
                    prev.map((d) => {
                      const hit = tagged.find((t) => t.id === d.id);
                      return hit
                        ? { ...d, deal_category: hit.category || d.deal_category }
                        : d;
                    })
                  );
                }
                return res;
              }
            : null
        }
        onOpenFlyerBuilder={() => setFlyerBuilderOpen(true)}
      />

      {/* ============= FORWARDING GUIDE MODAL ============= */}
      <ForwardingGuideModal
        open={forwardingGuideOpen}
        onClose={() => setForwardingGuideOpen(false)}
        agentNumber={agentNumber}
        hasAgentNumber={hasAgentNumber}
        displayNumber={displayNumber}
        copied={copied}
        onCopy={handleCopyNumber}
      />

      {/* ============= CALL SETTINGS DRAWER =============
          One drawer, two tabs — replaces the separate Notifications and
          Call Forwarding drawers. Both cards keep their own save buttons
          (bare mode), so each tab persists independently; `dirty` is the
          union so closing with unsaved work in EITHER tab still warns. */}
      <SideDrawer
        open={callSettingsOpen}
        onClose={() => setCallSettingsOpen(false)}
        icon={Phone}
        iconBg="bg-slate-100"
        iconColor="text-slate-600"
        title="Call Settings"
        subtitle="Notifications, live-call transfers, and the agent's voice"
        dirty={recipientsDirty || contactsDirty || voiceDirty}
      >
        {/* Tab bar — full-bleed to the drawer edges so the underline reads
            as a real tab strip, not a floating pill row. */}
        <div
          className="flex items-stretch gap-1 border-b border-slate-200 -mt-5 mb-5 -mx-4 sm:-mx-5 px-4 sm:px-5 pt-1 sticky top-0 bg-white z-10"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={callSettingsTab === "notifications"}
            onClick={() => setCallSettingsTab("notifications")}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap ${
              callSettingsTab === "notifications"
                ? "border-emerald-500 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Notifications
            {recipientCount > 0 && (
              <span
                className={`ml-0.5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                  callSettingsTab === "notifications"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {recipientCount}
              </span>
            )}
            {recipientsDirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Unsaved changes" />
            )}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={callSettingsTab === "transfers"}
            onClick={() => setCallSettingsTab("transfers")}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap ${
              callSettingsTab === "transfers"
                ? "border-purple-500 text-purple-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <PhoneForwarded className="w-3.5 h-3.5" />
            Call Transfers
            {contactCount > 0 && (
              <span
                className={`ml-0.5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                  callSettingsTab === "transfers"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {contactCount}
              </span>
            )}
            {contactsDirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Unsaved changes" />
            )}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={callSettingsTab === "voice"}
            onClick={() => setCallSettingsTab("voice")}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold border-b-2 transition-all whitespace-nowrap ${
              callSettingsTab === "voice"
                ? "border-blue-500 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Volume2 className="w-3.5 h-3.5" />
            Voice
            {voiceDirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Unsaved changes" />
            )}
          </button>
        </div>

        {callSettingsTab === "notifications" && (
          <>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Everyone here gets texted when calls complete, escalate, or come
              in after hours.
            </p>
            <NotificationsCard
              bare
              recipients={recipients}
              onAdd={handleAddRecipient}
              onUpdate={handleUpdateRecipient}
              onRemove={handleRemoveRecipient}
              onSave={handleSaveRecipients}
              saving={savingRecipients}
              saved={savedRecipients}
              dirty={recipientsDirty}
            />
          </>
        )}

        {callSettingsTab === "transfers" && (
          <>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              People your agent can transfer live calls to — by name (“Is Mike
              there?”) or by role (“the owner”).
            </p>
            <CallForwardingCard
              bare
              contacts={transferContacts}
              onAdd={handleAddTransferContact}
              onUpdate={handleUpdateTransferContact}
              onRemove={handleRemoveTransferContact}
              onSave={handleSaveTransferContacts}
              saving={savingContacts}
              saved={savedContacts}
              dirty={contactsDirty}
            />
          </>
        )}

        {callSettingsTab === "voice" && (
          <>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              How your agent sounds on every call. Changes apply to the next
              call — no restart needed.
            </p>
            <VoiceCard
              options={effectiveVoiceOptions}
              value={voiceChoice}
              customActive={voiceIsCustom && voiceChoice === savedVoiceValue}
              onSelect={(id) => {
                setVoiceChoice(id);
                setVoiceError(null);
              }}
              onSave={handleSaveVoice}
              saving={savingVoice}
              saved={savedVoice}
              dirty={voiceDirty}
              error={voiceError}
            />
          </>
        )}
      </SideDrawer>

      {/* ============= KILL SWITCH CONFIRM MODAL ============= */}
      <KillSwitchConfirmModal
        open={killConfirmOpen}
        busy={togglingAgent}
        agentNumber={hasAgentNumber ? displayNumber : ""}
        onCancel={() => setKillConfirmOpen(false)}
        onConfirm={() => applyAgentToggle(false)}
      />

      {/* ============= FLYER BUILDER OVERLAY =============
          The AI flyer generator, relocated from the retired Marketing
          Studio. Full-screen at z-[60] so it sits ABOVE the Knowledge
          drawer (z-50) it's launched from. Only the current-deal format
          is offered: every save auto-publishes to this agent's Current
          Deals (is_current_deal=true via /api/designs), which the agent
          leads with on calls and send_flyer prefers. Content is
          conditionally mounted so the heavy builder isn't in the tree
          while closed. */}
      {flyerBuilderOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-50 flex flex-col"
          role="dialog"
          aria-label="AI Flyer Builder"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          {/* Header bar */}
          <div className="flex-shrink-0 h-14 px-4 sm:px-5 flex items-center justify-between gap-3 bg-white border-b border-slate-200">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-4 h-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-slate-900 truncate">
                  AI Flyer Builder
                </h2>
                <p className="text-[11px] text-slate-500 truncate">
                  Saved designs auto-publish to your agent's Current Deals
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCloseFlyerBuilder}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors flex-shrink-0"
              aria-label="Close flyer builder"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Builder body — the split variant expects a bounded flex column
              (same structure the old Marketing Studio gave it). */}
          <div className="flex-1 min-h-0 px-4 sm:px-5 py-4">
            <FlyerBuilder
              storeId={currentStore?.id}
              businessName={currentStore?.name || "Your Store"}
              businessInfo={currentStore?.address || ""}
              addNotification={addNotification}
              variant="split"
              format={CURRENT_DEAL_FORMAT}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   ================ SUBCOMPONENTS — BENTO CARDS ===================
   ================================================================ */

/* PhoneNumberCard was removed — the agent number now lives in the
   slim "Agent Line" bar at the top of PhoneAgentTab. Forwarding setup
   is set-and-forget; it doesn't deserve hero treatment in the bento. */

const NotificationsCard = ({
  recipients,
  onAdd,
  onUpdate,
  onRemove,
  onSave,
  saving,
  saved,
  dirty,
  bare = false,  // When true, skip outer card chrome + title block (host
                 // — typically MobileSection — provides its own wrapper).
}) => {
  const body = (
    <>
      {!bare && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
            <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
              Notifications
            </h2>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Everyone here gets texted when calls complete, escalate, or come in after hours.
          </p>
        </>
      )}

      <ul className="space-y-2 mb-3">
        {recipients.map((r, idx) => (
          <RecipientRow
            key={r.id}
            recipient={r}
            isLast={recipients.length === 1}
            onUpdate={(patch) => onUpdate(r.id, patch)}
            onRemove={() => onRemove(r.id)}
            autoFocus={idx === recipients.length - 1 && !r.phone && !r.name}
          />
        ))}
      </ul>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 px-2 py-1 -ml-2 rounded-md text-[12px] font-semibold text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Add recipient
        </button>

        <button
          type="button"
          onClick={onSave}
          disabled={saving || !dirty}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all min-w-[80px] flex items-center justify-center gap-1 ${
            saved
              ? "bg-emerald-600 text-white"
              : !dirty
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saved ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Saved
            </>
          ) : (
            "Save changes"
          )}
        </button>
      </div>
    </>
  );

  if (bare) return body;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      {body}
    </div>
  );
};

const RecipientRow = ({ recipient, isLast, onUpdate, onRemove, autoFocus }) => {
  return (
    <li className="group bg-slate-50 hover:bg-slate-50/80 border border-slate-200 rounded-lg p-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="relative flex-1">
          <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Name (optional)"
            value={recipient.name || ""}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="w-full pl-8 pr-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={isLast}
          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Remove recipient"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus={autoFocus}
            placeholder="+1 (555) 123-4567"
            value={recipient.phone || ""}
            onChange={(e) => onUpdate({ phone: e.target.value })}
            onBlur={(e) => {
              const e164 = toE164(e.target.value);
              if (e164 !== e.target.value) onUpdate({ phone: e164 });
            }}
            className="w-full pl-8 pr-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
        </div>
      </div>
    </li>
  );
};

/* ----------------------------------------------------------------
   CALL FORWARDING — multi-contact directory
   ----------------------------------------------------------------
   Mirrors the NotificationsCard pattern but with required-name rules
   (the AI uses the name as the routing key) and an optional role
   field (so callers can also ask for "the owner" / "the manager").
   ---------------------------------------------------------------- */

const CallForwardingCard = ({
  contacts,
  onAdd,
  onUpdate,
  onRemove,
  onSave,
  saving,
  saved,
  dirty,
  bare = false,  // When true, skip outer card chrome + title block.
}) => {
  const isEmpty = !contacts || contacts.length === 0;

  const body = (
    <>
      {!bare && (
        <>
          <div className="flex items-center gap-2 mb-1">
            <PhoneForwarded className="w-3.5 h-3.5 text-purple-600" />
            <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
              Call Forwarding
            </h2>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            People your agent can transfer live calls to — by name (“Is Mike
            there?”) or by role (“the owner”).
          </p>
        </>
      )}

      {isEmpty ? (
        <div className="mb-3 px-3 py-3 rounded-lg bg-slate-50 border border-dashed border-slate-200 text-[12px] text-slate-500 leading-relaxed">
          No one configured yet. Until you add someone, the agent will take
          a message instead of trying to transfer.
        </div>
      ) : (
        <ul className="space-y-2 mb-3">
          {contacts.map((c, idx) => (
            <TransferContactRow
              key={c.id}
              contact={c}
              onUpdate={(patch) => onUpdate(c.id, patch)}
              onRemove={() => onRemove(c.id)}
              autoFocus={
                idx === contacts.length - 1 && !c.phone && !c.name
              }
            />
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 px-2 py-1 -ml-2 rounded-md text-[12px] font-semibold text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Add person
        </button>

        <button
          type="button"
          onClick={onSave}
          disabled={saving || !dirty}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all min-w-[80px] flex items-center justify-center gap-1 ${
            saved
              ? "bg-emerald-600 text-white"
              : !dirty
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saved ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Saved
            </>
          ) : (
            "Save changes"
          )}
        </button>
      </div>
    </>
  );

  if (bare) return body;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      {body}
    </div>
  );
};

/* ----------------------------------------------------------------
   BlackoutEditor — per-contact "don't forward to me" scheduling.
   ----------------------------------------------------------------
   Lets each forwarded number pick the days AND time-of-day they do
   NOT want live calls routed to them. Multiple windows are allowed
   (e.g. all weekend + weeknights after 6pm). At call time the backend
   filters anyone currently inside a window out of BOTH the agent's
   directory and the dialable transfer_list, so the agent never offers
   to connect a caller to someone who isn't taking calls right now.
   ---------------------------------------------------------------- */
const BlackoutEditor = ({ blackouts, onChange }) => {
  const rules = Array.isArray(blackouts) ? blackouts : [];

  const updateRule = (id, patch) =>
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const toggleDay = (id, dayKey) =>
    onChange(
      rules.map((r) => {
        if (r.id !== id) return r;
        const has = (r.days || []).includes(dayKey);
        return {
          ...r,
          days: has
            ? r.days.filter((d) => d !== dayKey)
            : [...(r.days || []), dayKey],
        };
      })
    );

  const addRule = () => onChange([...rules, createDefaultBlackout()]);
  const removeRule = (id) => onChange(rules.filter((r) => r.id !== id));

  return (
    <div className="space-y-2">
      {rules.length === 0 ? (
        <p className="text-[11px] text-slate-400 leading-relaxed">
          No limits set — this person can receive forwarded calls at any time.
        </p>
      ) : (
        rules.map((r) => {
          const noDays = (r.days || []).length === 0;
          return (
            <div
              key={r.id}
              className="rounded-md border border-slate-200 bg-white p-2.5"
            >
              {/* Day chips */}
              <div className="flex items-center gap-1 flex-wrap mb-2">
                {BLACKOUT_DAYS.map((d) => {
                  const active = (r.days || []).includes(d.key);
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => toggleDay(r.id, d.key)}
                      data-tip="Include or exclude this day from the window"
                      className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-all ${
                        active
                          ? "bg-blue-600 text-white"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {d.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => removeRule(r.id)}
                  className="ml-auto p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                  aria-label="Remove window"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Time range up front; "All day" is an opt-in button beside it.
                  The times lead so it's obvious you're picking a window — the
                  all-day state only appears once they actually choose it. */}
              <div className="flex items-center gap-2 flex-wrap">
                {r.allDay ? (
                  <span className="px-2.5 py-1.5 rounded-md bg-purple-50 text-purple-700 text-[12px] font-semibold">
                    Unavailable all day
                  </span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <input
                      type="time"
                      value={r.start || "17:00"}
                      onChange={(e) => updateRule(r.id, { start: e.target.value })}
                      className="px-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-[11px] text-slate-400">to</span>
                    <input
                      type="time"
                      value={r.end || "09:00"}
                      onChange={(e) => updateRule(r.id, { end: e.target.value })}
                      className="px-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => updateRule(r.id, { allDay: !r.allDay })}
                  data-tip="Makes the whole day unavailable instead of a time range"
                  className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-all ${
                    r.allDay
                      ? "bg-purple-600 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  All day
                </button>
              </div>

              {noDays && (
                <p className="mt-1.5 text-[11px] text-amber-700">
                  Pick at least one day for this window to take effect.
                </p>
              )}
              {!r.allDay && r.start > r.end && (r.days || []).length > 0 && (
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Overnight window — carries into the next morning.
                </p>
              )}
            </div>
          );
        })
      )}

      <button
        type="button"
        onClick={addRule}
        data-tip="Adds days and times when this person can't take transfers"
        className="flex items-center gap-1 px-2 py-1 -ml-1 rounded-md text-[11px] font-semibold text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition-all"
      >
        <Plus className="w-3 h-3" />
        Add unavailable window
      </button>
    </div>
  );
};

const TransferContactRow = ({ contact, onUpdate, onRemove, autoFocus }) => {
  const phoneValid = normalizeDigits(contact.phone).length >= 10;
  const nameValid = (contact.name || "").trim().length > 0;
  const hasAnyInput = !!(contact.name || contact.phone || contact.role);
  const isComplete = phoneValid && nameValid;

  const blackouts = Array.isArray(contact.blackouts) ? contact.blackouts : [];
  const hasLimits = blackouts.some((b) => (b.days || []).length > 0);
  const [showHours, setShowHours] = useState(false);

  return (
    <li className="group bg-slate-50 hover:bg-slate-50/80 border border-slate-200 rounded-lg p-2.5">
      {/* Row 1: Name + remove */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="relative flex-1">
          <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Name (required)"
            value={contact.name || ""}
            autoFocus={autoFocus}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className={`w-full pl-8 pr-2 py-1.5 rounded-md bg-white border text-slate-900 placeholder-slate-400 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
              !nameValid && hasAnyInput
                ? "border-amber-300"
                : "border-slate-200"
            }`}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
          aria-label="Remove contact"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Row 2: Phone + Role */}
      <div className="flex items-stretch gap-2">
        <div className="relative flex-[2]">
          <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+1 (555) 123-4567"
            value={contact.phone || ""}
            onChange={(e) => onUpdate({ phone: e.target.value })}
            onBlur={(e) => {
              const e164 = toE164(e.target.value);
              if (e164 !== e.target.value) onUpdate({ phone: e164 });
            }}
            className="w-full pl-8 pr-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
        </div>
        <div className="relative flex-1">
          <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Role (optional)"
            value={contact.role || ""}
            onChange={(e) => onUpdate({ role: e.target.value })}
            className="w-full pl-8 pr-2 py-1.5 rounded-md bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
          />
        </div>
      </div>

      {hasAnyInput && !isComplete && (
        <p className="mt-1.5 text-[11px] text-amber-700">
          Both name and phone are required for the agent to transfer.
        </p>
      )}

      {/* Row 3: Availability ("don't forward to me") — collapsed by default */}
      <div className="mt-2 pt-2 border-t border-slate-200/70">
        <button
          type="button"
          onClick={() => setShowHours((s) => !s)}
          data-tip="Show or hide when this person can't take transferred calls"
          className="w-full flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-700 transition-colors"
        >
          <Clock className="w-3.5 h-3.5" />
          <span>Availability</span>
          <span
            className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              hasLimits
                ? "bg-purple-50 text-purple-700"
                : "bg-slate-100 text-slate-400"
            }`}
          >
            {summarizeBlackouts(blackouts)}
          </span>
          {showHours ? (
            <ChevronUp className="w-3.5 h-3.5 ml-auto" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 ml-auto" />
          )}
        </button>

        {showHours && (
          <div className="mt-2">
            <p className="text-[11px] text-slate-500 leading-relaxed mb-2">
              Days and times calls should <span className="font-semibold">not</span> be
              forwarded to this person. While they’re unavailable the agent won’t
              offer to connect callers to them — it routes to someone else or
              takes a message.
            </p>
            <BlackoutEditor
              blackouts={blackouts}
              onChange={(next) => onUpdate({ blackouts: next })}
            />
          </div>
        )}
      </div>
    </li>
  );
};

/* ----------------------------------------------------------------
   MOBILE SECTION — collapsible pill wrapper
   ----------------------------------------------------------------
   On mobile (<lg) the Phone Agent surfaces are collapsed into a
   single stack of compact pill rows. Each pill shows an icon, title,
   and one-line summary, and either:
     - expands inline to reveal `children` (default), or
     - acts as a one-tap trigger if `onClick` is passed (used for the
       Agent Knowledge pill, which opens the existing KnowledgeDrawer
       — its full editor is too rich for inline expansion).

   Optional slots:
     - `peek` — content rendered below the header WHEN COLLAPSED. Used
       by Recent Calls to surface the latest call inline so the pill is
       glanceable without tapping.
     - `bodyClassName` — overrides the wrapper around `children` when
       expanded. Default is "border-t border-slate-100 p-4". Useful when
       the children manage their own padding/scroll (Recent Calls uses
       this for the toolbar + max-height scrollable list).
   ---------------------------------------------------------------- */

const MobileSection = ({
  icon: Icon,
  iconBg = "bg-slate-50",
  iconColor = "text-slate-600",
  title,
  summary,
  defaultOpen = false,
  onClick = null,
  children = null,
  peek = null,
  bodyClassName = "border-t border-slate-100 p-4",
  badge = null,
  openSignal = null,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  // A parent can force this section open (e.g. jump-to-call from a board):
  // pass a CHANGING truthy value (a timestamp) — each new signal opens it,
  // while the user keeps full manual control in between.
  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);
  const isTrigger = !!onClick;
  const hasChildren = !isTrigger && !!children;
  const showPeek = !isTrigger && !open && peek;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={isTrigger ? onClick : () => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left active:bg-slate-50 transition-colors"
        aria-expanded={hasChildren ? open : undefined}
      >
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-lg ${iconBg} ${iconColor} flex-shrink-0`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold text-slate-900 truncate">
              {title}
            </h2>
            {badge}
          </div>
          {summary && (
            <div className="text-[11px] text-slate-500 truncate">{summary}</div>
          )}
        </div>
        {isTrigger ? (
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {showPeek && (
        <div className="border-t border-slate-100">{peek}</div>
      )}
      {hasChildren && open && (
        <div className={bodyClassName}>{children}</div>
      )}
    </div>
  );
};


/* `large` renders the primary desktop treatment (bigger heading, roomier
   padding, catalog line in the body copy). Every class below is written as a
   complete literal string — Tailwind scans source as plain text, so an
   interpolated class like `p-${large ? "5" : "4"}` never gets generated and
   silently renders as nothing. */
/* NOTE: KnowledgeTeaserCard / CatalogTeaserCard / TeaserCard are no longer
   mounted anywhere — AgentSetupBar (the slim status-pill line below the
   work surfaces) replaced their desktop hero row. Kept intact in case an
   onboarding or marketing surface wants the rich-card treatment back. */
const KnowledgeTeaserCard = ({ summary, onOpen, large = false }) => {
  const items = [
    { label: summary.hoursConfigured ? "Hours set" : "Hours not set" },
    { label: `${summary.qaCount} Q&A ${summary.qaCount === 1 ? "entry" : "entries"}` },
    summary.closureCount > 0
      ? { label: `${summary.closureCount} ${summary.closureCount === 1 ? "closure" : "closures"} scheduled` }
      : null,
    summary.hasInstructions ? { label: "Custom instructions" } : null,
  ].filter(Boolean);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative w-full h-full text-left bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden hover:border-indigo-300 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px transition-all group"
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-indigo-50/60 via-white to-white" />

      <div className={large ? "relative p-6" : "relative p-5"}>
        <div className={large ? "flex items-center justify-between mb-4" : "flex items-center justify-between mb-3"}>
          <div className="flex items-center gap-2">
            <div
              className={
                large
                  ? "w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0"
                  : "contents"
              }
            >
              <BookOpen className={large ? "w-4 h-4 text-indigo-600" : "w-3.5 h-3.5 text-indigo-600"} />
            </div>
            <h2
              className={
                large
                  ? "text-[13px] font-bold text-slate-900 uppercase tracking-[0.08em]"
                  : "text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]"
              }
            >
              Agent Knowledge
            </h2>
          </div>
          <ArrowRight
            className={
              large
                ? "w-5 h-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all"
                : "w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all"
            }
          />
        </div>

        <p
          className={
            large
              ? "text-[15px] text-slate-700 leading-relaxed mb-4 max-w-xl"
              : "text-sm text-slate-700 leading-relaxed mb-3"
          }
        >
          {large
            ? "What your agent tells callers — opening hours, holiday closures, answers to common questions, and how it should sound."
            : "What your agent knows about your business — hours, FAQs, and custom instructions."}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {items.map((item, i) => (
            <span
              key={i}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                item.tone === "warn"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
};

/* ----------------------------------------------------------------
   CatalogTeaserCard — the second primary surface.
   ----------------------------------------------------------------
   Agent Knowledge answers "what does my agent SAY?". This one answers
   "what does my agent SELL?". Previously these were tangled: the
   Knowledge drawer had a "Deals & Products" tab AND a separate
   "Product Catalog" tab, so the same mental category was split across
   two tabs inside a drawer named after a different concept entirely.

   Everything merchandising now lives behind this one card: the bulk
   catalog import, active deals, featured products, and the AI flyer
   generator.
   ---------------------------------------------------------------- */
const CatalogTeaserCard = ({ summary, onOpen, large = false }) => {
  const items = [
    summary.dealCount > 0
      ? { label: `${summary.dealCount} active ${summary.dealCount === 1 ? "deal" : "deals"}` }
      : { label: "No active deals" },
    summary.productCount > 0
      ? { label: `${summary.productCount} featured product${summary.productCount === 1 ? "" : "s"}` }
      : null,
    summary.untaggedDeals > 0
      ? {
          label: `${summary.untaggedDeals} deal${summary.untaggedDeals === 1 ? "" : "s"} untagged`,
          tone: "warn",
        }
      : null,
  ].filter(Boolean);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative w-full h-full text-left bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden hover:border-amber-300 hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px transition-all group"
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-amber-50/60 via-white to-white" />

      <div className={large ? "relative p-6" : "relative p-5"}>
        <div className={large ? "flex items-center justify-between mb-4" : "flex items-center justify-between mb-3"}>
          <div className="flex items-center gap-2">
            <div
              className={
                large
                  ? "w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"
                  : "contents"
              }
            >
              <Package className={large ? "w-4 h-4 text-amber-600" : "w-3.5 h-3.5 text-amber-600"} />
            </div>
            <h2
              className={
                large
                  ? "text-[13px] font-bold text-slate-900 uppercase tracking-[0.08em]"
                  : "text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]"
              }
            >
              Catalog &amp; Deals
            </h2>
          </div>
          <ArrowRight
            className={
              large
                ? "w-5 h-5 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all"
                : "w-4 h-4 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all"
            }
          />
        </div>

        <p
          className={
            large
              ? "text-[15px] text-slate-700 leading-relaxed mb-4 max-w-xl"
              : "text-sm text-slate-700 leading-relaxed mb-3"
          }
        >
          What your agent sells — your product catalog, active deals, and the
          flyers it texts to callers who ask what's on sale.
        </p>

        <div className="flex flex-wrap gap-1.5">
          {items.map((item, i) => (
            <span
              key={i}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                item.tone === "warn"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
};

/* ----------------------------------------------------------------
   TeaserCard — generic compact card for the desktop top row.
   ----------------------------------------------------------------
   Mirrors KnowledgeTeaserCard's look (gradient wash, hover lift,
   trailing arrow, summary chips) but is configurable for the
   Notifications and Call Forwarding surfaces. Tapping it opens the
   matching SideDrawer.
   ---------------------------------------------------------------- */
const TeaserCard = ({
  icon: Icon,
  iconColor = "text-slate-600",
  accentGradient = "from-slate-50/60",
  hoverBorder = "hover:border-slate-300",
  hoverArrow = "group-hover:text-slate-600",
  title,
  description,
  chips = [],
  onOpen,
  // `compact` = the secondary treatment: tighter padding, smaller body copy.
  // Used so Call Settings reads as clearly subordinate to Agent Knowledge
  // rather than competing with it for attention.
  compact = false,
}) => {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`relative w-full h-full text-left bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px transition-all group ${hoverBorder}`}
    >
      <div
        className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${accentGradient} via-white to-white`}
      />

      <div className={compact ? "relative p-4" : "relative p-5"}>
        <div className={compact ? "flex items-center justify-between mb-2" : "flex items-center justify-between mb-3"}>
          <div className="flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
            <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
              {title}
            </h2>
          </div>
          <ArrowRight
            className={`w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-all ${hoverArrow}`}
          />
        </div>

        <p
          className={
            compact
              ? "text-[12px] text-slate-500 leading-relaxed mb-3"
              : "text-sm text-slate-700 leading-relaxed mb-3"
          }
        >
          {description}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {chips.filter(Boolean).map((label, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
};

/* ----------------------------------------------------------------
   SideDrawer — lightweight right-side drawer shell.
   ----------------------------------------------------------------
   Same chrome as KnowledgeDrawer (backdrop blur, slide-in panel,
   escape-to-close, header with icon/title) but content-agnostic:
   it just renders `children` in a scrollable body. Used for the
   Notifications and Call Forwarding editors, which already manage
   their own save buttons via the `bare` card bodies, so this shell
   doesn't add a footer. If `dirty` is true, closing confirms first.
   ---------------------------------------------------------------- */
const SideDrawer = ({
  open,
  onClose,
  icon: Icon,
  iconBg = "bg-slate-50",
  iconColor = "text-slate-600",
  title,
  subtitle,
  dirty = false,
  children,
}) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dirty]);

  const handleClose = () => {
    if (dirty) {
      if (!window.confirm("You have unsaved changes. Discard them?")) return;
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label={title}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-8 h-8 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
            >
              {Icon && <Icon className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">{title}</h2>
              {subtitle && (
                <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -m-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-all flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-5">
          {children}
        </div>
      </aside>
    </>
  );
};

/* ================================================================
   ================ KILL SWITCH CONFIRM MODAL =====================
   ================================================================
   One-tap confirm before taking the agent line down. Deliberately
   small and fast — Escape or Cancel backs out, the primary button
   is the destructive action. Re-enabling does NOT use this modal
   (it fires immediately), so this only ever guards the OFF path.
   ---------------------------------------------------------------- */

const KillSwitchConfirmModal = ({
  open,
  busy,
  agentNumber,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={() => !busy && onCancel()}
      />
      {/* Card */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="Pause AI phone agent"
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">
                Pause the phone agent?
              </h2>
              <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">
                The agent will immediately stop answering inbound calls
                {agentNumber ? (
                  <>
                    {" "}
                    on{" "}
                    <span className="font-mono font-semibold text-slate-800">
                      {agentNumber}
                    </span>
                  </>
                ) : null}
                . Callers won't reach the AI until you resume it.
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-200/70 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-tip="Stops the agent answering inbound calls until you resume it"
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold text-white bg-rose-600 hover:bg-rose-700 active:bg-rose-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Pausing…
              </>
            ) : (
              <>
                <PowerOff className="w-4 h-4" />
                Pause agent
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   ================ FORWARDING GUIDE MODAL ========================
   ================================================================
   Centered modal with detailed setup instructions for the four
   phone-system categories store owners actually have: landline,
   iPhone, Android, and VoIP/business systems. Read-once-and-close
   reference — different intent from the Knowledge drawer (which is
   for ongoing tuning), so it gets a different container pattern.
   ---------------------------------------------------------------- */

const ForwardingGuideModal = ({
  open,
  onClose,
  agentNumber,
  hasAgentNumber,
  displayNumber,
  copied,
  onCopy,
}) => {
  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  // The number string we inline into instructions. Falls back gracefully
  // if no agent number is assigned yet so the guide still reads correctly.
  const numberForInstructions = hasAgentNumber
    ? displayNumber
    : "your agent number";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="forwarding-guide-title"
    >
      <div
        className="relative bg-white sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------- Sticky header ---------- */}
        <div className="flex-shrink-0 flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-slate-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600">
                <PhoneForwarded className="w-4 h-4" />
              </div>
              <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-[0.1em]">
                Setup guide
              </span>
            </div>
            <h2
              id="forwarding-guide-title"
              className="text-lg font-bold text-slate-900 leading-tight"
            >
              Forward your store line to your AI agent
            </h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              About 3 minutes to set up. Your store keeps its existing number — callers reach the AI when staff can't pick up.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            className="flex-shrink-0 p-1.5 -m-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
            aria-label="Close forwarding guide"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ---------- Scrollable body ---------- */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 sm:py-6 space-y-7">

          {/* Agent number reminder card */}
          <div className="relative rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-100 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-[0.1em] mb-1">
                  You'll forward to
                </div>
                <div className="font-mono text-xl font-bold text-slate-900 tabular-nums tracking-tight">
                  {hasAgentNumber ? displayNumber : "Not yet assigned"}
                </div>
              </div>
              {hasAgentNumber && (
                <button
                  type="button"
                  onClick={onCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${
                    copied
                      ? "bg-emerald-600 text-white"
                      : "bg-white border border-blue-200 text-blue-700 hover:bg-blue-50"
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy number
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ===== Step 1: Pick a strategy ===== */}
          <section>
            <SectionHeader number={1} title="Pick a forwarding strategy" />
            <p className="text-sm text-slate-600 mt-1.5 mb-3 leading-relaxed">
              You decide when calls go to the AI. Most stores pick the middle option.
            </p>
            <div className="space-y-2">
              <ScenarioCard
                title="Forward only when busy or unanswered"
                description="Staff answers when they can. AI catches everything else — including after-hours."
                recommended
              />
              <ScenarioCard
                title="Forward all calls"
                description="AI picks up everything immediately. Good if you want every call greeted instantly and triaged."
              />
              <ScenarioCard
                title="Forward only after business hours"
                description="Requires scheduled forwarding — supported by most VoIP systems, rarely by basic landlines."
              />
            </div>
          </section>

          {/* ===== Step 2: Set it up on your phone ===== */}
          <section>
            <SectionHeader number={2} title="Set up forwarding on your phone" />
            <p className="text-sm text-slate-600 mt-1.5 mb-4 leading-relaxed">
              Find the type of line your store uses below and follow those steps.
            </p>

            <div className="space-y-3">
              {/* Landline */}
              <PhoneTypeCard
                icon={Phone}
                accent="blue"
                title="Landline"
                subtitle="AT&T, Spectrum, Verizon, CenturyLink, Comcast Xfinity, etc."
              >
                <InstructionList>
                  <li>Pick up the receiver and wait for the dial tone.</li>
                  <li>
                    Dial <Code>*72</Code> to forward all calls, or{" "}
                    <Code>*71</Code> to forward only when nobody picks up.
                  </li>
                  <li>
                    After the second dial tone, dial your agent number:{" "}
                    <Code>{numberForInstructions}</Code>
                  </li>
                  <li>Wait for the confirmation tone (usually a short beep or recording).</li>
                  <li>Hang up. Forwarding is now active.</li>
                </InstructionList>
                <Callout tone="neutral">
                  <strong>To turn it off:</strong> pick up the phone and dial{" "}
                  <Code>*73</Code>. If <Code>*72</Code> doesn't work on your line, your carrier may use a different sequence — check their support site or call them.
                </Callout>
              </PhoneTypeCard>

              {/* iPhone */}
              <PhoneTypeCard
                icon={Smartphone}
                accent="purple"
                title="iPhone"
                subtitle="iOS 14 or newer"
              >
                <InstructionList>
                  <li>Open the <strong>Settings</strong> app.</li>
                  <li>Scroll down and tap <strong>Phone</strong>.</li>
                  <li>Tap <strong>Call Forwarding</strong>.</li>
                  <li>Toggle <strong>Call Forwarding</strong> on.</li>
                  <li>
                    Tap <strong>Forward To</strong> and enter your agent number:{" "}
                    <Code>{numberForInstructions}</Code>
                  </li>
                  <li>Tap <strong>Back</strong>. A small phone icon appears in your status bar — that means forwarding is active.</li>
                </InstructionList>
                <Callout tone="neutral">
                  iPhone only supports "forward all calls" through Settings. For conditional forwarding (busy / no answer), use the landline codes above on the carrier or set it up through your carrier's web portal.
                </Callout>
              </PhoneTypeCard>

              {/* Android */}
              <PhoneTypeCard
                icon={Smartphone}
                accent="emerald"
                title="Android"
                subtitle="Samsung, Google Pixel, OnePlus, Motorola, etc."
              >
                <InstructionList>
                  <li>Open the <strong>Phone</strong> app.</li>
                  <li>
                    Tap the menu (three dots <strong>⋮</strong> in the top-right) and choose{" "}
                    <strong>Settings</strong>.
                  </li>
                  <li>
                    Tap <strong>Calling accounts</strong> (or <strong>Calls</strong>) →{" "}
                    <strong>Call forwarding</strong>.
                  </li>
                  <li>
                    Choose <strong>Always forward</strong>, or one of the conditional options like{" "}
                    <strong>When busy</strong> / <strong>When unanswered</strong>.
                  </li>
                  <li>
                    Enter your agent number (<Code>{numberForInstructions}</Code>) and tap{" "}
                    <strong>Turn on</strong>.
                  </li>
                </InstructionList>
                <Callout tone="neutral">
                  Menu names vary slightly between manufacturers. If you don't see "Call forwarding," open your phone's Settings app and search for it directly.
                </Callout>
              </PhoneTypeCard>

              {/* VoIP / Business */}
              <PhoneTypeCard
                icon={Building2}
                accent="amber"
                title="VoIP or business phone system"
                subtitle="Google Voice, RingCentral, Vonage, OOMA, Grasshopper, 8x8, Spectrum Business, etc."
              >
                <InstructionList>
                  <li>Log into your provider's admin portal in a web browser.</li>
                  <li>
                    Look for <strong>Call Forwarding</strong> — sometimes labeled{" "}
                    <em>Find Me / Follow Me</em>, <em>Call Handling</em>, or{" "}
                    <em>Answering Rules</em>.
                  </li>
                  <li>
                    Pick your strategy: always, when busy, after X rings, or on a schedule.
                  </li>
                  <li>
                    Set the destination number to your agent number:{" "}
                    <Code>{numberForInstructions}</Code>
                  </li>
                  <li>Save and apply your changes.</li>
                </InstructionList>
                <Callout tone="info">
                  <strong>VoIP is the most flexible option.</strong> Most providers let you set business-hours schedules so the AI only picks up after close — no need to flip a setting on and off manually each day.
                </Callout>
              </PhoneTypeCard>
            </div>
          </section>

          {/* ===== Step 3: Test it ===== */}
          <section>
            <SectionHeader number={3} title="Confirm it's working" />
            <InstructionList>
              <li>
                Call your store's number from a different phone — your cell, a colleague's phone, anything that's not the line you forwarded.
              </li>
              <li>
                If you set "forward all calls," the AI should pick up within 1–2 rings. If you set conditional forwarding, let it ring 4–5 times.
              </li>
              <li>
                As soon as you hear the AI greet you, you're done — hang up.
              </li>
            </InstructionList>
            <Callout tone="success">
              <strong>It worked?</strong> You're live. Calls that ring through to your AI will appear in <em>Recent Calls</em> on this tab, and your notification recipients will get SMS summaries when each call ends.
            </Callout>
          </section>

          {/* ===== Step 4: Turn it off ===== */}
          <section>
            <SectionHeader number={4} title="When you want to turn forwarding off" />
            <ul className="mt-2 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2.5">
                <span className="flex-shrink-0 text-slate-400 mt-1">•</span>
                <span><strong>Landline:</strong> pick up and dial <Code>*73</Code>.</span>
              </li>
              <li className="flex gap-2.5">
                <span className="flex-shrink-0 text-slate-400 mt-1">•</span>
                <span><strong>iPhone / Android:</strong> open the same Call Forwarding setting and toggle it off.</span>
              </li>
              <li className="flex gap-2.5">
                <span className="flex-shrink-0 text-slate-400 mt-1">•</span>
                <span><strong>VoIP:</strong> disable the forwarding rule in the same admin portal where you created it.</span>
              </li>
            </ul>
          </section>
        </div>

        {/* ---------- Sticky footer ---------- */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 px-5 sm:px-6 py-3.5 border-t border-slate-200 bg-slate-50">
          <span className="text-[12px] text-slate-500">
            Need a hand? Contact support and we'll walk you through it.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

/* ----- Forwarding guide subcomponents ----- */

const SectionHeader = ({ number, title }) => (
  <div className="flex items-center gap-2.5">
    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-900 text-white text-[11px] font-bold tabular-nums">
      {number}
    </div>
    <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
  </div>
);

const ScenarioCard = ({ title, description, recommended }) => (
  <div
    className={`rounded-lg border p-3 ${
      recommended
        ? "border-emerald-200 bg-emerald-50/40"
        : "border-slate-200 bg-white"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[13px] font-semibold text-slate-900">
            {title}
          </span>
          {recommended && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
              <CheckCircle2 className="w-2.5 h-2.5" />
              Recommended
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-slate-600 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  </div>
);

const PhoneTypeCard = ({ icon: Icon, accent, title, subtitle, children }) => {
  const accentClasses = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${
            accentClasses[accent] || accentClasses.blue
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-slate-900 leading-tight">
            {title}
          </div>
          {subtitle && (
            <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
};

const InstructionList = ({ children }) => (
  <ol className="space-y-2 text-[13px] text-slate-700 leading-relaxed">
    {React.Children.map(children, (child, i) => (
      <li className="flex gap-2.5">
        <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[10.5px] font-bold tabular-nums mt-px">
          {i + 1}
        </span>
        <div className="min-w-0 pt-0.5">{child.props.children}</div>
      </li>
    ))}
  </ol>
);

const Code = ({ children }) => (
  <code className="inline-block px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[12px] font-semibold text-slate-800 leading-none align-baseline">
    {children}
  </code>
);

const Callout = ({ tone = "neutral", children }) => {
  const toneClasses = {
    neutral: "bg-slate-50 border-slate-200 text-slate-700",
    info: "bg-blue-50 border-blue-200 text-blue-900",
    success: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-[12.5px] leading-relaxed ${
        toneClasses[tone] || toneClasses.neutral
      }`}
    >
      {children}
    </div>
  );
};

/* ================================================================
   ================ KNOWLEDGE DRAWER ==============================
   ================================================================ */

const KnowledgeDrawer = ({
  open,
  onClose,
  knowledge,
  onSave,
  tier,
  businessType,
}) => {
  // Active tab persists for the same reason as the drawer's open flag —
  // a remount after a parent refetch should drop the user back on the exact
  // tab they were using. Stored value is validated so a stale id from an
  // older build (e.g. the removed "deals" / "catalog" tabs, which now live
  // in CatalogDrawer) can't strand the UI on a tab that no longer exists.
  const [tab, _setTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem("pa_knowledge_tab");
      return ["hours", "qa", "custom"].includes(saved) ? saved : "hours";
    } catch {
      return "hours";
    }
  });
  const setTab = useCallback((t) => {
    _setTab(t);
    try {
      sessionStorage.setItem("pa_knowledge_tab", t);
    } catch {}
  }, []); // hours | qa | custom

  const [draft, setDraft] = useState(knowledge);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reset draft when drawer opens
  useEffect(() => {
    if (open) {
      setDraft(knowledge);
      setSaved(false);
    }
  }, [open, knowledge]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(knowledge),
    [draft, knowledge]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (dirty) {
      if (!window.confirm("You have unsaved changes. Discard them?")) return;
    }
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[600px] lg:w-[760px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Agent Knowledge editor"
        style={{
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">Agent Knowledge</h2>
              <p className="text-[11px] text-slate-500 truncate">
                What your AI tells callers about your business
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 -m-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-all flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs — three now that everything merchandising moved out. A grid
            can't overflow, so no tab is ever clipped or hidden. */}
        <div
          className="flex-shrink-0 px-2 sm:px-3 pt-2 border-b border-slate-200 grid grid-cols-3 gap-0.5"
          role="tablist"
        >
          <DrawerTab
            active={tab === "hours"}
            onClick={() => setTab("hours")}
            icon={Clock}
            label="Hours & Closures"
            mobileLabel="Hours"
          />
          <DrawerTab
            active={tab === "qa"}
            onClick={() => setTab("qa")}
            icon={MessageSquare}
            label="Q&A Library"
            mobileLabel="Q&A"
            count={draft?.qa?.length || 0}
          />
          <DrawerTab
            active={tab === "custom"}
            onClick={() => setTab("custom")}
            icon={FileText}
            label="Custom Instructions"
            mobileLabel="Custom"
          />
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
          {tab === "hours" && (
            <HoursPanel
              hours={draft.hours}
              tier={tier}
              onChange={(hours) => setDraft({ ...draft, hours })}
            />
          )}
          {tab === "qa" && (
            <QAPanel
              qa={draft.qa || []}
              businessType={businessType}
              onChange={(qa) => setDraft({ ...draft, qa })}
            />
          )}
          {tab === "custom" && (
            <CustomInstructionsPanel
              value={draft.customInstructions || ""}
              businessType={businessType}
              onChange={(customInstructions) =>
                setDraft({ ...draft, customInstructions })
              }
            />
          )}
        </div>

        {/* Footer */}
        <div
          className="flex-shrink-0 px-4 sm:px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between gap-3"
          style={{
            paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
          }}
        >
          <span className="text-[11px] text-slate-500 truncate">
            {dirty ? "You have unsaved changes" : "All changes saved"}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-2 sm:py-1.5 rounded-lg text-[13px] sm:text-[12px] font-semibold text-slate-600 hover:bg-slate-100 transition-all min-h-[40px] sm:min-h-0"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className={`px-4 py-2 sm:py-1.5 rounded-lg text-[13px] sm:text-[12px] font-semibold transition-all min-w-[100px] min-h-[40px] sm:min-h-0 flex items-center justify-center gap-1.5 ${
                saved
                  ? "bg-emerald-600 text-white"
                  : !dirty
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
              }`}
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : saved ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Saved
                </>
              ) : (
                "Save changes"
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

/* ================================================================
   ================ CATALOG & DEALS DRAWER ========================
   ================================================================
   The second primary surface: everything about what the store SELLS.

   Merges what used to be two separate tabs inside the Knowledge drawer
   ("Deals & Products" and "Product Catalog"). They were always the same
   mental category — the catalog is the raw product data, the deals are
   the subset the agent leads with, and the flyer builder produces those
   deals — so splitting them across two tabs under a differently-named
   drawer was the core confusion.

   Both tabs write straight to the server (CatalogImport posts its own
   uploads, CurrentDealsPanel posts via /api/designs and the current-deals
   endpoints), so there is no staged draft and no Save button — the footer
   just says so and offers Close.
   ================================================================ */


/* ================================================================
   PRODUCT CATALOG — inlined (was ./CatalogImport). Browse + INLINE
   price/sale/stock editing + upload, one component, zero external
   imports so the build can never resolve a stale file again.
   ================================================================ */
const IMPORTER_URL = "/api/phone-agents-2/catalog-importer";
const ITEMS_URL = "/api/phone-agents-2/catalog-items";
const PAGE = 1000; // supabase per-request row cap

/* ---------------- auth ---------------- */
const authHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/* ---------------- helpers ---------------- */
const storeIdOf = (s) => s?.id ?? s?.store_id ?? null;
const catLabel = (c) => (c || "").replace(/_/g, " ").trim();
const asStr = (v) => (v == null ? "" : String(v));
const numOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const cleanMoney = (raw) =>
  raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
const cleanInt = (raw) => raw.replace(/[^\d]/g, "");

// flexible header mapping for CSV/XLSX uploads (same vocabulary the
// importer has always accepted)
const HEADER_MAP = {
  sku: ["sku", "item_number", "item_no", "item_#", "model", "model_number", "part_number"],
  name: ["name", "product_name", "item_name", "description", "title"],
  brand: ["brand", "manufacturer", "vendor", "mfg"],
  category: ["category", "type", "product_type", "group"],
  price: ["price", "msrp", "retail", "retail_price", "unit_price"],
  sale_price: ["sale_price", "sale", "sale price", "promo_price"],
  stock: ["stock", "stock_qty", "stock qty", "qty", "quantity", "on_hand", "inventory"],
  stock_location: ["stock_location", "stock location", "location", "warehouse"],
  color: ["color", "colour", "finish"],
  image_url: ["image_url", "image url", "image", "photo", "photo_url", "img"],
  notes: ["notes", "note", "details", "materials"],
};
const normHeader = (h) => h.trim().toLowerCase().replace(/\s+/g, "_");
const mapRow = (row) => {
  const keys = Object.keys(row);
  const pick = (field) => {
    const k = keys.find((key) => HEADER_MAP[field].includes(normHeader(key)));
    return k != null ? asStr(row[k]).trim() : "";
  };
  return {
    sku: pick("sku"),
    name: pick("name"),
    brand: pick("brand"),
    category: pick("category"),
    price: numOrNull(cleanMoney(pick("price"))),
    sale_price: numOrNull(cleanMoney(pick("sale_price"))),
    stock: numOrNull(cleanInt(pick("stock"))),
    stock_location: pick("stock_location"),
    color: pick("color"),
    image_url: pick("image_url"),
    notes: pick("notes"),
  };
};

/* ================================================================
   CatalogImport — the whole Product Catalog tab.
   ================================================================ */
const CatalogImport = ({ currentStore, onClose, bare = false, title = "Product Catalog" }) => {
  const storeId = storeIdOf(currentStore);

  // ---------- catalog state ----------
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [togglingId, setTogglingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null); // photo upload
  const photoInputRef = useRef(null);
  const photoTargetRef = useRef(null);

  // ---------- inline edit state (the merged Prices & Stock lane) ----------
  const [dirty, setDirty] = useState({}); // sku -> {price?, stock?} as strings
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ---------- upload state ----------
  const [view, setView] = useState("catalog"); // catalog | upload
  const [file, setFile] = useState(null); // { name, rows }
  const [parseError, setParseError] = useState(null);
  const [fullReplace, setFullReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  /* ---------------- load (paginated past the 1000-row cap) ---------------- */
  const loadCatalog = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setLoadError(null);
    try {
      let all = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("agent_catalog_items")
          .select("*")
          .eq("store_id", storeId)
          .order("category", { ascending: true })
          .order("name", { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) throw error;
        all = all.concat(data || []);
        if (!data || data.length < PAGE) break;
      }
      setItems(all);
    } catch (e) {
      setLoadError(e?.message || "Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  /* ---------------- derived ---------------- */
  const categories = useMemo(() => {
    const s = new Set();
    for (const it of items) if (it.category) s.add(it.category);
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== "all" && it.category !== cat) return false;
      if (!needle) return true;
      return (
        (it.name || "").toLowerCase().includes(needle) ||
        (it.sku || "").toLowerCase().includes(needle) ||
        (it.brand || "").toLowerCase().includes(needle)
      );
    });
  }, [items, q, cat]);

  const withPhotos = useMemo(
    () => items.filter((i) => i.image_url).length,
    [items],
  );

  // what an input should display: pending edit wins, else DB value
  const shownVal = useCallback(
    (it, field) => {
      const d = dirty[it.sku];
      if (d && field in d) return d[field];
      const v = field === "stock" ? (it.stock ?? it.stock_qty) : it[field];
      return asStr(v);
    },
    [dirty],
  );

  const noPriceCount = useMemo(
    () => items.reduce((n, it) => (shownVal(it, "price") === "" ? n + 1 : n), 0),
    [items, shownVal],
  );

  const dirtyCount = Object.keys(dirty).length;

  /* ---------------- inline edits ---------------- */
  const setField = useCallback(
    (it, field, raw) => {
      const clean = field === "stock" ? cleanInt(raw) : cleanMoney(raw);
      setSaved(false);
      setSaveError(null);
      setDirty((prev) => {
        const base = asStr(
          field === "stock" ? (it.stock ?? it.stock_qty) : it[field],
        );
        const row = { ...(prev[it.sku] || {}) };
        if (clean === base) delete row[field];
        else row[field] = clean;
        const next = { ...prev };
        if (Object.keys(row).length === 0) delete next[it.sku];
        else next[it.sku] = row;
        return next;
      });
    },
    [],
  );

  const saveEdits = useCallback(async () => {
    if (dirtyCount === 0 || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = Object.entries(dirty).map(([sku, fields]) => {
        const out = { sku };
        for (const f of ["price", "stock"]) {
          if (f in fields) out[f] = numOrNull(fields[f]);
        }
        return out;
      });
      const res = await fetch(ITEMS_URL, {
        method: "PATCH",
        headers: await authHeaders(),
        body: JSON.stringify({ store_id: storeId, items: payload }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Save failed (${res.status})`);
      // fold edits into local rows — no refetch flash
      setItems((prev) =>
        prev.map((it) => {
          const d = dirty[it.sku];
          if (!d) return it;
          const merged = { ...it };
          if ("price" in d) merged.price = numOrNull(d.price);
          if ("stock" in d) {
            merged.stock = numOrNull(d.stock);
            if ("stock_qty" in merged) merged.stock_qty = merged.stock;
          }
          return merged;
        }),
      );
      setDirty({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [dirty, dirtyCount, saving, storeId]);

  /* ---------------- toggle (instant, unchanged behavior) ---------------- */
  const toggleActive = useCallback(
    async (it) => {
      setTogglingId(it.id);
      const nextVal = !it.is_active;
      setItems((prev) =>
        prev.map((x) => (x.id === it.id ? { ...x, is_active: nextVal } : x)),
      );
      try {
        const { error } = await supabase
          .from("agent_catalog_items")
          .update({ is_active: nextVal })
          .eq("id", it.id);
        if (error) throw error;
      } catch (e) {
        // revert on failure
        setItems((prev) =>
          prev.map((x) => (x.id === it.id ? { ...x, is_active: !nextVal } : x)),
        );
        setLoadError(e?.message || "Toggle failed");
      } finally {
        setTogglingId(null);
      }
    },
    [],
  );

  /* ---------------- per-item photo upload (unchanged behavior) ---------------- */
  const pickPhoto = (it) => {
    photoTargetRef.current = it;
    photoInputRef.current?.click();
  };
  const uploadItemImage = async (e) => {
    const f = e.target.files?.[0];
    const it = photoTargetRef.current;
    e.target.value = "";
    if (!f || !it) return;
    setUploadingId(it.id);
    try {
      const ext = (f.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${storeId}/${it.sku}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("catalog-images")
        .upload(path, f, { upsert: true, contentType: f.type });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("catalog-images").getPublicUrl(path);
      const publicUrl = data?.publicUrl;
      const { error: dbErr } = await supabase
        .from("agent_catalog_items")
        .update({ image_url: publicUrl })
        .eq("id", it.id);
      if (dbErr) throw dbErr;
      setItems((prev) =>
        prev.map((x) => (x.id === it.id ? { ...x, image_url: publicUrl } : x)),
      );
    } catch (err) {
      setLoadError(`Photo upload failed: ${err.message}`);
    } finally {
      setUploadingId(null);
    }
  };

  /* ---------------- file upload lane (full-sync, unchanged behavior) ---------------- */
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setParseError(null);
    setResult(null);
    const done = (rows) => {
      const mapped = rows.map(mapRow).filter((r) => r.sku && r.name);
      if (mapped.length === 0) {
        setParseError(
          "No usable rows — the file needs at least a SKU column and a Name column.",
        );
        setFile(null);
        return;
      }
      setFile({ name: f.name, rows: mapped });
      setView("upload");
    };
    const isCsv = /\.csv$/i.test(f.name);
    if (isCsv) {
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => done(res.data || []),
        error: (err) => setParseError(`Failed to parse CSV: ${err.message}`),
      });
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(ev.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          done(XLSX.utils.sheet_to_json(ws, { defval: "" }));
        } catch (err) {
          setParseError(`Failed to parse spreadsheet: ${err.message}`);
        }
      };
      reader.readAsArrayBuffer(f);
    }
  };

  const runImport = async () => {
    if (!file?.rows?.length || importing) return;
    setImporting(true);
    setParseError(null);
    try {
      const res = await fetch(IMPORTER_URL, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
          store_id: storeId,
          full_replace: fullReplace,
          items: file.rows,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `Import failed (${res.status})`);
      setResult(body);
    } catch (e) {
      setParseError(e.message);
    } finally {
      setImporting(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setParseError(null);
    setFullReplace(false);
  };

  /* ================================================================
     render
     ================================================================ */
  return (
    <div
      className={
        bare
          ? "flex-1 min-h-0 flex flex-col bg-white relative"
          : "bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col relative"
      }
    >
      {/* hidden photo picker */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={uploadItemImage}
      />
      {/* hidden catalog file picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={onFilePicked}
      />

      {/* ---------- header row ---------- */}
      <div className="flex-shrink-0 px-4 sm:px-5 pt-4 pb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-tip="Choose a CSV or spreadsheet to import into the catalog"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-semibold transition-all"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload file
          </button>
          {onClose && (
            <button
              type="button"
              onClick={view === "upload" ? () => { resetUpload(); setView("catalog"); } : onClose}
              className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold text-slate-500 hover:bg-slate-100 transition-all"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {view === "catalog" && (
        <>
          {/* ---------- count line ---------- */}
          <div className="flex-shrink-0 px-4 sm:px-5 pb-3 flex items-center gap-2 flex-wrap text-[13px] text-slate-600">
            <span className="tabular-nums">
              {items.length.toLocaleString()} products ·{" "}
              {withPhotos.toLocaleString()} with photos · {categories.length}{" "}
              categories
            </span>
            {noPriceCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-semibold tabular-nums">
                $ {noPriceCount.toLocaleString()} without a price
              </span>
            )}
            <button
              type="button"
              onClick={loadCatalog}
              title="Refresh"
              className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* ---------- search + category ---------- */}
          <div className="flex-shrink-0 px-4 sm:px-5 pb-3 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, SKU, brand…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-[13px] focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>
            <div className="relative flex-shrink-0">
              <select
                value={cat}
                onChange={(e) => setCat(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-slate-200 text-[13px] bg-white focus:outline-none focus:border-amber-400 transition-all max-w-[180px]"
              >
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {catLabel(c)}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* ---------- errors ---------- */}
          {(loadError || saveError) && (
            <div className="flex-shrink-0 mx-4 sm:mx-5 mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px] flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{loadError || saveError}</span>
            </div>
          )}

          {/* ---------- list ---------- */}
          <div className="flex-1 min-h-0 overflow-y-auto border-t border-slate-100">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-slate-400 gap-2 text-[13px]">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading catalog…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-[13px] text-slate-400">
                {items.length === 0
                  ? "No products yet — upload a file to load your catalog."
                  : "No products match."}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map((it) => (
                  <div
                    key={it.id ?? it.sku}
                    className={`px-4 sm:px-5 py-3 flex items-center gap-3 ${
                      it.is_active === false ? "opacity-50" : ""
                    }`}
                  >
                    {/* thumb — click to add/replace photo */}
                    <button
                      type="button"
                      onClick={() => pickPhoto(it)}
                      title="Add or replace photo"
                      className="relative w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center flex-shrink-0 group"
                    >
                      {uploadingId === it.id ? (
                        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                      ) : it.image_url ? (
                        <img
                          src={it.image_url}
                          alt={it.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageOff className="w-4 h-4 text-slate-300" />
                      )}
                      <span className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Camera className="w-4 h-4 text-white" />
                      </span>
                    </button>

                    {/* name / sku / category / description */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-slate-900 truncate">
                        {it.brand ? `${it.brand} · ` : ""}
                        {it.name}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                        <span className="text-[11px] text-slate-500 tabular-nums flex-shrink-0">
                          {it.sku}
                        </span>
                        {it.category && (
                          <span className="px-1.5 py-0 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold truncate">
                            {it.category}
                          </span>
                        )}
                      </div>
                      {(it.description || it.notes) && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {it.description || it.notes}
                        </p>
                      )}
                    </div>

                    {/* THE MERGE — inline price / stock, right in the row */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={shownVal(it, "price")}
                        onChange={(e) => setField(it, "price", e.target.value)}
                        placeholder="Price"
                        title="Price"
                        className={`w-[70px] px-2 py-1.5 rounded-lg border bg-white text-right text-[12px] font-semibold text-slate-800 tabular-nums placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all ${
                          dirty[it.sku]?.price !== undefined
                            ? "border-amber-400"
                            : "border-slate-200"
                        }`}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={shownVal(it, "stock")}
                        onChange={(e) => setField(it, "stock", e.target.value)}
                        placeholder="Stock"
                        title="Stock"
                        className={`w-[58px] px-2 py-1.5 rounded-lg border bg-white text-right text-[12px] font-semibold text-slate-800 tabular-nums placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all ${
                          dirty[it.sku]?.stock !== undefined
                            ? "border-amber-400"
                            : "border-slate-200"
                        }`}
                      />
                    </div>

                    {/* toggle — instant, untouched behavior */}
                    <button
                      type="button"
                      onClick={() => toggleActive(it)}
                      disabled={togglingId === it.id}
                      role="switch"
                      aria-checked={it.is_active !== false}
                      title={
                        it.is_active !== false
                          ? "Visible to the phone agent — tap to hide"
                          : "Hidden from the phone agent — tap to show"
                      }
                      className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                        it.is_active !== false ? "bg-emerald-500" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${
                          it.is_active !== false ? "left-[18px]" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ---------- footer hint ---------- */}
          <div className="flex-shrink-0 px-4 sm:px-5 py-2.5 border-t border-slate-100 text-[11px] text-slate-400">
            Type in a row to set price or stock — then Save. The toggle
            hides an item from the phone agent instantly. Click a thumbnail to
            add or replace a photo. Uploads update prices and stock; use the
            full-catalog checkbox on upload to retire missing SKUs.
          </div>

          {/* ---------- floating save bar (only when dirty) ---------- */}
          {(dirtyCount > 0 || saved) && (
            <div className="pointer-events-none absolute bottom-14 right-4 sm:right-5 z-10 flex items-center gap-2">
              {dirtyCount > 0 ? (
                <button
                  type="button"
                  onClick={saveEdits}
                  disabled={saving}
                  className="pointer-events-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white text-[12px] font-semibold shadow-lg shadow-indigo-600/25 transition-all"
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  {saving
                    ? "Saving…"
                    : `Save changes${dirtyCount > 1 ? ` (${dirtyCount})` : ""}`}
                </button>
              ) : (
                <span className="pointer-events-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold shadow-sm">
                  <Check className="w-3 h-3" /> Saved
                </span>
              )}
            </div>
          )}
        </>
      )}

      {/* ================= upload lane ================= */}
      {view === "upload" && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4 flex flex-col gap-3">
          {!result && (
            <>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50">
                <FileSpreadsheet className="w-5 h-5 text-slate-500 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">
                    {file?.name}
                  </div>
                  <div className="text-[11px] text-slate-500 tabular-nums">
                    {file?.rows?.length?.toLocaleString()} rows ready
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-2 px-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={fullReplace}
                  onChange={(e) => setFullReplace(e.target.checked)}
                  className="mt-0.5 accent-amber-500"
                />
                <span className="text-[12px] text-slate-600">
                  <span className="font-semibold text-slate-800">
                    This file is my full catalog
                  </span>{" "}
                  — deactivate any SKU not in this file. Leave unchecked to
                  add/update only.
                </span>
              </label>

              {parseError && (
                <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px] flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {parseError}
                </div>
              )}

              <div className="flex justify-end gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => {
                    resetUpload();
                    setView("catalog");
                  }}
                  className="px-3 py-2 rounded-lg text-[12px] font-semibold text-slate-600 hover:bg-slate-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runImport}
                  data-tip="Adds or updates these SKUs in the catalog the phone agent uses"
                  disabled={importing || !file?.rows?.length}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-[12px] font-semibold transition-all"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Importing…
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      Import {file?.rows?.length?.toLocaleString()} products
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="flex flex-col gap-3">
              <div className="text-sm">
                <span className="text-emerald-600 font-semibold">
                  ✓ {result.upserted ?? 0} products live
                </span>
                <span className="text-slate-500 ml-3 tabular-nums">
                  {result.with_images ?? 0} with photos ·{" "}
                  {result.skipped_count ?? result.skipped?.length ?? 0} skipped
                  {result.deactivated ? ` · ${result.deactivated} deactivated` : ""}
                </span>
              </div>
              {result.skipped?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="px-3 py-1.5 text-xs text-slate-500">
                      Row {s.row} — {s.reason}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    resetUpload();
                    setView("catalog");
                    loadCatalog();
                  }}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold"
                >
                  View catalog →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CatalogDrawer = ({
  open,
  onClose,
  currentStore,
  currentDeals = [],
  onUploadCurrentDeal,
  onCreateCurrentDeal,
  onDeleteCurrentDeal,
  onUpdateCurrentDeal,
  onEnrichCurrentDeals,
  onOpenFlyerBuilder,
}) => {
  const [tab, _setTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem("pa_catalog_tab");
      // "items" (the old Prices & Stock tab) merged into "catalog" —
      // migrate any stale saved value so nobody strands on a dead tab.
      if (saved === "items") return "catalog";
      return ["deals", "catalog"].includes(saved) ? saved : "deals";
    } catch {
      return "deals";
    }
  });
  const setTab = useCallback((t) => {
    _setTab(t);
    try {
      sessionStorage.setItem("pa_catalog_tab", t);
    } catch {}
  }, []); // deals | catalog

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer — wider than Knowledge because this surface is image-heavy
          (deal cards, product photos, the catalog table). */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[640px] lg:w-[880px] xl:w-[980px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Catalog and deals"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900">
                Catalog &amp; Deals
              </h2>
              <p className="text-[11px] text-slate-500 truncate">
                What your agent sells, quotes, and texts to callers
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -m-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-all flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div
          className="flex-shrink-0 px-2 sm:px-3 pt-2 border-b border-slate-200 grid grid-cols-2 gap-0.5"
          role="tablist"
        >
          <DrawerTab
            active={tab === "deals"}
            onClick={() => setTab("deals")}
            icon={Tag}
            label="Deals & Flyers"
            mobileLabel="Deals"
            count={currentDeals?.length || 0}
            accent="amber"
          />
          <DrawerTab
            active={tab === "catalog"}
            onClick={() => setTab("catalog")}
            icon={Upload}
            label="Product Catalog"
            mobileLabel="Catalog"
            accent="amber"
          />
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
          {tab === "deals" && (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Demo catalog loader — REMOVE AFTER VEGAS.
                  Uses the same onUploadImage → onCreate → onUpdate chain as
                  the manual uploader, so it can't drift from the real path. */}
              <div className="flex-shrink-0 px-4 sm:px-5 pt-4">
                <DemoCatalogSeeder
                  onUploadImage={onUploadCurrentDeal}
                  onCreate={onCreateCurrentDeal}
                  onUpdate={onUpdateCurrentDeal}
                  existingCount={currentDeals?.length || 0}
                />
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <CurrentDealsPanel
                  deals={currentDeals}
                  onUploadImage={onUploadCurrentDeal}
                  onCreate={onCreateCurrentDeal}
                  onDelete={onDeleteCurrentDeal}
                  onUpdate={onUpdateCurrentDeal}
                  onEnrich={onEnrichCurrentDeals}
                  onOpenFlyerBuilder={onOpenFlyerBuilder}
                />
              </div>
            </div>
          )}
          {tab === "catalog" && (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Catalog rows carry inline price / stock editing
                  (the old Prices & Stock tab, merged in). Edits PATCH the
                  catalog-items delta lane; uploads stay the full-sync lane. */}
              {/* The shared manufacturer catalog for the brands this store
                  carries (read-only), with the upload lane below it as
                  "Your own items". */}
              <StoreCatalogPanel
                currentStore={currentStore}
                ownItems={<CatalogImport bare title="Your own items" currentStore={currentStore} onClose={onClose} />}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex-shrink-0 px-4 sm:px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between gap-3"
          style={{
            paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
          }}
        >
          <span className="text-[11px] text-slate-500 truncate">
            {tab === "deals"
              ? "Deals save instantly"
              : "Catalog is read-only · your own items: imports save instantly, price & stock edits save on demand"}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 sm:py-1.5 rounded-lg text-[13px] sm:text-[12px] font-semibold text-slate-600 hover:bg-slate-100 transition-all min-h-[40px] sm:min-h-0 flex-shrink-0"
          >
            Close
          </button>
        </div>
      </aside>
    </>
  );
};

const DrawerTab = ({ active, onClick, icon: Icon, label, mobileLabel, count, accent = "indigo" }) => {
  // Literal class strings only — Tailwind can't see interpolated names.
  const activeTab =
    accent === "amber"
      ? "text-amber-700 border-amber-500 bg-amber-50/40"
      : "text-indigo-700 border-indigo-600 bg-indigo-50/40";
  const activeBadge =
    accent === "amber"
      ? "bg-amber-100 text-amber-700"
      : "bg-indigo-100 text-indigo-700";

  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      title={label}
      className={`w-full min-w-0 flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 px-1 sm:px-2 py-2 rounded-t-md text-[10px] sm:text-[12px] font-semibold border-b-2 transition-all min-h-[46px] sm:min-h-[38px] ${
        active
          ? activeTab
          : "text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-50"
      }`}
    >
      <Icon className="w-4 h-4 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
      {/* Short label on mobile (avoids wrap), full label on sm+ */}
      {mobileLabel ? (
        <>
          <span className="sm:hidden leading-tight text-center">{mobileLabel}</span>
          <span className="hidden sm:inline truncate">{label}</span>
        </>
      ) : (
        <span className="truncate">{label}</span>
      )}
      {count != null && count > 0 && (
        <span
          className={`hidden sm:inline-block px-1.5 py-0 rounded-full text-[10px] font-bold tabular-nums flex-shrink-0 ${
            active ? activeBadge : "bg-slate-200 text-slate-600"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
};

/* ================================================================
   ================ SMART DEALS UI (paste-in block) ===============
   ================================================================
   Paste this ENTIRE block into PhoneAgentTab.jsx right above the
   "HOURS PANEL" section banner (i.e. immediately after the DrawerTab
   component). Requires the import patches in UI-PATCHES.md (adds
   Wand2 / Volume2 / Package / LayoutGrid to the lucide import and
   the dealCategories util import).

   What lives here:
     CATEGORY_STYLES / CategoryChip / CategorySelect
       — the shared visual language for deal categories
     ttsLintSpoken / SpokenHints
       — live "written for a voice" linting on spoken lines
     CategoryFilterChips / CatalogSlide / CatalogCarousel
       — the Products & Deals showcase: filter chips, image-forward
         slides with inline editing, native scroll-snap slider
     ProductsPanel / ProductEntryCard
       — the new "Products" drawer tab editing knowledge.products
   ================================================================ */

// Category ids that make sense as PRODUCT coverage (storewide/other are
// deal-only concepts — you don't stock "storewide").
const COVERAGE_CATEGORIES = DEAL_CATEGORIES.filter(
  (c) => c.id !== "storewide" && c.id !== "other"
);

// Literal Tailwind classes per category (must stay literal for the purge).
const CATEGORY_STYLES = {
  mattresses:             { chip: "bg-sky-50 text-sky-700 border-sky-200",             dot: "bg-sky-500" },
  bedroom:                { chip: "bg-violet-50 text-violet-700 border-violet-200",    dot: "bg-violet-500" },
  sofas_sectionals:       { chip: "bg-amber-50 text-amber-700 border-amber-200",       dot: "bg-amber-500" },
  living_room:            { chip: "bg-orange-50 text-orange-700 border-orange-200",    dot: "bg-orange-500" },
  recliners_chairs:       { chip: "bg-rose-50 text-rose-700 border-rose-200",          dot: "bg-rose-500" },
  dining:                 { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  office:                 { chip: "bg-cyan-50 text-cyan-700 border-cyan-200",          dot: "bg-cyan-500" },
  outdoor:                { chip: "bg-lime-50 text-lime-700 border-lime-200",          dot: "bg-lime-600" },
  kids:                   { chip: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", dot: "bg-fuchsia-500" },
  rugs_decor:             { chip: "bg-teal-50 text-teal-700 border-teal-200",          dot: "bg-teal-500" },
  appliances_electronics: { chip: "bg-slate-100 text-slate-700 border-slate-300",      dot: "bg-slate-500" },
  storewide:              { chip: "bg-indigo-50 text-indigo-700 border-indigo-200",    dot: "bg-indigo-500" },
  other:                  { chip: "bg-slate-50 text-slate-600 border-slate-200",       dot: "bg-slate-400" },
};

const UNTAGGED_STYLE = {
  chip: "bg-amber-50 text-amber-700 border-amber-300 border-dashed",
  dot: "bg-amber-400",
};

const categoryStyle = (id) => CATEGORY_STYLES[id] || CATEGORY_STYLES.other;

const CategoryChip = ({ categoryId }) => {
  const tagged = Boolean(categoryId);
  const style = tagged ? categoryStyle(categoryId) : UNTAGGED_STYLE;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${style.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {tagged ? dealCategoryLabel(categoryId) : "Untagged"}
    </span>
  );
};

/* A select disguised as a chip — the operator override for a deal's
   category. Empty value = untagged (backend clears the column). */
const CategorySelect = ({ value, onChange, disabled }) => {
  const style = value ? categoryStyle(value) : UNTAGGED_STYLE;
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`appearance-none cursor-pointer rounded-full border px-2 py-0.5 pr-5 text-[10px] font-bold uppercase tracking-wider outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed bg-no-repeat ${style.chip}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='6' viewBox='0 0 8 6'><path d='M1 1l3 3 3-3' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>\")",
        backgroundPosition: "right 6px center",
      }}
      title="Category — what caller questions this deal matches"
      aria-label="Deal category"
    >
      <option value="">Untagged</option>
      {DEAL_CATEGORIES.map((c) => (
        <option key={c.id} value={c.id}>
          {c.label}
        </option>
      ))}
    </select>
  );
};

/* ---------------- TTS hygiene linting ----------------
   The spoken line is read verbatim by the voice. Catch the classic
   traps (symbols, "$399") as the operator types. */
const ttsLintSpoken = (text) => {
  const t = text || "";
  const warnings = [];
  if (/\$\s*\d/.test(t)) {
    warnings.push("Spell prices out — the voice reads “$399” literally. Write “three ninety-nine.”");
  }
  if (/\d+\s*%/.test(t)) {
    warnings.push("Write percentages as words — “thirty percent off,” not “30%.”");
  }
  if (/[#&*_~^|<>]/.test(t)) {
    warnings.push("Drop symbols — the voice speaks them out loud.");
  }
  if (t.length > 160) {
    warnings.push("Keep it under ~160 characters — one natural spoken sentence.");
  }
  return warnings;
};

const SpokenHints = ({ text }) => {
  const warnings = ttsLintSpoken(text);
  if (!warnings.length) return null;
  return (
    <ul className="mt-1.5 space-y-1">
      {warnings.map((w, i) => (
        <li
          key={i}
          className="flex items-start gap-1.5 text-[11px] text-amber-700"
        >
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{w}</span>
        </li>
      ))}
    </ul>
  );
};

/* ---------------- The playbook, mirrored client-side ----------------
   Same resolution order the live agent follows:
     deals in the asked category → storewide deals → product info →
     honest "nothing on file" fallback. */
const dealSpokenLine = (deal) =>
  (deal?.deal_spoken || "").trim() ||
  (deal?.description || "").trim() ||
  (deal?.format_label || "Current promotion");

/* ---------------- Category filter chips ----------------
   Replaces the coverage grid: plain chips built from what's actually in the
   catalog, with counts. Tap to filter the showcase, tap again to clear. */
const CategoryFilterChips = ({ items = [], value = "", onChange }) => {
  const { cats, untagged } = useMemo(() => {
    const m = new Map();
    let un = 0;
    items.forEach((d) => {
      const c = (d.deal_category || "").trim();
      if (!c) {
        un += 1;
        return;
      }
      m.set(c, (m.get(c) || 0) + 1);
    });
    const known = DEAL_CATEGORIES.filter((c) => m.has(c.id)).map((c) => ({
      id: c.id,
      label: c.label,
      count: m.get(c.id),
    }));
    m.forEach((count, id) => {
      if (!DEAL_CATEGORIES.some((c) => c.id === id)) {
        known.push({ id, label: id.replace(/_/g, " "), count });
      }
    });
    return { cats: known, untagged: un };
  }, [items]);

  const dealsCount = useMemo(
    () => items.filter((d) => (d.item_kind || "deal") !== "product").length,
    [items]
  );

  const buckets = cats.length + (untagged ? 1 : 0);
  const showDealsChip = dealsCount > 0 && dealsCount < items.length;
  if (buckets < 2 && !showDealsChip) return null;

  const chip = (active, extra) =>
    `px-2.5 py-1.5 sm:py-1 rounded-full border text-[11px] font-semibold transition-all min-h-[32px] sm:min-h-0 ${
      active ? "ring-1 ring-indigo-300 " : ""
    }${extra}`;

  return (
    <div className="flex flex-wrap gap-1.5 mb-3">
      <button
        type="button"
        onClick={() => onChange("")}
        className={chip(
          value === "",
          value === ""
            ? "bg-slate-900 text-white border-slate-900"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
        )}
      >
        All · {items.length}
      </button>
      {showDealsChip && (
        <button
          type="button"
          onClick={() => onChange(value === "__deals" ? "" : "__deals")}
          className={chip(
            value === "__deals",
            value === "__deals"
              ? "bg-amber-500 text-white border-amber-500"
              : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
          )}
        >
          On sale · {dealsCount}
        </button>
      )}
      {cats.map((c) => {
        const st = categoryStyle(c.id);
        const active = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(active ? "" : c.id)}
            className={chip(active, st.chip)}
          >
            {c.label} · {c.count}
          </button>
        );
      })}
      {untagged > 0 && (
        <button
          type="button"
          onClick={() => onChange(value === "__untagged" ? "" : "__untagged")}
          className={chip(
            value === "__untagged",
            `${UNTAGGED_STYLE.chip}`
          )}
        >
          Untagged · {untagged}
        </button>
      )}
    </div>
  );
};

/* ---------------- Catalog slide ----------------
   One big showcase card: image-forward, kind badge + price on the photo,
   category + voice line + inline editing in the body. */
const CatalogSlide = ({ item, onUpdate, onRemove, removing }) => {
  const isProduct = (item.item_kind || "deal") === "product";
  const [savingField, setSavingField] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [spokenDraft, setSpokenDraft] = useState(item.deal_spoken || "");
  const [priceDraft, setPriceDraft] = useState(item.price_text || "");
  const [optionsDraft, setOptionsDraft] = useState(item.options_text || "");
  const [flash, setFlash] = useState(false);

  useEffect(() => { setSpokenDraft(item.deal_spoken || ""); }, [item.deal_spoken]);
  useEffect(() => { setPriceDraft(item.price_text || ""); }, [item.price_text]);
  useEffect(() => { setOptionsDraft(item.options_text || ""); }, [item.options_text]);

  const patch = async (field, payload) => {
    if (!onUpdate) return;
    setSavingField(field);
    try {
      await onUpdate(item.id, payload);
      setFlash(true);
      setTimeout(() => setFlash(false), 900);
    } catch (err) {
      console.error("Catalog update failed:", err);
      window.alert(err?.message || "Could not update. Try again.");
    } finally {
      setSavingField("");
    }
  };

  const blurSave = (field, draft, currentVal, key) => {
    const next = (draft || "").trim();
    if (next === (currentVal || "").trim()) return;
    patch(field, { [key]: next });
  };

  const spokenShown = dealSpokenLine(item);

  return (
    <div className="snap-center shrink-0 w-[84%] xs:w-[80%] sm:w-[400px]">
      <div
        className={`h-full bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col ${
          flash ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"
        }`}
      >
        {/* Image */}
        <div className="relative h-44 sm:h-52 bg-slate-100 flex-shrink-0">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt={item.format_label || (isProduct ? "Product" : "Deal")}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <ImageIcon className="w-8 h-8 text-slate-300" />
            </div>
          )}

          {!isProduct && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white shadow">
              <Tag className="w-2.5 h-2.5" />
              Current deal
            </span>
          )}

          <button
            type="button"
            onClick={() => onRemove(item.id)}
            disabled={removing}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 backdrop-blur text-slate-500 hover:text-rose-600 shadow-sm transition-colors disabled:opacity-50"
            aria-label="Remove from catalog"
            title="Remove from the agent's catalog"
          >
            {removing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>

          {(((item.price_text || "").trim() || (item.options_text || "").trim())) && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent px-3 pt-8 pb-2">
              {(item.price_text || "").trim() && (
                <p className="text-white font-bold text-lg leading-tight drop-shadow">
                  {item.price_text}
                </p>
              )}
              {(item.options_text || "").trim() && (
                <p className="text-white/85 text-[11px] leading-tight">
                  {item.options_text}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3.5 flex-1 flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center flex-wrap gap-1.5">
            {onUpdate ? (
              <CategorySelect
                value={item.deal_category || ""}
                onChange={(v) => patch("category", { deal_category: v })}
                disabled={savingField === "category"}
              />
            ) : (
              <CategoryChip categoryId={item.deal_category || ""} />
            )}
            {savingField === "category" && (
              <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
            )}
            {item.format_label && (
              <span className="text-[12px] font-bold text-slate-800 truncate">
                {item.format_label}
              </span>
            )}
          </div>

          <p className="text-[12px] text-slate-600 leading-snug line-clamp-2">
            {item.description || (
              <span className="text-slate-400 italic">No description</span>
            )}
          </p>

          {!editOpen ? (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="mt-auto w-full text-left group pt-1"
              title="What the agent says — tap to edit"
            >
              <span className="flex items-start gap-1.5">
                <Volume2 className="w-3 h-3 text-indigo-400 group-hover:text-indigo-600 mt-0.5 flex-shrink-0 transition-colors" />
                <span className="text-[11px] italic text-slate-500 group-hover:text-slate-700 leading-snug line-clamp-2 transition-colors">
                  “{spokenShown}”
                </span>
                <Pencil className="w-2.5 h-2.5 text-slate-300 group-hover:text-indigo-500 mt-0.5 flex-shrink-0 transition-colors" />
              </span>
            </button>
          ) : (
            <div className="mt-1 rounded-lg border border-indigo-200 bg-indigo-50/40 p-2.5 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider flex items-center gap-1">
                  <Volume2 className="w-3 h-3" />
                  What the agent says
                </label>
                <div className="flex items-center gap-1.5">
                  {savingField ? (
                    <Loader2 className="w-3 h-3 animate-spin text-indigo-500" />
                  ) : (
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      {spokenDraft.length}/180
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditOpen(false)}
                    className="p-0.5 rounded text-slate-400 hover:text-slate-600"
                    aria-label="Collapse"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <textarea
                value={spokenDraft}
                onChange={(e) => setSpokenDraft(e.target.value)}
                onBlur={() => blurSave("spoken", spokenDraft, item.deal_spoken, "deal_spoken")}
                rows={2}
                maxLength={180}
                disabled={!onUpdate || savingField === "spoken"}
                placeholder="One spoken sentence: the item, the price, any deadline."
                className="w-full px-2.5 py-2 rounded-md border border-indigo-200 bg-white text-[16px] sm:text-[12px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none resize-none"
              />
              <SpokenHints text={spokenDraft} />
              <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/50 px-2.5 py-2">
                <span className="text-[11px] font-semibold text-amber-800 flex items-center gap-1.5">
                  <Tag className="w-3 h-3" />
                  Special deal right now
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-label="Toggle special deal"
                  aria-checked={!isProduct}
                  onClick={() =>
                    patch("kind", {
                      kind: isProduct ? "deal" : "product",
                      item_kind: isProduct ? "deal" : "product",
                    })
                  }
                  disabled={!onUpdate || savingField === "kind"}
                  className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors disabled:opacity-50 ${
                    !isProduct ? "bg-amber-500" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      !isProduct ? "translate-x-4" : ""
                    }`}
                  />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-bold text-indigo-800 uppercase tracking-wider mb-0.5">
                      Price
                    </label>
                    <input
                      type="text"
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                      onBlur={() => blurSave("price", priceDraft, item.price_text, "price")}
                      maxLength={120}
                      disabled={!onUpdate || savingField === "price"}
                      placeholder="e.g. $399 or from $399 to $1,299"
                      className="w-full px-2.5 py-1.5 rounded-md border border-indigo-200 bg-white text-[16px] sm:text-[12px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-indigo-800 uppercase tracking-wider mb-0.5">
                      Sizes / options
                    </label>
                    <input
                      type="text"
                      value={optionsDraft}
                      onChange={(e) => setOptionsDraft(e.target.value)}
                      onBlur={() => blurSave("options", optionsDraft, item.options_text, "options")}
                      maxLength={200}
                      disabled={!onUpdate || savingField === "options"}
                      placeholder="e.g. twin, queen, king"
                      className="w-full px-2.5 py-1.5 rounded-md border border-indigo-200 bg-white text-[16px] sm:text-[12px] focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 outline-none"
                    />
                  </div>
                </div>
              <p className="text-[10px] text-slate-500">
                Read out loud, word for word. Edits save when you click away.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Catalog carousel ----------------
   Native scroll-snap slider — no library. Arrows on desktop, swipe on
   mobile, dots that track the centered slide. */
const CatalogCarousel = ({ items = [], onUpdate, onRemove, removingId }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((ch, i) => {
      const c = ch.offsetLeft + ch.offsetWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    setActive(0);
    const el = trackRef.current;
    if (el) el.scrollTo({ left: 0 });
  }, [items.length]);

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    const ch = el?.children?.[i];
    if (!el || !ch) return;
    el.scrollTo({
      left: ch.offsetLeft - (el.clientWidth - ch.offsetWidth) / 2,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex gap-3.5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((item) => (
          <CatalogSlide
            key={item.id}
            item={item}
            onUpdate={onUpdate}
            onRemove={onRemove}
            removing={removingId === item.id}
          />
        ))}
      </div>

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollToIndex(Math.max(0, active - 1))}
            disabled={active === 0}
            className="hidden sm:flex absolute -left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-indigo-600 hover:border-indigo-300 transition-all disabled:opacity-0"
            aria-label="Previous"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
          </button>
          <button
            type="button"
            onClick={() => scrollToIndex(Math.min(items.length - 1, active + 1))}
            disabled={active === items.length - 1}
            className="hidden sm:flex absolute -right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-indigo-600 hover:border-indigo-300 transition-all disabled:opacity-0"
            aria-label="Next"
          >
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="flex items-center justify-center gap-1.5 mt-1">
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                onClick={() => scrollToIndex(i)}
                className={`rounded-full transition-all ${
                  i === active
                    ? "w-4 h-1.5 bg-indigo-500"
                    : "w-1.5 h-1.5 bg-slate-300 hover:bg-slate-400"
                }`}
                aria-label={`Go to item ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ================================================================
   ================ HOURS PANEL ===================================
   ================================================================ */

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const HoursPanel = ({ hours, tier, onChange }) => {
  const readOnly = tier === "integrated";

  const updateDay = (dayKey, patch) => {
    onChange({
      ...hours,
      weekly: {
        ...hours.weekly,
        [dayKey]: { ...hours.weekly[dayKey], ...patch },
      },
    });
  };

  const addClosure = () => {
    const today = new Date().toISOString().split("T")[0];
    onChange({
      ...hours,
      closures: [
        ...(hours.closures || []),
        { id: `c_${Date.now()}`, date: today, reason: "" },
      ],
    });
  };

  const updateClosure = (id, patch) => {
    onChange({
      ...hours,
      closures: hours.closures.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const removeClosure = (id) => {
    onChange({
      ...hours,
      closures: hours.closures.filter((c) => c.id !== id),
    });
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 sm:space-y-5 min-h-0">
      {readOnly && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-[12px] text-blue-900">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Your hours are pulled from your store settings.{" "}
            <a href="#" className="font-semibold underline hover:no-underline">
              Edit in Settings →
            </a>
          </div>
        </div>
      )}

      {/* Weekly hours */}
      <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
            Weekly Hours
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Standing hours your agent uses to answer "Are you open?"
          </p>
        </header>

        <div className="divide-y divide-slate-100">
          {DAYS.map(({ key, label }) => {
            const day = hours.weekly?.[key] || { open: false, ranges: [] };
            return (
              <DayRow
                key={key}
                label={label}
                day={day}
                disabled={readOnly}
                onChange={(patch) => updateDay(key, patch)}
              />
            );
          })}
        </div>
      </section>

      {/* Closures */}
      <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <header className="px-4 py-3 border-b border-slate-200 flex items-start sm:items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
              One-off Closures
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Holidays, vacation days, or anything that overrides your weekly hours.
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={addClosure}
              data-tip="Adds a date the store will be closed"
              className="flex items-center gap-1 px-2.5 py-2 sm:py-1 rounded-md text-[12px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-all flex-shrink-0 min-h-[36px] sm:min-h-0"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add date</span>
            </button>
          )}
        </header>

        <div className="p-4">
          {(hours.closures || []).length === 0 ? (
            <div className="flex flex-col items-center py-6 text-center">
              <CalendarDays className="w-8 h-8 text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No closures scheduled</p>
              {!readOnly && (
                <button
                  type="button"
                  onClick={addClosure}
                  className="mt-2 text-[12px] font-semibold text-indigo-600 hover:underline"
                >
                  Add your first closure
                </button>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {hours.closures.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 sm:p-2"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0 ml-1" />
                    <input
                      type="date"
                      disabled={readOnly}
                      value={c.date}
                      onChange={(e) => updateClosure(c.id, { date: e.target.value })}
                      className="px-2 py-1.5 sm:py-1 rounded-md bg-white border border-slate-200 text-[16px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 min-w-0 flex-1 sm:flex-initial"
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeClosure(c.id)}
                        className="sm:hidden p-2 -m-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
                        aria-label="Remove closure"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    disabled={readOnly}
                    placeholder="Reason (e.g., Memorial Day)"
                    value={c.reason || ""}
                    onChange={(e) => updateClosure(c.id, { reason: e.target.value })}
                    className="flex-1 px-2 py-1.5 sm:py-1 rounded-md bg-white border border-slate-200 text-[16px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 min-w-0"
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removeClosure(c.id)}
                      className="hidden sm:block p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
                      aria-label="Remove closure"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
};

const DayRow = ({ label, day, disabled, onChange }) => {
  const range = day.ranges?.[0] || { start: "09:00", end: "17:00" };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3 sm:py-2.5 hover:bg-slate-50/50 transition-colors">
      <label className="flex items-center gap-2 sm:w-32 flex-shrink-0 cursor-pointer min-h-[28px]">
        <input
          type="checkbox"
          checked={day.open}
          disabled={disabled}
          onChange={(e) => {
            const open = e.target.checked;
            onChange({
              open,
              ranges: open
                ? day.ranges?.length > 0
                  ? day.ranges
                  : [{ start: "09:00", end: "17:00" }]
                : [],
            });
          }}
          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
        />
        <span className="text-sm font-medium text-slate-900">{label}</span>
      </label>

      {day.open ? (
        <div className="flex items-center gap-2 text-[16px] sm:text-[13px] pl-6 sm:pl-0 min-w-0 flex-1">
          <input
            type="time"
            disabled={disabled}
            value={range.start}
            onChange={(e) =>
              onChange({
                ranges: [{ ...range, start: e.target.value }],
              })
            }
            className="px-2 py-1.5 sm:py-1 rounded-md bg-white border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 min-w-0 flex-shrink"
          />
          <span className="text-slate-400 flex-shrink-0">—</span>
          <input
            type="time"
            disabled={disabled}
            value={range.end}
            onChange={(e) =>
              onChange({
                ranges: [{ ...range, end: e.target.value }],
              })
            }
            className="px-2 py-1.5 sm:py-1 rounded-md bg-white border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 min-w-0 flex-shrink"
          />
        </div>
      ) : (
        <span className="text-sm text-slate-400 italic pl-6 sm:pl-0">Closed</span>
      )}
    </div>
  );
};

/* ================================================================
   ================ Q&A PANEL =====================================
   ================================================================ */

const QAPanel = ({ qa, businessType, onChange }) => {
  const [search, setSearch] = useState("");

  const templates = useMemo(() => getQATemplates(businessType), [businessType]);
  const existingQuestions = useMemo(
    () =>
      new Set(
        qa.flatMap((q) => (q.questions || []).map((s) => s.toLowerCase().trim()))
      ),
    [qa]
  );

  const availableTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          !t.questions.some((q) => existingQuestions.has(q.toLowerCase().trim()))
      ),
    [templates, existingQuestions]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return qa;
    const q = search.toLowerCase();
    return qa.filter(
      (entry) =>
        (entry.questions || []).some((qq) => qq.toLowerCase().includes(q)) ||
        (entry.answer || "").toLowerCase().includes(q)
    );
  }, [qa, search]);

  const addBlank = () => {
    onChange([
      ...qa,
      { id: `qa_${Date.now()}`, questions: [""], answer: "" },
    ]);
  };

  const addFromTemplate = (template) => {
    onChange([
      ...qa,
      {
        id: `qa_${Date.now()}`,
        questions: [...template.questions],
        answer: template.answer,
      },
    ]);
  };

  const addAllTemplates = () => {
    const additions = availableTemplates.map((t, i) => ({
      id: `qa_${Date.now()}_${i}`,
      questions: [...t.questions],
      answer: t.answer,
    }));
    onChange([...qa, ...additions]);
  };

  const updateEntry = (id, patch) => {
    onChange(qa.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id) => {
    onChange(qa.filter((e) => e.id !== id));
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 min-h-0">
      {/* Templates banner */}
      {availableTemplates.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white text-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-slate-900">
                {qa.length === 0
                  ? "Start with common questions"
                  : "Add more common questions"}
              </h3>
              <p className="text-[12px] text-slate-600 mt-0.5 mb-3">
                We've prepared {availableTemplates.length} starter Q&A
                {availableTemplates.length === 1 ? "" : "s"} for{" "}
                {businessTypeLabel(businessType)}. Add them all, or pick the ones
                that fit.
              </p>

              <div className="flex flex-wrap gap-1.5 mb-3">
                {availableTemplates.slice(0, 5).map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    data-tip="Adds this suggested question with a starter answer"
                    onClick={() => addFromTemplate(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 sm:py-1 rounded-full bg-white border border-indigo-200 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 transition-all max-w-full"
                  >
                    <Plus className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{truncate(t.questions[0], 50)}</span>
                  </button>
                ))}
                {availableTemplates.length > 5 && (
                  <span className="inline-flex items-center px-2 text-[11px] text-slate-500">
                    +{availableTemplates.length - 5} more
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={addAllTemplates}
                data-tip="Adds every suggested question with starter answers"
                className="text-[12px] font-bold text-indigo-700 hover:underline"
              >
                Add all {availableTemplates.length} →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Add */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <input
            type="text"
            placeholder="Search Q&As…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-3 pr-3 py-2.5 sm:py-2 rounded-lg bg-white border border-slate-200 text-[16px] sm:text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          type="button"
          onClick={addBlank}
          className="flex items-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg text-[13px] sm:text-[12px] font-semibold bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-all whitespace-nowrap flex-shrink-0 min-h-[40px] sm:min-h-0"
        >
          <Plus className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">New Q&A</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* List */}
      {qa.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700 mb-1">
            No Q&A entries yet
          </p>
          <p className="text-[12px] text-slate-500 mb-4">
            Add Q&A pairs so your agent can answer common questions accurately.
          </p>
          <button
            type="button"
            onClick={addBlank}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add your first Q&A
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-500">
          No matches for "{search}"
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((entry) => (
            <QAEntryCard
              key={entry.id}
              entry={entry}
              onUpdate={(patch) => updateEntry(entry.id, patch)}
              onRemove={() => removeEntry(entry.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const QAEntryCard = ({ entry, onUpdate, onRemove }) => {
  const updateQuestion = (idx, value) => {
    const next = [...(entry.questions || [])];
    next[idx] = value;
    onUpdate({ questions: next });
  };

  const addAlternateQuestion = () => {
    onUpdate({ questions: [...(entry.questions || []), ""] });
  };

  const removeQuestion = (idx) => {
    const next = [...(entry.questions || [])];
    next.splice(idx, 1);
    onUpdate({ questions: next.length > 0 ? next : [""] });
  };

  return (
    <li className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em] pt-1">
          When customer asks
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 -m-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
          aria-label="Remove Q&A"
        >
          <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      </div>

      <div className="space-y-1.5 mb-3">
        {(entry.questions || []).map((q, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <input
              type="text"
              value={q}
              onChange={(e) => updateQuestion(idx, e.target.value)}
              placeholder={
                idx === 0 ? "What documents do I need?" : "Alternate phrasing…"
              }
              className="flex-1 min-w-0 px-3 py-2 sm:py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-[16px] sm:text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white"
            />
            {(entry.questions || []).length > 1 && (
              <button
                type="button"
                onClick={() => removeQuestion(idx)}
                className="p-1.5 -m-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all flex-shrink-0"
                aria-label="Remove phrasing"
              >
                <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addAlternateQuestion}
          className="text-[12px] sm:text-[11px] font-semibold text-slate-500 hover:text-indigo-600 transition-colors py-1"
        >
          + Add another way they might ask
        </button>
      </div>

      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.08em] block mb-1.5">
        Agent answers
      </span>
      <textarea
        value={entry.answer || ""}
        onChange={(e) => onUpdate({ answer: e.target.value })}
        placeholder="Bring photo ID and proof of insurance. For title transfers, also bring the signed title…"
        rows={3}
        className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[16px] sm:text-[13px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white resize-y"
      />
    </li>
  );
};

/* ================================================================
   ================ CURRENT DEALS PANEL ===========================
   ================================================================
   Shows the store's active deals (marketing_designs.is_current_deal = true)
   with two ways to add a new one:

     1) "Add a product" — direct upload, no AI generation. Image goes to
        Supabase 'flyers' bucket via onUploadImage(file) → publicUrl, then
        onCreate({ thumbnail_url, description, title }) inserts the row.

     2) "Open Flyer Builder" — opens the built-in AI Flyer Builder
        overlay (PhoneAgentTab owns it; Marketing Studio was retired
        July 2026 and this is the only place flyers are built now).

   Existing deals can be removed (soft-untag → drops out of agent prompt
   but the design row stays in marketing_designs). Each deal shows the thumbnail,
   description, and a small "ACTIVE" badge.
   ================================================================ */

const CurrentDealsPanel = ({
  deals = [],
  onUploadImage,
  onCreate,
  onDelete,
  onUpdate,
  onEnrich,
  onOpenFlyerBuilder,
}) => {
  const [enriching, setEnriching] = useState(false);
  const untaggedCount = useMemo(
    () => deals.filter((d) => !(d.deal_category || "").trim()).length,
    [deals]
  );
  const handleEnrich = async () => {
    if (!onEnrich || enriching) return;
    setEnriching(true);
    try {
      await onEnrich();
    } catch (err) {
      console.error("Auto-tag failed:", err);
      window.alert(err?.message || "Auto-tagging failed. Try again.");
    } finally {
      setEnriching(false);
    }
  };
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [isDeal, setIsDeal] = useState(false);
  const [price, setPrice] = useState("");
  const [options, setOptions] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const visibleItems = useMemo(() => {
    if (!catFilter) return deals;
    if (catFilter === "__untagged")
      return deals.filter((d) => !(d.deal_category || "").trim());
    if (catFilter === "__deals")
      return deals.filter((d) => (d.item_kind || "deal") !== "product");
    return deals.filter((d) => (d.deal_category || "") === catFilter);
  }, [deals, catFilter]);
  const fileInputRef = useRef(null);
  const [stagedFile, setStagedFile] = useState(null);   // File object selected but not yet uploaded
  const [stagedPreview, setStagedPreview] = useState("");
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);

  // Reset preview URL when stagedFile changes (and revoke the old object URL
  // to avoid leaks — createObjectURL holds a reference to the blob).
  useEffect(() => {
    if (!stagedFile) {
      setStagedPreview("");
      return;
    }
    const url = URL.createObjectURL(stagedFile);
    setStagedPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [stagedFile]);

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("Image is too large (max 10 MB).");
      return;
    }
    setError("");
    setStagedFile(f);
  };

  const handleClearStaged = () => {
    setStagedFile(null);
    setDescription("");
    setTitle("");
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!stagedFile || !onUploadImage) {
      setError("No image selected.");
      return;
    }
    if (!description.trim()) {
      setError("Add a short description so the agent knows what this deal is.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const publicUrl = await onUploadImage(stagedFile);
      if (!publicUrl) throw new Error("Image upload failed (no URL returned).");
      await onCreate({
        thumbnail_url: publicUrl,
        description: description.trim(),
        title: title.trim() || undefined,
        kind: isDeal ? "deal" : "product",
        price: price.trim() || undefined,
        options: options.trim() || undefined,
      });
      handleClearStaged();
      setIsDeal(false);
      setPrice("");
      setOptions("");
      setUploaderOpen(false);
    } catch (err) {
      console.error("Create deal failed:", err);
      setError(err?.message || "Could not save deal. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (id) => {
    if (!id) return;
    if (!window.confirm("Remove this deal from your phone agent? The design is kept and can be re-added later.")) {
      return;
    }
    setRemovingId(id);
    try {
      await onDelete(id, false);
    } catch (err) {
      console.error("Delete deal failed:", err);
      window.alert(err?.message || "Could not remove deal. Try again.");
    } finally {
      setRemovingId(null);
    }
  };

  const canCreate = Boolean(stagedFile) && Boolean(description.trim()) && !busy;

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 min-h-0">
      {/* Explainer */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-white text-amber-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Tag className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-slate-900">
              Active deals your agent leads with
            </h3>
            <p className="text-[12px] text-slate-600 mt-0.5">
              Your agent sells now: it describes matching deals and featured
              products out loud — prices included — then texts the exact
              picture on a yes. Upload a product photo or flyer below — or
              generate one with the{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold whitespace-nowrap">
                <Tag className="w-2.5 h-2.5" />
                AI Flyer Builder
              </span>{" "}
              and it'll auto-publish here.
            </p>
            {onOpenFlyerBuilder && (
              <button
                type="button"
                onClick={onOpenFlyerBuilder}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg bg-white border border-amber-300 text-[12px] font-semibold text-amber-800 hover:bg-amber-100 transition-all min-h-[36px] sm:min-h-0"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Open Flyer Builder
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>




      {/* LIVE CATALOG — the showcase */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2 px-0.5">
          <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            Live catalog
            {deals.length > 0 && (
              <span className="text-slate-400 font-semibold">
                ({deals.length})
              </span>
            )}
          </h4>
          {onEnrich && untaggedCount > 0 && (
            <button
              type="button"
              onClick={handleEnrich}
              disabled={enriching}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 sm:py-1 rounded-lg text-[12px] sm:text-[11px] font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 transition-all disabled:opacity-60 min-h-[32px] sm:min-h-0"
              title="AI-tag untagged items with a category and spoken line"
            >
              {enriching ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Wand2 className="w-3 h-3" />
              )}
              {enriching ? "Tagging…" : `Auto-tag ${untaggedCount} untagged`}
            </button>
          )}
        </div>

        <CategoryFilterChips
          items={deals}
          value={catFilter}
          onChange={setCatFilter}
        />

        {deals.length === 0 ? (
          <div className="bg-gradient-to-br from-indigo-50 via-white to-violet-50 border-2 border-dashed border-indigo-200 rounded-2xl p-10 text-center">
            <Sparkles className="w-8 h-8 text-indigo-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-800">
              Your agent's catalog is empty
            </p>
            <p className="text-[12px] text-slate-500 mt-1">
              Add your first product below — flip the deal switch when it's
              on promotion. The agent starts selling it on the very next call.
            </p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl p-6 text-center">
            <p className="text-[12px] text-slate-500">
              Nothing here — tap the chip again to clear the filter.
            </p>
          </div>
        ) : (
          <CatalogCarousel
            items={visibleItems}
            onUpdate={onUpdate}
            onRemove={handleRemove}
            removingId={removingId}
          />
        )}
      </div>

      {/* Add to catalog — collapsed by default to keep the tab compact */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <button
          type="button"
          onClick={() => setUploaderOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 min-h-[44px]"
        >
          <span className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
            <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Plus className="w-4 h-4" />
            </span>
            Add a product
          </span>
          {uploaderOpen ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </button>
        {uploaderOpen && (
          <div className="px-4 pb-4">
            <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {!stagedFile ? (
          <button
            type="button"
            onClick={handlePickFile}
            className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-amber-400 hover:bg-amber-50/30 hover:text-amber-700 transition-all"
          >
            <ImageIcon className="w-8 h-8" />
            <span className="text-[12px] font-semibold">Click to choose an image</span>
            <span className="text-[10px] text-slate-400">JPG, PNG, or HEIC · up to 10 MB</span>
          </button>
        ) : (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src={stagedPreview}
                alt="Deal preview"
                className="w-full max-h-72 object-contain"
              />
              <button
                type="button"
                onClick={handleClearStaged}
                disabled={busy}
                className="absolute top-2 right-2 p-1 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow-sm disabled:opacity-50"
                aria-label="Clear image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                Title <span className="text-slate-400 font-normal normal-case">(optional)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Memorial Day Sectional Sale"
                disabled={busy}
                maxLength={120}
                className="w-full px-3 py-2.5 sm:py-2 rounded-lg border border-slate-200 text-[16px] sm:text-[13px] focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                What's the deal? <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. 30% off all sectionals through Friday. Free delivery within 20 miles."
                rows={3}
                disabled={busy}
                maxLength={500}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[16px] sm:text-[13px] focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none resize-none"
              />
              <p className="mt-1 text-[10px] text-slate-400">
                The agent reads this aloud and uses it to match callers' questions.
              </p>
            </div>

            

            <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Price
                    </label>
                    <input
                      type="text"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="e.g. $399 or from $399 to $1,299"
                      disabled={busy}
                      maxLength={120}
                      className="w-full px-3 py-2.5 sm:py-2 rounded-lg border border-slate-200 text-[16px] sm:text-[13px] focus:border-violet-400 focus:ring-1 focus:ring-violet-200 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      Sizes / options{" "}
                      <span className="text-slate-400 font-normal normal-case">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={options}
                      onChange={(e) => setOptions(e.target.value)}
                      placeholder="e.g. twin, queen, king"
                      disabled={busy}
                      maxLength={200}
                      className="w-full px-3 py-2.5 sm:py-2 rounded-lg border border-slate-200 text-[16px] sm:text-[13px] focus:border-violet-400 focus:ring-1 focus:ring-violet-200 outline-none"
                    />
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-slate-400">
                  The agent quotes the price out loud in words and mentions the
                  options when it helps the caller choose.
                </p>
            </div>

            <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-[12px] font-bold text-slate-800 flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-amber-600" />
                  Special deal right now
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Flags this item as on promotion — your agent leads with it
                  when callers ask about sales. Off means a regular product at
                  its everyday price.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Toggle special deal"
                aria-checked={isDeal}
                onClick={() => setIsDeal((v) => !v)}
                disabled={busy}
                className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors mt-0.5 ${
                  isDeal ? "bg-amber-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    isDeal ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>

            {error && (
              <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleClearStaged}
                disabled={busy}
                className="px-3 py-2 sm:py-1.5 rounded-lg text-[13px] sm:text-[12px] font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50 min-h-[40px] sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                data-tip="Uploads the image and adds this item for the agent to mention"
                disabled={!canCreate}
                className="px-4 py-2 sm:py-1.5 rounded-lg text-[13px] sm:text-[12px] font-semibold bg-amber-600 hover:bg-amber-700 text-white shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1.5 min-h-[40px] sm:min-h-0"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Publish to agent
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================================================
   ================ CUSTOM INSTRUCTIONS PANEL =====================
   ================================================================ */

const CustomInstructionsPanel = ({ value, businessType, onChange }) => {
  const examples = getInstructionExamples(businessType);
  const [showExamples, setShowExamples] = useState(false); // collapsed by default — gives textarea max space

  const charCount = value.length;
  const wordCount = value.split(/\s+/).filter(Boolean).length;

  return (
    <div className="flex-1 flex flex-col min-h-0 px-4 sm:px-5 pt-4 pb-3 gap-2">
      {/* Compact header — single line, no card */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3">
        <p className="text-[12px] text-slate-500 leading-snug">
          Tone, edge cases, escalation rules, or anything else that doesn't fit
          a Q&A pair.
        </p>
      </div>

      {/* Textarea — fills everything that remains */}
      <div className="relative flex-1 flex min-h-[200px]">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g., Always greet callers warmly. Don't quote exact prices over the phone — offer to have a team member call back with details. If someone asks about commercial fleets, take their name and number and tell them Mike will call them tomorrow."
          className="flex-1 w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-[16px] sm:text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none shadow-sm"
        />
        {/* Floating counter — desktop only. On mobile it sits below to avoid overlapping text. */}
        <div className="hidden sm:flex absolute bottom-3 right-3 items-center gap-2 px-2 py-1 rounded-md bg-white/90 backdrop-blur-sm border border-slate-200 text-[10px] text-slate-400 tabular-nums pointer-events-none">
          <span>{charCount.toLocaleString()} chars</span>
          <span className="w-px h-2.5 bg-slate-200" />
          <span>{wordCount.toLocaleString()} words</span>
        </div>
      </div>

      {/* Mobile counter — below textarea, doesn't fight with text */}
      <div className="sm:hidden flex-shrink-0 flex items-center justify-end gap-2 text-[10px] text-slate-400 tabular-nums px-1">
        <span>{charCount.toLocaleString()} chars</span>
        <span className="w-px h-2.5 bg-slate-200" />
        <span>{wordCount.toLocaleString()} words</span>
      </div>

      {/* Examples — collapsed by default */}
      {examples.length > 0 && (
        <div className="bg-slate-100 rounded-lg flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowExamples((s) => !s)}
            className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-slate-200/60 rounded-lg transition-colors"
          >
            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.08em]">
              {showExamples ? "Hide" : "Show"} examples for{" "}
              {businessTypeLabel(businessType)}
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-slate-500 transition-transform ${
                showExamples ? "rotate-180" : ""
              }`}
            />
          </button>
          {showExamples && (
            <ul className="px-3 pb-3 space-y-1.5">
              {examples.map((ex, i) => (
                <li
                  key={i}
                  className="text-[12px] text-slate-600 leading-relaxed pl-3 border-l-2 border-slate-300 italic"
                >
                  "{ex}"
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   ================ r3 SHELL — Today / Calls / Setup ===============
   One job per view. Today: is the agent on, and what do I owe someone.
   Calls: the log. Setup: the checklist that replaced three drawers'
   worth of entry points, a popover, a pill bar and a red header button.
   ================================================================ */

function callerLabel(call) {
  return (
    call?.customer_name?.trim() ||
    (call?.caller_name || "").trim() ||
    formatPhone(call?.caller_phone) ||
    "Unknown caller"
  );
}

const VIEW_TABS = [
  { id: "today", label: "Today", tip: "What needs you today, plus the latest calls" },
  { id: "calls", label: "Calls", tip: "Every call the agent has taken, with transcripts" },
  { id: "setup", label: "Setup", tip: "Phone line, hours, what the agent knows, voice, transfers" },
];

const ViewSwitch = ({ view, onChange, todoCount, setupAttention, full = false }) => (
  <nav
    aria-label="Phone agent views"
    className={`flex items-center gap-0.5 p-[3px] bg-slate-100 rounded-[10px] ${full ? "w-full" : ""}`}
  >
    {VIEW_TABS.map((t) => {
      const active = view === t.id;
      const badge =
        t.id === "today" && todoCount > 0
          ? String(todoCount)
          : t.id === "setup" && setupAttention
            ? "!"
            : "";
      return (
        <button
          key={t.id}
          type="button"
          aria-current={active ? "page" : undefined}
          data-tip={t.tip}
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 px-3.5 rounded-lg text-[13px] font-semibold transition-colors ${
            full ? "flex-1 justify-center py-2.5" : "py-1.5"
          } ${
            active
              ? "bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.08)]"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {t.label}
          {badge && (
            <span
              className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center ${
                badge === "!" ? "bg-amber-500 text-white" : "bg-blue-600 text-white"
              }`}
            >
              {badge}
            </span>
          )}
        </button>
      );
    })}
  </nav>
);

const AgentStatusPill = ({ hasNumber, enabled, displayNumber }) => {
  const tone = !hasNumber
    ? { dot: "bg-slate-400", wrap: "bg-slate-100 border-slate-200", text: "text-slate-700", label: "No number yet", tip: "The agent has no phone number assigned yet — contact support" }
    : enabled
      ? { dot: "bg-emerald-500", wrap: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", label: "Answering calls", tip: "The agent is live on this number" }
      : { dot: "bg-rose-500", wrap: "bg-rose-50 border-rose-200", text: "text-rose-800", label: "Paused", tip: "The agent is not answering calls right now" };
  return (
    <div
      data-tip={tone.tip}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${tone.wrap}`}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tone.dot}`} />
      <span className={`text-[13px] font-semibold ${tone.text}`}>{tone.label}</span>
      {hasNumber && (
        <span className="text-[13px] text-slate-500 tabular-nums">· {displayNumber}</span>
      )}
    </div>
  );
};

const HeaderMenu = ({ open, onToggle, onClose, hasNumber, displayNumber, copied, onCopy, onGuide, killSwitch }) => (
  <div className="relative">
    <button
      type="button"
      aria-label="More actions"
      aria-haspopup="menu"
      aria-expanded={open}
      data-tip="Copy the agent number, forwarding guide, pause the agent"
      onClick={onToggle}
      className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${
        open
          ? "bg-slate-100 border-slate-300 text-slate-800"
          : "bg-white border-slate-200 text-slate-500 hover:text-slate-800 hover:bg-slate-50"
      }`}
    >
      <MoreHorizontal className="w-[18px] h-[18px]" />
    </button>
    {open && (
      <>
        <div className="fixed inset-0 z-30" onClick={onClose} aria-hidden="true" />
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22)] p-1.5 z-40"
        >
          {hasNumber && (
            <button
              type="button"
              role="menuitem"
              onClick={onCopy}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-slate-700 hover:bg-slate-50"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
              <span className="flex-1 text-left">{copied ? "Copied" : "Copy agent number"}</span>
              <span className="font-mono text-[11px] text-slate-400 tabular-nums">{displayNumber}</span>
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onGuide();
              onClose();
            }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-slate-700 hover:bg-slate-50"
          >
            <HelpCircle className="w-4 h-4 text-slate-400" />
            <span className="flex-1 text-left">Forwarding setup guide</span>
          </button>
          <div className="my-1.5 border-t border-slate-100" />
          <div className="px-1 pb-0.5">{killSwitch}</div>
        </div>
      </>
    )}
  </div>
);

const TODO_TAG = {
  callback: { label: "CALL BACK", cls: "bg-orange-50 text-orange-800 border-orange-100" },
  delivery: { label: "DELIVERY", cls: "bg-indigo-50 text-indigo-800 border-indigo-100" },
  visit: { label: "VISIT", cls: "bg-emerald-50 text-emerald-800 border-emerald-100" },
  review: { label: "REVIEW", cls: "bg-rose-50 text-rose-800 border-rose-100" },
  lead: { label: "LEAD", cls: "bg-sky-50 text-sky-800 border-sky-100" },
};

const ActionBtn = ({ action, primary = false, onJump }) => {
  if (!action) return null;
  const cls = primary
    ? "px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold"
    : "px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-[13px] font-semibold";
  if (action.href) {
    return (
      <a href={action.href} onClick={(e) => e.stopPropagation()} className={cls}>
        {action.label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (action.jump) onJump?.();
        else action.run?.();
      }}
      className={cls}
    >
      {action.label}
    </button>
  );
};

const TodoRow = ({ item, onJumpToCall }) => {
  const tag = TODO_TAG[item.kind];
  const jump = () => onJumpToCall?.(item.call.id);
  return (
    <li className="flex flex-col gap-2 lg:grid lg:grid-cols-[92px_minmax(0,1fr)_auto] lg:items-center lg:gap-4 px-4 lg:px-5 py-3.5 border-t border-slate-100 hover:bg-slate-50/70 transition-colors">
      <span className={`justify-self-start px-2.5 py-1 rounded-md border text-[10.5px] font-bold tracking-[0.05em] ${tag.cls}`}>
        {tag.label}
      </span>
      <button type="button" onClick={jump} data-tip="Opens this call" className="min-w-0 text-left">
        <span className="flex items-baseline gap-2 min-w-0">
          <span className="text-[15px] font-semibold text-slate-900 truncate">{item.who}</span>
          <span className="text-[12px] text-slate-400 whitespace-nowrap">{item.meta}</span>
        </span>
        <span className="block text-[13px] text-slate-600 truncate">{item.sub}</span>
      </button>
      <span className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <ActionBtn action={item.primary} primary onJump={jump} />
        <ActionBtn action={item.secondary} onJump={jump} />
        <ActionBtn action={item.done} onJump={jump} />
      </span>
    </li>
  );
};

const TodoList = ({ items, older, olderOpen, onToggleOlder, callsToday, onJumpToCall }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
    <div className="px-5 pt-4 pb-3 flex items-baseline gap-2.5">
      <h2 className="text-[16px] font-bold text-slate-900">To do</h2>
      <span className="text-[13px] text-slate-500">
        {items.length === 0
          ? "Nothing waiting on you"
          : "Follow-ups from calls · the buttons only update this list, nothing is sent to the customer"}
      </span>
    </div>
    {items.length === 0 ? (
      <div className="px-5 pb-5 flex items-center gap-3">
        <span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <p className="text-[13px] text-slate-600 m-0">
          All caught up
          {callsToday > 0 ? ` — ${callsToday} ${callsToday === 1 ? "call" : "calls"} handled by your agent today.` : "."}
        </p>
      </div>
    ) : (
      <ul className="m-0 p-0 list-none">
        {items.map((it) => (
          <TodoRow key={it.key} item={it} onJumpToCall={onJumpToCall} />
        ))}
      </ul>
    )}
    {older.length > 0 && (
      <div className="border-t border-slate-100">
        <button
          type="button"
          onClick={onToggleOlder}
          data-tip="Items nobody acted on in two weeks"
          className="w-full px-5 py-2.5 flex items-center justify-between text-[13px] text-slate-500 hover:bg-slate-50"
        >
          <span>
            {older.length} older {older.length === 1 ? "item" : "items"} tucked away
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${olderOpen ? "rotate-180" : ""}`} />
        </button>
        {olderOpen && (
          <ul className="m-0 p-0 list-none opacity-80">
            {older.map((it) => (
              <TodoRow key={it.key} item={it} onJumpToCall={onJumpToCall} />
            ))}
          </ul>
        )}
      </div>
    )}
  </section>
);

const CompactCallRow = ({ call, onOpen }) => {
  const who = callerLabel(call);
  const unknown = !call.customer_name?.trim() && !(call.caller_name || "").trim();
  const initial = unknown ? "?" : who.charAt(0).toUpperCase();
  const summary = (call.summary || "").trim() || "No summary for this call";
  const intel = normalizeSalesIntel(call);
  const topic = intel?.interests?.length
    ? interestLabel(intel.interests[0])
    : Array.isArray(call.intent_tags) ? call.intent_tags[0] : null;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(call.id)}
        data-tip="Opens this call in the log"
        className="w-full grid grid-cols-[36px_minmax(0,1fr)_auto] lg:grid-cols-[36px_160px_minmax(0,1fr)_150px] 2xl:grid-cols-[36px_180px_140px_minmax(0,1fr)_150px] items-center gap-3 lg:gap-4 px-4 lg:px-5 py-3 border-t border-slate-100 hover:bg-slate-50/70 text-left transition-colors"
      >
        <span className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold ${unknown ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>
          {initial}
        </span>
        <span className="min-w-0">
          <span className={`block text-[14px] font-semibold truncate ${unknown ? "text-slate-500" : "text-slate-900"}`}>{who}</span>
          {(call.escalated || call.after_hours) && (
            <span className={`block text-[11px] font-medium ${call.escalated ? "text-rose-600" : "text-slate-400"}`}>
              {call.escalated ? "Needed a person" : "After hours"}
            </span>
          )}
          {/* Phone: the summary sits under the name; desktop has its own column. */}
          <span className="block lg:hidden text-[12.5px] text-slate-500 truncate">{summary}</span>
        </span>
        <span className="hidden 2xl:block min-w-0">
          {topic && (
            <span className="inline-block max-w-full truncate px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11.5px] font-medium">
              {topic}
            </span>
          )}
        </span>
        <span className="hidden lg:block text-[13px] text-slate-600 truncate">{summary}</span>
        <span className="text-[12px] text-slate-400 text-right tabular-nums whitespace-nowrap">
          <span className="lg:hidden">{formatDateTime(call.created_at)}</span>
          <span className="hidden lg:inline">{formatDateTime(call.created_at)} · {formatDuration(call.duration_sec)}</span>
        </span>
      </button>
    </li>
  );
};

const RecentCallsPreview = ({ calls, loading, onOpen, onSeeAll }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
    <div className="px-5 pt-4 pb-3 flex items-baseline justify-between">
      <h2 className="text-[16px] font-bold text-slate-900">Recent calls</h2>
      <button type="button" onClick={onSeeAll} data-tip="Switches to the full call log" className="text-[13px] font-semibold text-blue-600 hover:text-blue-700">
        All calls →
      </button>
    </div>
    {loading && calls.length === 0 ? (
      <p className="px-5 pb-5 text-[13px] text-slate-400 m-0">Loading calls…</p>
    ) : calls.length === 0 ? (
      <p className="px-5 pb-5 text-[13px] text-slate-500 m-0">No calls yet. When a customer calls, the summary shows up here.</p>
    ) : (
      <ul className="m-0 p-0 list-none">
        {calls.slice(0, 6).map((c) => (
          <CompactCallRow key={c.id} call={c} onOpen={onOpen} />
        ))}
      </ul>
    )}
  </section>
);

/* ---- Today: stat strip + right rail (r3.1) ----
   Stripe-home pattern: a few numbers, each with a small 7-day trend, color
   only where it means something. The rail holds what used to be tucked in
   the ⋯ menu (is it live, what number) plus what callers asked about. */

const CARD = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]";

const Sparkline = ({ values, className = "text-blue-500" }) => {
  const w = 96, h = 32, pad = 3;
  const max = Math.max(1, ...values);
  const step = (w - pad * 2) / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => [pad + i * step, h - pad - (v / max) * (h - pad * 2)]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className={`flex-shrink-0 ${className}`} aria-hidden="true">
      <polygon points={`${pad},${h - pad} ${line} ${lx},${h - pad}`} fill="currentColor" opacity="0.08" />
      <polyline points={line} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.5" fill="currentColor" />
    </svg>
  );
};

// Phone: label / number / sub stack above the sparkline so the text column
// keeps the tile's full width. Desktop: text left, sparkline right.
const StatTile = ({ label, value, sub, tip, children }) => (
  <div className={`${CARD} px-4 lg:px-5 py-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between min-w-0`} data-tip={tip}>
    <div className="min-w-0 w-full lg:w-auto">
      <div className="text-[12.5px] font-medium text-slate-500 leading-snug">{label}</div>
      <div className="mt-1 text-[28px] leading-none font-bold text-slate-900 tracking-[-0.02em] tabular-nums">{value}</div>
      <div className="mt-2 text-[12px] text-slate-500 truncate">{sub}</div>
    </div>
    {children}
  </div>
);

function formatTalkTime(sec) {
  const m = Math.round((sec || 0) / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const TODO_BAR = { callback: "bg-orange-400", delivery: "bg-indigo-400", visit: "bg-emerald-400", review: "bg-rose-400", lead: "bg-sky-400" };
const TODO_NOUN = {
  callback: ["callback", "callbacks"], delivery: ["delivery", "deliveries"], visit: ["visit", "visits"],
  review: ["review", "reviews"], lead: ["lead", "leads"],
};

const StatStrip = ({ week, callsToday, afterHoursToday, todo }) => {
  const byKind = todo.reduce((m, it) => ((m[it.kind] = (m[it.kind] || 0) + 1), m), {});
  const kinds = Object.keys(TODO_BAR).filter((k) => byKind[k]);
  const todoSub = kinds.length
    ? kinds.map((k) => `${byKind[k]} ${TODO_NOUN[k][byKind[k] === 1 ? 0 : 1]}`).join(" · ")
    : "Nothing waiting on you";
  return (
    <div className="grid grid-cols-2 2xl:grid-cols-4 gap-3 lg:gap-5">
      <StatTile
        label="Calls today"
        value={callsToday}
        sub={`${week.calls} in the last 7 days`}
        tip="Calls the agent answered since midnight; the line shows the last 7 days"
      >
        <Sparkline values={week.days.map((d) => d.calls)} />
      </StatTile>
      <StatTile
        label="After-hours calls caught"
        value={week.afterHours}
        sub={afterHoursToday > 0 ? `${afterHoursToday} today · would've gone to voicemail` : "Last 7 days · would've gone to voicemail"}
        tip="Calls answered while the store was closed"
      >
        <Sparkline values={week.days.map((d) => d.afterHours)} className="text-violet-500" />
      </StatTile>
      <StatTile
        label="Waiting on you"
        value={todo.length}
        sub={todoSub}
        tip="Open items in the To do list"
      >
        {todo.length > 0 && (
          <div className="flex h-2 w-24 rounded-full overflow-hidden bg-slate-100 lg:mb-2 flex-shrink-0">
            {kinds.map((k) => (
              <span key={k} className={TODO_BAR[k]} style={{ width: `${(byKind[k] / todo.length) * 100}%` }} />
            ))}
          </div>
        )}
      </StatTile>
      <StatTile
        label="Phone time handled"
        value={formatTalkTime(week.talkSec)}
        sub="Last 7 days · time your staff stayed on the floor"
        tip="Total length of calls the agent took this week"
      >
        <Sparkline values={week.days.map((d) => d.talkSec)} className="text-emerald-500" />
      </StatTile>
    </div>
  );
};

const AgentCard = ({ hasNumber, enabled, displayNumber, lastCallAt, copied, onCopy, onGuide, onSetup, setupDone }) => {
  const live = hasNumber && enabled;
  const state = !hasNumber ? "No number yet" : enabled ? "Answering calls" : "Paused";
  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className={`px-5 pt-4 pb-4 ${live ? "bg-gradient-to-b from-emerald-50/80 to-white" : !hasNumber ? "bg-slate-50" : "bg-rose-50/70"}`}>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {live && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${live ? "bg-emerald-500" : !hasNumber ? "bg-slate-400" : "bg-rose-500"}`} />
          </span>
          <span className={`text-[13px] font-semibold ${live ? "text-emerald-800" : !hasNumber ? "text-slate-700" : "text-rose-800"}`}>{state}</span>
        </div>
        <div className="mt-2.5 text-[22px] font-bold text-slate-900 tabular-nums tracking-[-0.01em]">{displayNumber}</div>
        <div className="mt-0.5 text-[12.5px] text-slate-500">
          {lastCallAt ? `Last call ${formatRelativeTime(lastCallAt)}` : "No calls yet"}
        </div>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex gap-2">
        {hasNumber && (
          <button type="button" onClick={onCopy} data-tip="Copies the agent's number"
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
            {copied ? "Copied" : "Copy"}
          </button>
        )}
        <button type="button" onClick={onGuide} data-tip="Step-by-step forwarding for landline, iPhone, Android and VoIP"
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700">
          <PhoneForwarded className="w-3.5 h-3.5 text-slate-400" />
          Forwarding
        </button>
      </div>
      <button type="button" onClick={onSetup} data-tip="Opens the Setup checklist"
        className="w-full px-5 py-3 border-t border-slate-100 flex items-center gap-3 hover:bg-slate-50 text-left">
        <span className="text-[12.5px] font-medium text-slate-600 whitespace-nowrap">Setup {setupDone} of 6</span>
        <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <span className={`block h-full ${setupDone === 6 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${(setupDone / 6) * 100}%` }} />
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
      </button>
    </section>
  );
};

const TopicsCard = ({ topics, total }) => (
  <section className={`${CARD} px-5 py-4`}>
    <div className="flex items-baseline justify-between">
      <h2 className="text-[14px] font-bold text-slate-900 m-0">Callers asked about</h2>
      <span className="text-[12px] text-slate-400">Last 7 days</span>
    </div>
    {topics.length === 0 ? (
      <p className="mt-3 text-[13px] text-slate-500 m-0">
        {total > 0 ? "No topics tagged on this week's calls yet." : "Topics show up here once calls come in."}
      </p>
    ) : (
      <ul className="mt-3 m-0 p-0 list-none space-y-2.5">
        {topics.map(([label, n]) => (
          <li key={label}>
            <div className="flex items-baseline justify-between gap-3 text-[13px]">
              <span className="text-slate-700 truncate capitalize">{label}</span>
              <span className="text-slate-500 tabular-nums">{n}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <span className="block h-full rounded-full bg-blue-500/70" style={{ width: `${(n / topics[0][1]) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

/* ---- Live call banner (r3.1) ----
   One per call in progress. Collapsed: who, how long, the last thing
   said. Open: the transcript so far, pinned to the newest line. */

const LiveCallBanner = ({ live, knownCalls }) => {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const endRef = useRef(null);
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [open, live.transcript]);

  const known = live.caller_phone
    ? knownCalls.find((c) => c.caller_phone === live.caller_phone && (c.customer_name?.trim() || (c.caller_name || "").trim()))
    : null;
  const who = (known && callerLabel(known)) || formatPhone(live.caller_phone) || "Unknown caller";
  const secs = Math.max(0, Math.floor((now - new Date(live.started_at).getTime()) / 1000));
  const elapsed = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  const turns = parseTranscript(live.transcript).filter((t) => t.role !== "note");
  const last = turns[turns.length - 1];

  return (
    <section className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-tip={open ? "Hide the live transcript" : "Show the transcript so far"}
        className="w-full px-5 py-3.5 grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-4 text-left"
      >
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
        </span>
        <span className="flex items-baseline gap-2 whitespace-nowrap">
          <span className="text-[13px] font-semibold text-emerald-800">On a call with</span>
          <span className="text-[15px] font-bold text-slate-900">{who}</span>
          <span className="text-[13px] text-slate-500 tabular-nums">{elapsed}</span>
        </span>
        <span className="text-[13px] text-slate-600 truncate">
          {last ? `${last.role === "agent" ? "Agent" : "Caller"}: ${last.text}` : "Just picked up…"}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 max-h-[360px] overflow-y-auto border-t border-emerald-100 pt-4 bg-white">
          {turns.length > 0
            ? <Transcript state={{ text: live.transcript }} />
            : <p className="m-0 text-[13px] text-slate-500">Nothing said yet.</p>}
          <div ref={endRef} />
          <p className="mt-3 mb-0 text-[11.5px] text-slate-400">Updates every few seconds.</p>
        </div>
      )}
    </section>
  );
};

/* ---- Calls: detail pane (r3.1) ----
   The right half of the desktop Calls view. Reads top-down in the order an
   owner needs it: who / call back, anything flagged, what the agent
   promised, the gist, the captured details, the transcript, then the
   caller's other calls. The transcript comes from the detail fetch (the
   list feed leaves it out to stay light) and is cached per call. */

// "Caller: …\nAgent: …\n[Agent used: send_flyer]" → bubbles + notes.
function parseTranscript(text) {
  const out = [];
  for (const raw of String(text || "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^(caller|user|customer|agent|assistant|bot)\s*:\s*(.*)$/i);
    if (m) {
      const role = /^(caller|user|customer)$/i.test(m[1]) ? "caller" : "agent";
      out.push({ role, text: m[2] });
    } else if (line.startsWith("[")) {
      out.push({ role: "note", text: line.replace(/^\[|\]$/g, "") });
    } else if (out.length && out[out.length - 1].role !== "note") {
      out[out.length - 1].text += ` ${line}`;
    } else {
      out.push({ role: "note", text: line });
    }
  }
  return out;
}

const PaneLabel = ({ children, right }) => (
  <div className="flex items-baseline justify-between mb-2">
    <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">{children}</h3>
    {right}
  </div>
);

const DetailField = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[12px] text-slate-500">{label}</dt>
    <dd className="m-0 mt-0.5 text-[14px] text-slate-900 min-w-0">{children}</dd>
  </div>
);

const Transcript = ({ state }) => {
  if (!state || state.loading) {
    return (
      <div className="space-y-2.5" aria-busy="true">
        {[62, 48, 70, 40].map((w, i) => (
          <div key={i} className={`h-9 rounded-2xl bg-slate-100 animate-pulse ${i % 2 ? "ml-auto" : ""}`} style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  }
  if (state.error) return <p className="m-0 text-[13px] text-rose-600">Couldn't load the transcript. {state.error}</p>;
  const turns = parseTranscript(state.text);
  if (turns.length === 0) return <p className="m-0 text-[13px] text-slate-500">No transcript was saved for this call.</p>;
  return (
    <ol className="m-0 p-0 list-none space-y-2">
      {turns.map((t, i) =>
        t.role === "note" ? (
          <li key={i} className="flex justify-center py-1">
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-[11.5px] text-slate-500">{t.text}</span>
          </li>
        ) : (
          <li key={i} className={`flex flex-col ${t.role === "agent" ? "items-end" : "items-start"}`}>
            {(i === 0 || turns[i - 1].role !== t.role) && (
              <span className="text-[11px] font-medium text-slate-400 mb-0.5 px-1">
                {t.role === "agent" ? "Agent" : "Caller"}
              </span>
            )}
            <span
              className={`max-w-[78%] px-3.5 py-2 text-[13.5px] leading-relaxed ${
                t.role === "agent"
                  ? "bg-blue-600 text-white rounded-2xl rounded-br-md"
                  : "bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md"
              }`}
            >
              {t.text}
            </span>
          </li>
        )
      )}
    </ol>
  );
};

const CallDetailPane = ({
  call, history, isResolved, visitStatus, callbackDone, deliveryStatus,
  onUpdateVisit, onUpdateCallback, onUpdateDelivery, onMarkReviewed, onOpenReceipt,
  onSelect, onFetchDetail,
  mobile = false, // phone: rendered inside its own full-screen sheet, no sticky/max-h
}) => {
  const cache = useRef(new Map());
  const [, bump] = useState(0);
  const callId = call?.id;
  // The parent passes an inline arrow; a ref keeps a re-render from
  // refetching. Results land in the cache even if the user moved on.
  const fetchRef = useRef(onFetchDetail);
  fetchRef.current = onFetchDetail;
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    const fetchDetail = fetchRef.current;
    if (!callId || !fetchDetail || cache.current.has(callId)) return;
    cache.current.set(callId, { loading: true });
    fetchDetail(callId)
      .then((row) => cache.current.set(callId, { text: row?.transcript || "" }))
      .catch((e) => cache.current.set(callId, { error: e?.message || "" }))
      .finally(() => mounted.current && bump((n) => n + 1));
  }, [callId]);

  if (!call) {
    return (
      <section className={`${CARD} min-h-[420px] flex flex-col items-center justify-center text-center px-8`}>
        <span className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
          <PhoneIncoming className="w-5 h-5" />
        </span>
        <div className="text-[15px] font-semibold text-slate-800">Pick a call to read it</div>
        <div className="mt-1 text-[13px] text-slate-500">Summary, what the agent promised, and the full transcript show up here.</div>
      </section>
    );
  }

  const spoken = (call.caller_name || "").trim();
  const name = call.customer_name?.trim() || spoken;
  const who = name || formatPhone(call.caller_phone) || "Unknown caller";
  const intel = normalizeSalesIntel(call);
  const visit = normalizeVisit(call);
  const cb = callbackOf(call);
  const delivery = normalizeDeliveryRequest(call);
  const transfers = transferAttemptsOf(call);
  const lang = callLanguageLabel(call.call_language);
  const summary = (call.summary || "").trim();
  const cbDigits = normalizeDigits(call.callback_number || "");
  const callBackHref = cbDigits.length >= 10
    ? `tel:${toE164(call.callback_number)}`
    : call.caller_phone ? `tel:${call.caller_phone}` : null;
  const flagged = call.escalated && !isResolved;
  const hasPromise = !!(cb || delivery || visit);
  const hasDetails = !!(intel?.interests?.length || intel?.items?.length || intel?.budget || intel?.timeline ||
    intel?.flyers?.length || call.order_preview || transfers.length || cb?.number);
  const transcript = onFetchDetail
    ? cache.current.get(call.id)
    : { text: call.transcript || "" };

  return (
    <section className={`${CARD} ${mobile ? "" : "xl:sticky xl:top-6 max-h-[calc(100vh-8.5rem)] overflow-y-auto"}`} aria-label={`Call from ${who}`}>
      {/* Header */}
      <div className="px-4 lg:px-6 pt-5 pb-4 border-b border-slate-100 flex items-start gap-3 lg:gap-4 flex-wrap">
        <span className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          name ? avatarTint(name + (call.caller_phone || "")) : "bg-slate-100 text-slate-500"
        }`}>
          {name ? <span className="text-[15px] font-bold">{callerInitials(name)}</span> : <PhoneIncoming className="w-5 h-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="m-0 text-[20px] font-bold text-slate-900 tracking-[-0.01em] truncate">{who}</h2>
          <div className="mt-0.5 flex items-center gap-x-2 gap-y-1 flex-wrap text-[13px] text-slate-500">
            {name && call.caller_phone && <span className="tabular-nums">{formatPhone(call.caller_phone)}</span>}
            <span>{formatDateTime(call.created_at)}</span>
            <span>· {formatDuration(call.duration_sec)}</span>
            {call.after_hours && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold">After hours</span>}
            {lang && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-semibold">{lang}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {call.receipt_id && onOpenReceipt && (
            <button type="button" onClick={() => onOpenReceipt(call.receipt_id)} data-tip="Opens this customer's receipt"
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700">
              Receipt
            </button>
          )}
          {callBackHref && (
            <a href={callBackHref} data-tip={cbDigits.length >= 10 ? "Dials the number they asked to be reached at" : "Dials the number they called from"}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold">
              <PhoneCall className="w-3.5 h-3.5" />
              Call back
            </a>
          )}
        </div>
      </div>

      <div className="px-4 lg:px-6 py-5 space-y-6">
        {flagged && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-rose-50 border border-rose-200">
            <div className="flex items-center gap-2 min-w-0 text-[13px] text-rose-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="min-w-0"><span className="font-semibold">Needs a person.</span> {formatEscalationReason(call.escalation_reason)}</span>
            </div>
            {onMarkReviewed && (
              <button type="button" onClick={() => onMarkReviewed(call.id)} data-tip="Clears this call from your follow-up list"
                className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-white border border-rose-200 hover:bg-rose-100 text-[12.5px] font-semibold text-rose-700">
                Mark reviewed
              </button>
            )}
          </div>
        )}

        {hasPromise && (
          <div>
            <PaneLabel>What the agent promised</PaneLabel>
            <ul className="m-0 p-0 list-none rounded-lg border border-slate-200 divide-y divide-slate-100">
              {cb && (
                <li className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <PhoneCall className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-[13.5px] text-slate-800">
                    {callbackDone ? "Called back" : "A call back"}
                    {cb.number && <span className="text-slate-500"> · reach at <span className="tabular-nums">{cb.number}</span></span>}
                  </span>
                  <CallbackActions done={callbackDone} onSet={onUpdateCallback ? (d) => onUpdateCallback(call.id, d) : null} />
                </li>
              )}
              {delivery && (
                <li className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <Truck className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-[13.5px] text-slate-800">
                    Delivery {formatVisitWhen(delivery) || "day to be set"}
                    {delivery.item && <span className="text-slate-500"> · {delivery.item}</span>}
                    {delivery.constraints && <span className="text-slate-500 italic"> · {delivery.constraints}</span>}
                  </span>
                  <DeliveryActions status={deliveryStatus} onSet={onUpdateDelivery ? (st) => onUpdateDelivery(call.id, st) : null} />
                </li>
              )}
              {visit && (
                <li className="px-4 py-3 flex items-center gap-3 flex-wrap">
                  <CalendarDays className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0 text-[13.5px] text-slate-800">
                    Coming in {formatVisitWhen(visit)}
                    {visit.raw && visit.date && <span className="text-slate-400 italic"> · “{visit.raw}”</span>}
                  </span>
                  <VisitActions status={visitStatus} onSet={onUpdateVisit ? (st) => onUpdateVisit(call.id, st) : null} />
                </li>
              )}
            </ul>
          </div>
        )}

        <div>
          <PaneLabel>Summary</PaneLabel>
          <p className="m-0 text-[14.5px] leading-relaxed text-slate-700 whitespace-pre-wrap">
            {summary || "No summary for this call."}
          </p>
        </div>

        {hasDetails && (
          <div>
            <PaneLabel>Details</PaneLabel>
            <dl className="m-0 grid grid-cols-1 2xl:grid-cols-2 gap-x-8 gap-y-4 rounded-lg bg-slate-50 px-4 py-4">
              {(intel?.interests?.length > 0 || intel?.items?.length > 0) && (
                <DetailField label="Called about">
                  {intel.interests?.length > 0 && (
                    <span className="flex flex-wrap gap-1">
                      {intel.interests.map((slug) => (
                        <span key={slug} className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[12.5px] text-slate-700">{interestLabel(slug)}</span>
                      ))}
                    </span>
                  )}
                  {intel.items?.map((item, i) => (
                    <span key={`${item.sku || item.name}_${i}`} className="block mt-1">
                      {item.name}{item.brand && <span className="text-slate-400"> · {item.brand}</span>}
                    </span>
                  ))}
                </DetailField>
              )}
              {intel?.budget && <DetailField label="Budget">{intel.budget}</DetailField>}
              {intel?.timeline && <DetailField label="Timeline">{intel.timeline}</DetailField>}
              {call.order_preview && <DetailField label="On order">{call.order_preview}</DetailField>}
              {cb?.number && <DetailField label="Reach at"><span className="tabular-nums">{cb.number}</span></DetailField>}
              {intel?.flyers?.length > 0 && (
                <DetailField label="Agent texted">
                  {intel.flyers.map((f, i) => (
                    <span key={`${f.title}_${i}`} className="flex items-center gap-2 mt-0.5">
                      {f.thumbnail_url && <img src={f.thumbnail_url} alt="" className="w-7 h-9 rounded object-cover border border-slate-200" />}
                      <span className="truncate">{f.title}</span>
                    </span>
                  ))}
                </DetailField>
              )}
              {transfers.length > 0 && (
                <DetailField label="Transfers">
                  {transfers.map((t, i) => (
                    <span key={`${t.target}_${i}`} className="block">
                      {t.target || "Transfer"} <span className="text-slate-500">· {transferResultLabel(t.result)}</span>
                    </span>
                  ))}
                </DetailField>
              )}
            </dl>
          </div>
        )}

        {call.recording_url && (
          <div>
            <PaneLabel>Recording</PaneLabel>
            <audio controls preload="none" src={call.recording_url} className="w-full h-10" />
          </div>
        )}

        <div>
          <PaneLabel>Transcript</PaneLabel>
          <Transcript state={transcript} />
        </div>

        {history.length > 0 && (
          <div>
            <PaneLabel>Earlier calls from {name ? name.split(" ")[0] : "this caller"}</PaneLabel>
            <ul className="m-0 p-0 list-none rounded-lg border border-slate-200 divide-y divide-slate-100">
              {history.slice(0, 8).map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => onSelect(c.id)}
                    className="w-full px-4 py-2.5 grid grid-cols-[110px_minmax(0,1fr)_auto] gap-3 items-center text-left hover:bg-slate-50">
                    <span className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">{formatDateTime(c.created_at)}</span>
                    <span className="text-[13px] text-slate-700 truncate">{(c.summary || "").trim() || "No summary"}</span>
                    <span className="text-[12px] text-slate-400 tabular-nums">{formatDuration(c.duration_sec)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

const SetupCard = ({ title, status, attention = false, actions }) => (
  <div
    className={`grid grid-cols-[28px_minmax(0,1fr)] lg:grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 lg:gap-4 px-4 lg:px-5 py-4 rounded-xl border ${
      attention ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200"
    }`}
  >
    <span
      className={`w-6 h-6 rounded-full flex items-center justify-center text-[13px] font-extrabold ${
        attention ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"
      }`}
    >
      {attention ? "!" : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
    </span>
    <div className="min-w-0">
      <div className="text-[15px] font-semibold text-slate-900">{title}</div>
      <div className={`text-[13px] lg:truncate ${attention ? "text-amber-900" : "text-slate-500"}`}>{status}</div>
    </div>
    {/* Phone: actions drop under the text, aligned with it. */}
    <div className="flex items-center gap-2 flex-wrap col-start-2 lg:col-auto">{actions}</div>
  </div>
);

const SetupBtn = ({ children, onClick, tip, primary = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-tip={tip}
    className={
      primary
        ? "px-3 py-2 lg:py-1.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-[13px] font-semibold"
        : "px-3 py-2 lg:py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 text-[13px] font-semibold"
    }
  >
    {children}
  </button>
);

/* ================================================================
   ================ EXISTING SUBCOMPONENTS ========================
   ================================================================ */

const InlineStat = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-1.5">
    <Icon
      className={`w-3.5 h-3.5 ${
        accent === "amber"
          ? "text-amber-500"
          : accent === "emerald"
            ? "text-emerald-500"
            : accent === "sky"
              ? "text-sky-500"
              : accent === "violet"
                ? "text-violet-500"
                : "text-slate-400"
      }`}
    />
    <span
      className={`font-semibold tabular-nums ${
        accent === "amber"
          ? "text-amber-700"
          : accent === "emerald"
            ? "text-emerald-700"
            : accent === "sky"
              ? "text-sky-700"
              : accent === "violet"
                ? "text-violet-700"
                : "text-slate-900"
      }`}
    >
      {value}
    </span>
    <span className="text-slate-500 hidden md:inline">{label}</span>
  </div>
);

const FilterChip = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2.5 lg:px-2 py-1.5 lg:py-0.5 rounded text-[12px] lg:text-[11px] font-semibold whitespace-nowrap transition-all ${
      active
        ? "bg-white text-slate-900 shadow-sm"
        : "text-slate-500 hover:text-slate-800"
    }`}
  >
    {label}
  </button>
);

const CallsEmptyState = ({ icon: Icon, title, body, tone, spinning }) => (
  <div className="flex flex-col items-center justify-center h-full py-12 px-6 text-center">
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
        tone === "error"
          ? "bg-red-50 text-red-500"
          : "bg-slate-100 text-slate-400"
      }`}
    >
      <Icon className={`w-5 h-5 ${spinning ? "animate-spin" : ""}`} />
    </div>
    <div className="text-sm font-semibold text-slate-700 mb-1">{title}</div>
    {body && <div className="text-xs text-slate-500 max-w-sm">{body}</div>}
  </div>
);

/* One labeled row inside the expanded Sales recap panel. */
const RecapRow = ({ label, children }) => (
  <div className="grid grid-cols-[84px_1fr] gap-2 items-baseline">
    <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      {label}
    </dt>
    <dd className="text-[12px] text-slate-700 min-w-0">{children}</dd>
  </div>
);

/* Visit lifecycle controls. planned → Confirm; confirmed → Came in; either
   can be dismissed; done/dismissed offer Restore (cheap undo — a fat-finger
   on the sales floor shouldn't lose a lead). Renders as inert text when no
   onSet is wired (parent didn't pass onUpdateVisit). */
const VisitActions = ({ status, onSet }) => {
  const stop = (e) => e.stopPropagation();

  if (status === "dismissed" || status === "done" || status === "no_show") {
    const label =
      status === "done"
        ? "Came in"
        : status === "no_show"
          ? "No-show"
          : "Dismissed";
    return (
      <span className="inline-flex items-center gap-1.5" onClick={stop}>
        <span className="text-[11px] text-slate-400">{label}</span>
        {onSet && (
          <button
            type="button"
            data-tip="Puts this visit back on the expected list"
            onClick={(e) => {
              stop(e);
              onSet("planned");
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-emerald-700 transition-colors"
          >
            Restore
          </button>
        )}
      </span>
    );
  }

  if (!onSet) {
    return status === "confirmed" ? (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Confirmed
      </span>
    ) : null;
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={stop}>
      {status === "confirmed" ? (
        <>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Confirmed
          </span>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              onSet("done");
            }}
            title="Customer came in"
            className="px-1.5 py-0.5 rounded-md bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-[11px] font-semibold text-slate-600 hover:text-emerald-700 transition-all"
          >
            Came in
          </button>
        </>
      ) : (
        <button
          type="button"
          data-tip="Marks this expected visit as confirmed"
          onClick={(e) => {
            stop(e);
            onSet("confirmed");
          }}
          className="px-2 py-0.5 rounded-md bg-white border border-emerald-300 hover:bg-emerald-50 text-[11px] font-semibold text-emerald-700 transition-all"
        >
          Confirm
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          onSet("dismissed");
        }}
        title="Remove from expected visits"
        className="p-0.5 rounded text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
};

/* The sales-floor agenda: callers who committed to a day, soonest first.
   Each row: when · who · what they want · budget, plus visit actions.
   Clicking a row jumps to (and expands) the underlying call. Renders
   nothing when no visits are planned. Styled as a callout (left accent,
   emerald wash, Today in a solid pill) — this is the block the morning
   shift reads first. */
/* Voice picker for the Call Settings drawer. Radio option cards; a store
   whose Supabase row carries a raw Cartesia UUID (power-user override) shows
   a "custom voice" notice instead of a false selection, and keeps it unless
   the user actively picks a catalog option. */
const VoiceCard = ({
  options,
  value,
  customActive,
  onSelect,
  onSave,
  saving,
  saved,
  dirty,
  error = null,
}) => (
  <div className="space-y-3">
    {customActive && (
      <div className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-500 leading-relaxed">
        This store currently uses a <span className="font-semibold">custom
        Cartesia voice id</span> set directly in the database. Picking an
        option below will replace it.
      </div>
    )}
    <div
      role="radiogroup"
      className="space-y-2 max-h-[340px] overflow-y-auto overscroll-contain pr-1 -mr-1"
    >
      {options.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(opt.id)}
            className={`w-full text-left px-3.5 py-3 rounded-xl border-2 transition-all flex items-start gap-3 ${
              selected
                ? "border-blue-500 bg-blue-50/50"
                : "border-slate-200 hover:border-slate-300 bg-white"
            }`}
          >
            <span
              className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                selected ? "border-blue-500" : "border-slate-300"
              }`}
            >
              {selected && (
                <span className="w-2 h-2 rounded-full bg-blue-500" />
              )}
            </span>
            <span className="min-w-0">
              <span
                className={`block text-[13px] font-semibold ${
                  selected ? "text-blue-900" : "text-slate-800"
                }`}
              >
                {opt.label}
              </span>
              {opt.description && (
                <span className="block text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                  {opt.description}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
    {error && (
      <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[11px] text-rose-700 leading-relaxed">
        {error}
      </div>
    )}
    <button
      type="button"
      onClick={onSave}
      data-tip="Applies the chosen voice starting with the next call"
      disabled={!dirty || saving}
      className={`w-full py-2 rounded-lg text-[12px] font-semibold transition-all ${
        saved
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : dirty
            ? "bg-blue-600 hover:bg-blue-700 text-white"
            : "bg-slate-100 text-slate-400 cursor-default"
      } disabled:opacity-60`}
    >
      {saved ? "Saved — applies to the next call" : saving ? "Saving…" : "Save voice"}
    </button>
  </div>
);

/* Pagination footer for the calls feed. The invisible sentinel div turns
   "scroll to the bottom" into "load the next page": an IntersectionObserver
   fires onLoad whenever the sentinel becomes visible — inside the list's
   max-h scroll container or the page — and keeps firing after each append
   until it leaves view, history ends, or the endpoint proves stale. The
   button stays for taps, keyboards, and IO-less environments. */
const OlderCallsFooter = ({ loading, exhausted, stale, onLoad }) => {
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (exhausted || stale) return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onLoad();
        }
      },
      { rootMargin: "120px" } // start fetching just before the user arrives
    );
    io.observe(el);
    return () => io.disconnect();
  }, [exhausted, stale, onLoad]);

  if (exhausted) {
    return (
      <div className="py-2.5 text-center text-[11px] text-slate-400">
        Beginning of call history
      </div>
    );
  }
  if (stale) {
    return (
      <div className="py-2.5 text-center text-[11px] text-amber-600">
        Can't page older calls — the calls endpoint needs the `before` update
        (redeploy calls.py).
      </div>
    );
  }
  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" />
      <button
        type="button"
        onClick={onLoad}
        disabled={loading}
        className="w-full py-2.5 text-[12px] font-semibold text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {loading ? "Loading older calls…" : "Load older calls"}
      </button>
    </>
  );
};

/* ---------------- Shared board row pieces ---------------- */

// "2h ago" / "Yesterday" / "Mon, Jul 21" — follow-up urgency reads better
// relative than absolute on the Leads board.
function formatRelativeTime(iso) {
  const t = new Date(iso || 0).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  const d = new Date(t);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((today.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
  if (dayDiff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// Bucket upcoming visits into day groups, preserving the soonest-first order
// the caller already sorted them into. Dateless-but-fresh intent lands in a
// trailing "Day not set" bucket so it stays visible without polluting a date.
function groupVisitsByDay(visits) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const groups = new Map();
  for (const entry of visits) {
    const d = parseVisitDate(entry.visit);
    let key, label, isToday = false;
    if (!d) {
      key = "__nodate";
      label = "Day not set";
    } else {
      key = entry.visit.date;
      const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
      isToday = diff === 0;
      label =
        diff === 0
          ? "Today"
          : diff === 1
            ? "Tomorrow"
            : d.toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              });
    }
    if (!groups.has(key)) groups.set(key, { key, label, isToday, items: [] });
    groups.get(key).items.push(entry);
  }
  // Map preserves insertion order (sorted input), except "__nodate" may have
  // been inserted anywhere relative to nothing — visitSortKey already sinks
  // dateless entries to the end, so insertion order IS the display order.
  return Array.from(groups.values());
}

/* One visit row — shared between the desktop board and the mobile pill. */
const VisitRow = ({ call, visit, status, solidPill, onUpdateVisit, onJumpToCall }) => {
  const intel = normalizeSalesIntel(call);
  const who =
    call.customer_name?.trim() || formatPhone(call.caller_phone) || "Unknown caller";
  const what = [
    intel?.interests?.length
      ? `Asking about ${interestLabel(intel.interests[0])}`
      : intel?.items?.[0]?.name
        ? `Asking about ${intel.items[0].name}`
        : null,
    intel?.budget ? `Budget ${intel.budget}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ");

  return (
    <div
      role="button"
      tabIndex={0}
      data-tip="Opens this call in the call log"
      onClick={() => onJumpToCall?.(call.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onJumpToCall?.(call.id);
        }
      }}
      className="w-full flex items-center gap-2.5 px-5 py-1.5 hover:bg-emerald-50/40 transition-colors cursor-pointer select-none"
    >
      <span
        className={`flex-shrink-0 min-w-[104px] px-2 py-1 rounded-md text-[11px] font-bold tabular-nums text-center ${
          solidPill
            ? "bg-emerald-600 text-white shadow-sm"
            : "bg-white border border-emerald-300 text-emerald-800"
        }`}
      >
        {formatVisitWhen(visit)}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900 truncate">
          {who}
        </span>
        {what && (
          <span className="block text-[11px] text-slate-500 truncate">
            {what}
          </span>
        )}
      </span>
      <VisitActions status={status} onSet={(s) => onUpdateVisit(call.id, s)} />
    </div>
  );
};

/* The Expected Visits BOARD — day-grouped worklist, sibling of Recent Calls.
   Adaptive density: at ≤6 visits every day is open; past that, Today stays
   open and future days collapse to a header + count until tapped. Renders
   null when nothing is planned. `bare` drops the card chrome + header so the
   same body can live inside a MobileSection pill. */
const VisitsCard = ({
  visits,
  visitStatusOf,
  onUpdateVisit,
  onJumpToCall,
  bare = false,
}) => {
  const [openDays, setOpenDays] = useState(() => new Set());
  if (!visits || visits.length === 0) return null;

  const groups = groupVisitsByDay(visits);
  const compact = visits.length > 6;
  const todayCount = groups.find((g) => g.isToday)?.items.length || 0;

  const body = (
    <div className="divide-y divide-slate-100">
      {groups.map((g) => {
        const collapsible = compact && !g.isToday;
        const open = !collapsible || openDays.has(g.key);
        return (
          <div key={g.key}>
            <button
              type="button"
              disabled={!collapsible}
              data-tip="Show or hide this day's visits"
              onClick={() =>
                setOpenDays((prev) => {
                  const next = new Set(prev);
                  if (next.has(g.key)) next.delete(g.key);
                  else next.add(g.key);
                  return next;
                })
              }
              className={`w-full flex items-center gap-1.5 px-5 py-1.5 text-left ${
                g.isToday ? "bg-emerald-50/70" : "bg-slate-50/70"
              } ${collapsible ? "hover:bg-slate-100/70 cursor-pointer" : "cursor-default"}`}
            >
              <span
                className={`text-[10px] font-bold uppercase tracking-[0.08em] ${
                  g.isToday ? "text-emerald-700" : "text-slate-500"
                }`}
              >
                {g.label}
              </span>
              <span className="text-[10px] font-semibold text-slate-400 tabular-nums">
                {g.items.length}
              </span>
              {collapsible && (
                <ChevronDown
                  className={`w-3 h-3 text-slate-400 ml-auto transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              )}
            </button>
            {open && (
              <ul className="py-0.5">
                {g.items.map(({ call, visit }) => (
                  <li key={call.id}>
                    <VisitRow
                      call={call}
                      visit={visit}
                      status={visitStatusOf(call)}
                      solidPill={g.isToday}
                      onUpdateVisit={onUpdateVisit}
                      onJumpToCall={onJumpToCall}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );

  if (bare) return body;

  return (
    <div className="bg-white rounded-xl border border-slate-200 border-l-4 border-l-emerald-400 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-emerald-50/80 to-transparent">
        <CalendarDays className="w-3.5 h-3.5 text-emerald-600" />
        <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
          Expected Visits
        </h2>
        {todayCount > 0 && (
          <span className="px-1.5 h-[1.1rem] rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center tabular-nums">
            {todayCount} today
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400 tabular-nums">
          {visits.length} upcoming
        </span>
      </div>
      {body}
    </div>
  );
};

/* Lead lifecycle controls: new → Contacted → Came in; dismissable; cheap
   Restore undo. The tel: Call button is the star — following up IS the job. */
const LeadActions = ({ status, phone, onSet }) => {
  const stop = (e) => e.stopPropagation();

  if (status === "dismissed" || status === "converted") {
    return (
      <span className="inline-flex items-center gap-1.5" onClick={stop}>
        <span className="text-[11px] text-slate-400">
          {status === "converted" ? "Came in" : "Dismissed"}
        </span>
        {onSet && (
          <button
            type="button"
            data-tip="Puts this lead back on the board"
            onClick={(e) => {
              stop(e);
              onSet("new");
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-sky-700 transition-colors"
          >
            Restore
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={stop}>
      {phone && (
        <a
          href={`tel:${phone}`}
          onClick={stop}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-600 hover:bg-sky-700 text-[11px] font-semibold text-white transition-colors"
        >
          <PhoneCall className="w-3 h-3" />
          Call
        </a>
      )}
      {status === "contacted" ? (
        <>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-sky-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Contacted
          </span>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              onSet("converted");
            }}
            title="Customer came in"
            className="px-1.5 py-0.5 rounded-md bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-[11px] font-semibold text-slate-600 hover:text-emerald-700 transition-all"
          >
            Came in
          </button>
        </>
      ) : (
        <button
          type="button"
          data-tip="Records that you've followed up with this lead"
          onClick={(e) => {
            stop(e);
            onSet("contacted");
          }}
          className="px-2 py-0.5 rounded-md bg-white border border-sky-300 hover:bg-sky-50 text-[11px] font-semibold text-sky-700 transition-all"
        >
          Contacted
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          onSet("dismissed");
        }}
        title="Remove from leads"
        className="p-0.5 rounded text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
  );
};

/* The LEADS board — callers with buying signal who didn't commit to a visit.
   Deduped by phone upstream; sorted new-first. Shows what the follow-up call
   needs: who, what they wanted, budget, what was sent, and how long ago.
   Renders null when empty; `bare` for the mobile pill. */
const LeadsCard = ({ leads, leadStatusOf, onUpdateLead, onJumpToCall, bare = false }) => {
  const [showAll, setShowAll] = useState(false);
  if (!leads || leads.length === 0) return null;

  const MAX = 4;
  const shown = showAll ? leads : leads.slice(0, MAX);

  const body = (
    <ul className="divide-y divide-slate-100">
      {shown.map(({ call, intel }) => {
        const status = leadStatusOf(call);
        const isNew = status !== "contacted";
        const who =
          call.customer_name?.trim() ||
          formatPhone(call.caller_phone) ||
          "Unknown caller";
        return (
          <li key={call.id}>
            <div
              role="button"
              tabIndex={0}
              data-tip="Opens this call in the call log"
              onClick={() => onJumpToCall?.(call.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onJumpToCall?.(call.id);
                }
              }}
              className="w-full flex items-start gap-2.5 px-5 py-2 hover:bg-sky-50/40 transition-colors cursor-pointer select-none"
            >
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${
                  call.customer_name
                    ? avatarTint(call.customer_name + (call.caller_phone || ""))
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {call.customer_name ? (
                  <span className="text-[11px] font-bold tracking-wide select-none">
                    {callerInitials(call.customer_name)}
                  </span>
                ) : (
                  <PhoneIncoming className="w-4 h-4" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-[13px] text-slate-900 truncate">
                    {who}
                  </span>
                  {call.customer_name && call.caller_phone && (
                    <span className="text-[11px] font-mono text-slate-400">
                      {formatPhone(call.caller_phone)}
                    </span>
                  )}
                  {isNew && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 text-[10px] font-bold uppercase tracking-wide">
                      New
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {intel.budget && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px] font-semibold">
                      <span className="opacity-70 font-medium">Budget:</span>
                      {intel.budget}
                    </span>
                  )}
                  {intel.interests.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium max-w-full min-w-0">
                      <span className="opacity-70 flex-shrink-0">Called about:</span>
                      <span className="truncate">
                        {intel.interests.slice(0, 2).map(interestLabel).join(" · ")}
                      </span>
                    </span>
                  )}
                  {intel.items[0] && (
                    <span className="text-[11px] text-slate-500 truncate max-w-full min-w-0">
                      <span className="text-slate-400 font-medium">Discussed:</span>{" "}
                      {intel.items[0].name}
                    </span>
                  )}
                  {intel.flyers[0] && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 text-[10px] font-medium max-w-full min-w-0">
                      <ImageIcon className="w-2.5 h-2.5 flex-shrink-0" />
                      <span className="opacity-70 flex-shrink-0">Agent sent:</span>
                      <span className="truncate">{intel.flyers[0].title}</span>
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
                  {formatRelativeTime(call.created_at)}
                </span>
                <LeadActions
                  status={status}
                  phone={call.caller_phone}
                  onSet={(s) => onUpdateLead(call.id, s)}
                />
              </div>
            </div>
          </li>
        );
      })}
      {leads.length > MAX && (
        <li>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="w-full py-2 text-[11px] font-semibold text-sky-700 hover:bg-sky-50 transition-colors text-center"
          >
            {showAll ? "Show fewer" : `Show all ${leads.length} leads`}
          </button>
        </li>
      )}
    </ul>
  );

  if (bare) return body;

  return (
    <div className="bg-white rounded-xl border border-slate-200 border-l-4 border-l-sky-400 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-sky-50/80 to-transparent">
        <User className="w-3.5 h-3.5 text-sky-600" />
        <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
          Leads
        </h2>
        <span className="px-1.5 h-[1.1rem] rounded-full bg-sky-600 text-white text-[10px] font-bold flex items-center tabular-nums">
          {leads.length}
        </span>
        <span className="ml-auto text-[11px] text-slate-400">
          interested · no visit set · last 14 days
        </span>
      </div>
      {body}
    </div>
  );
};

/* ================================================================
   ================ NEEDS-YOU QUEUE ===============================
   ================================================================
   The While-You-Were-Out slip, reborn. The agent SPEAKS three promises
   on calls — "someone will reach out shortly" (callback), "the team
   will call to lock in the exact window" (delivery), and the implicit
   promise that an upset caller gets a human. This queue is where those
   promises get KEPT: every row is a commitment with a one-tap done
   state, ranked promises → deliveries → fires. Page order below it
   (visits → leads → log) carries the rest of the priority order, so
   the queue never duplicates a board. */

/* Done/Undo for a promised callback. Done stamps callback_done_at
   server-side (onUpdateCallback → calls.py {callback_done:true}); Undo
   reopens it — a fat-finger on the sales floor must never bury a
   promise. Renders nothing actionable when the parent didn't wire the
   handler. */
const CallbackActions = ({ done, onSet }) => {
  const stop = (e) => e.stopPropagation();

  if (done) {
    return (
      <span className="inline-flex items-center gap-1.5" onClick={stop}>
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Called
        </span>
        {onSet && (
          <button
            type="button"
            data-tip="Marks the call back as not yet made"
            onClick={(e) => {
              stop(e);
              onSet(false);
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-amber-700 transition-colors"
          >
            Undo
          </button>
        )}
      </span>
    );
  }

  if (!onSet) return null;
  return (
    <span className="inline-flex items-center gap-1" onClick={stop}>
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          onSet(true);
        }}
        title="The promised call back was made"
        className="px-2 py-0.5 rounded-md bg-white border border-amber-300 hover:bg-amber-50 text-[11px] font-semibold text-amber-700 transition-all"
      >
        Called back
      </button>
    </span>
  );
};

/* Delivery lifecycle controls. requested → "Confirm window" (the staff
   act the agent promised on the call — the extractor only ever writes
   'requested', so this button IS the requested-vs-confirmed claims
   guardrail doing its job); confirmed can be undone; dismissed offers
   Restore. Deliberately no "delivered" here — delivered-ness lives on
   the receipt's item statuses, not on a call row. */
const DeliveryActions = ({ status, onSet }) => {
  const stop = (e) => e.stopPropagation();

  if (status === "dismissed") {
    return (
      <span className="inline-flex items-center gap-1.5" onClick={stop}>
        <span className="text-[11px] text-slate-400">Dismissed</span>
        {onSet && (
          <button
            type="button"
            data-tip="Puts this delivery back in the queue"
            onClick={(e) => {
              stop(e);
              onSet("requested");
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors"
          >
            Restore
          </button>
        )}
      </span>
    );
  }

  if (!onSet) {
    return status === "confirmed" ? (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Confirmed with customer
      </span>
    ) : null;
  }

  return (
    <span className="inline-flex items-center gap-1" onClick={stop}>
      {status === "confirmed" ? (
        <>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Confirmed with customer
          </span>
          <button
            type="button"
            data-tip="Marks the delivery date as not confirmed yet"
            onClick={(e) => {
              stop(e);
              onSet("requested");
            }}
            className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors"
          >
            Undo
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              onSet("confirmed");
            }}
            title="You called the customer and locked in the window"
            className="px-2 py-0.5 rounded-md bg-white border border-violet-300 hover:bg-violet-50 text-[11px] font-semibold text-violet-700 transition-all"
          >
            Mark confirmed
          </button>
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              onSet("dismissed");
            }}
            title="Remove from the queue"
            className="p-0.5 rounded text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </span>
  );
};

// Age → urgency tint for the callback pill. The speed-to-lead decay curve,
// worn on the row: green under an hour, amber under four, red past that.
function callbackAgeTone(iso) {
  const t = new Date(iso || 0).getTime();
  if (Number.isNaN(t)) return "bg-white border border-slate-300 text-slate-700";
  const h = (Date.now() - t) / 3600000;
  if (h < 1) return "bg-white border border-emerald-300 text-emerald-800";
  if (h < 4) return "bg-white border border-amber-300 text-amber-800";
  return "bg-rose-600 text-white shadow-sm";
}

const QueueSectionLabel = ({ children }) => (
  <div className="px-5 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
    {children}
  </div>
);

/* One open promised callback. Left: how long the caller has been waiting
   (urgency-tinted). Middle: who + what the return call needs. Right: Call
   (dials the number they ASKED for when they gave one) + Done. */
const CallbackQueueRow = ({ call, cb, onUpdateCallback, onJumpToCall }) => {
  const who =
    call.customer_name?.trim() ||
    (call.caller_name || "").trim() ||
    formatPhone(call.caller_phone) ||
    "Unknown caller";
  const intel = normalizeSalesIntel(call);
  const wants = intel?.interests?.length
    ? intel.interests.slice(0, 2).map(interestLabel).join(", ")
    : intel?.items?.length
      ? intel.items
          .slice(0, 2)
          .map((i) => i.name)
          .join(", ")
      : null;
  const telHref = (() => {
    const d = normalizeDigits(cb.number || "");
    if (d.length >= 10) return `tel:${toE164(cb.number)}`;
    return call.caller_phone ? `tel:${call.caller_phone}` : null;
  })();
  const sub =
    [
      wants ? `Asking about ${wants}` : null,
      intel?.budget ? `Budget ${intel.budget}` : null,
      cb.number ? `Reach at ${cb.number}` : null,
    ]
      .filter(Boolean)
      .join("  ·  ") || "Asked for a call back";
  // The AI recap, one truncated line — enough context to act, and the
  // teaser that pulls staff down to the full call for the rest.
  const snippet = (call.summary || "").trim();

  return (
    <div
      role="button"
      tabIndex={0}
      data-tip="Opens this call in the call log"
      onClick={() => onJumpToCall?.(call.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onJumpToCall?.(call.id);
        }
      }}
      className="w-full flex items-center gap-2.5 px-5 py-1.5 hover:bg-amber-50/40 transition-colors cursor-pointer select-none"
    >
      <span
        className={`flex-shrink-0 min-w-[84px] px-2 py-1 rounded-md text-[11px] font-bold tabular-nums text-center ${callbackAgeTone(
          call.created_at
        )}`}
      >
        {formatRelativeTime(call.created_at)}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900 truncate">
          {who}
        </span>
        <span className="block text-[11px] text-slate-500 truncate">{sub}</span>
        {snippet && (
          <span className="block text-[11px] text-slate-400 truncate">
            {snippet}
          </span>
        )}
      </span>
      <span
        className="flex-shrink-0 inline-flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {telHref && (
          <a
            href={telHref}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-600 hover:bg-amber-700 text-[11px] font-semibold text-white transition-colors"
          >
            <PhoneCall className="w-3 h-3" />
            Call
          </a>
        )}
        <CallbackActions
          done={false}
          onSet={
            onUpdateCallback ? (d) => onUpdateCallback(call.id, d) : null
          }
        />
      </span>
    </div>
  );
};

/* One delivery awaiting the promised confirmation call. Left: the day the
   caller asked for. Middle: who + item + standing constraints. Right:
   Confirm window / dismiss. */
const DeliveryQueueRow = ({ call, dr, onUpdateDelivery, onJumpToCall }) => {
  const who =
    call.customer_name?.trim() ||
    (call.caller_name || "").trim() ||
    formatPhone(call.caller_phone) ||
    "Unknown caller";
  const sub =
    [dr.item || null, dr.constraints || null].filter(Boolean).join("  ·  ") ||
    "Delivery window to confirm";

  return (
    <div
      role="button"
      tabIndex={0}
      data-tip="Opens this call in the call log"
      onClick={() => onJumpToCall?.(call.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onJumpToCall?.(call.id);
        }
      }}
      className="w-full flex items-center gap-2.5 px-5 py-1.5 hover:bg-violet-50/40 transition-colors cursor-pointer select-none"
    >
      <span className="flex-shrink-0 min-w-[104px] px-2 py-1 rounded-md text-[11px] font-bold tabular-nums text-center bg-white border border-violet-300 text-violet-800">
        <span className="inline-flex items-center gap-1 justify-center">
          <Truck className="w-3 h-3" />
          {formatVisitWhen(dr) || "Day not set"}
        </span>
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900 truncate">
          {who}
        </span>
        <span className="block text-[11px] text-slate-500 truncate">{sub}</span>
      </span>
      <span
        className="flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <DeliveryActions
          status="requested"
          onSet={
            onUpdateDelivery ? (s) => onUpdateDelivery(call.id, s) : null
          }
        />
      </span>
    </div>
  );
};

/* One unresolved escalation. Refund/legal/angry run hot (red pill);
   everything else amber. Mark reviewed closes it, same action as the row. */
const _HOT_ESCALATIONS = new Set([
  "legal_threat",
  "refund_or_cancel",
  "angry_or_abusive",
]);

const EscalationQueueRow = ({ call, onMarkReviewed, onJumpToCall }) => {
  const who =
    call.customer_name?.trim() ||
    (call.caller_name || "").trim() ||
    formatPhone(call.caller_phone) ||
    "Unknown caller";
  const hot = _HOT_ESCALATIONS.has(
    String(call.escalation_reason || "").trim().toLowerCase()
  );
  const snippet = (call.summary || "").trim();
  return (
    <div
      role="button"
      tabIndex={0}
      data-tip="Opens this call in the call log"
      onClick={() => onJumpToCall?.(call.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onJumpToCall?.(call.id);
        }
      }}
      className="w-full flex items-center gap-2.5 px-5 py-1.5 hover:bg-amber-50/40 transition-colors cursor-pointer select-none"
    >
      <span
        className={`flex-shrink-0 min-w-[84px] px-2 py-1 rounded-md text-[11px] font-bold tabular-nums text-center ${
          hot
            ? "bg-rose-600 text-white shadow-sm"
            : "bg-white border border-amber-300 text-amber-800"
        }`}
      >
        {formatRelativeTime(call.created_at)}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900 truncate">
          {who}
        </span>
        <span
          className={`block text-[11px] truncate ${
            hot ? "text-rose-600 font-medium" : "text-slate-500"
          }`}
        >
          {formatEscalationReason(call.escalation_reason)}
        </span>
        {snippet && (
          <span className="block text-[11px] text-slate-400 truncate">
            {snippet}
          </span>
        )}
      </span>
      {onMarkReviewed && (
        <button
          type="button"
          data-tip="Clears this call from your follow-up list"
          onClick={(e) => {
            e.stopPropagation();
            onMarkReviewed(call.id);
          }}
          className="flex-shrink-0 px-2 py-0.5 rounded-md bg-white border border-red-200 hover:border-red-300 hover:bg-red-100 text-[11px] font-semibold text-red-700 transition-all"
        >
          Mark reviewed
        </button>
      )}
    </div>
  );
};

/* The queue card itself. Hidden entirely on a store with no calls today and
   no open work (no chrome before there's a story); when the work is done,
   the empty state IS the product's pitch. `bare` drops the card chrome for
   the mobile pill. */
const NeedsYouQueue = ({
  callbacks,
  deliveries,
  escalations,
  callsToday,
  onUpdateCallback,
  onUpdateDelivery,
  onMarkReviewed,
  onJumpToCall,
  bare = false,
}) => {
  const total = callbacks.length + deliveries.length + escalations.length;
  if (total === 0 && !(callsToday > 0)) return null;

  const body =
    total === 0 ? (
      <div className="px-5 py-4 flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900">
            All caught up
          </div>
          <div className="text-[11px] text-slate-500">
            {callsToday === 1 ? "1 call" : `${callsToday} calls`} handled by
            your agent today — every promise kept.
          </div>
        </div>
      </div>
    ) : (
      <div className="pb-1.5">
        {callbacks.length > 0 && (
          <>
            <QueueSectionLabel>
              Call back — the agent promised someone would
            </QueueSectionLabel>
            {callbacks.map(({ call, cb }) => (
              <CallbackQueueRow
                key={call.id}
                call={call}
                cb={cb}
                onUpdateCallback={onUpdateCallback}
                onJumpToCall={onJumpToCall}
              />
            ))}
          </>
        )}
        {deliveries.length > 0 && (
          <>
            <QueueSectionLabel>Confirm delivery windows</QueueSectionLabel>
            {deliveries.map(({ call, dr }) => (
              <DeliveryQueueRow
                key={call.id}
                call={call}
                dr={dr}
                onUpdateDelivery={onUpdateDelivery}
                onJumpToCall={onJumpToCall}
              />
            ))}
          </>
        )}
        {escalations.length > 0 && (
          <>
            <QueueSectionLabel>Escalations</QueueSectionLabel>
            {escalations.map((call) => (
              <EscalationQueueRow
                key={call.id}
                call={call}
                onMarkReviewed={onMarkReviewed}
                onJumpToCall={onJumpToCall}
              />
            ))}
          </>
        )}
      </div>
    );

  if (bare) return body;

  return (
    <div className="bg-white rounded-xl border border-slate-200 border-l-4 border-l-blue-500 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-blue-50/80 to-transparent">
        <ListChecks className="w-3.5 h-3.5 text-blue-600" />
        <h2 className="text-[11px] font-bold text-slate-900 uppercase tracking-[0.08em]">
          Needs attention
        </h2>
        {total > 0 && (
          <span className="px-1.5 h-[1.1rem] rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center tabular-nums">
            {total}
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400">
          {total > 0
            ? "promises the agent made on your behalf"
            : "the agent has the phones"}
        </span>
      </div>
      {body}
    </div>
  );
};

/* ================================================================
   ================ CALLER THREADING ==============================
   ================================================================
   One caller's calls as one feed row — the living-receipt law ("one
   thread per customer") applied to the call log. The newest call
   renders full; earlier calls tuck behind an expander with a left
   rail. Jumping to an older call force-opens its thread so
   jump-scroll-flash keeps working. */
const CallThread = ({ thread, expandedCallId, renderRow }) => {
  const [open, setOpen] = useState(false);
  const mustOpen = thread.older.some((c) => c.id === expandedCallId);
  const isOpen = open || mustOpen;

  if (thread.older.length === 0) return renderRow(thread.primary);

  return (
    <li className="list-none">
      <ul className="divide-y divide-slate-100">
        {renderRow(thread.primary)}
        <li>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center gap-1.5 px-5 py-1.5 pl-16 text-[11px] font-semibold text-slate-500 hover:text-blue-600 hover:bg-slate-50 transition-colors"
          >
            <ChevronDown
              className={`w-3 h-3 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
            {isOpen
              ? "Hide earlier calls"
              : `${thread.older.length} earlier ${
                  thread.older.length === 1 ? "call" : "calls"
                } from this caller`}
          </button>
        </li>
        {isOpen && (
          <li>
            <ul className="divide-y divide-slate-100 border-l-2 border-slate-200 ml-6 bg-slate-50/30">
              {thread.older.map((c) => renderRow(c))}
            </ul>
          </li>
        )}
      </ul>
    </li>
  );
};

/* Guardrail-terminated calls (prompt injection, abuse, data fishing,
   duration abuse) — logged, never mixed in with customers. One quiet row
   at the feed's tail; expand to audit what the agent shut down. */
const BlockedCallsFooter = ({ count, open, onToggle, children }) => (
  <li className="border-t border-slate-100">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-2 px-5 py-2 text-[11px] font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
    >
      <span className="inline-flex items-center gap-1.5">
        <Ban className="w-3 h-3" />
        {count} junk {count === 1 ? "call" : "calls"} blocked by the agent
      </span>
      <ChevronDown
        className={`w-3.5 h-3.5 transition-transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && (
      <ul className="divide-y divide-slate-100 bg-slate-50/40">{children}</ul>
    )}
  </li>
);

/* ================================================================
   ================ AGENT SETUP HEALTH BAR ========================
   ================================================================
   The three configuration surfaces — Agent Knowledge, Catalog & Deals,
   Call Settings — demoted from hero cards to one slim line of status
   pills. They're set-once, checked-monthly; the first screenful
   belongs to the work (Needs You, boards, calls), so this bar lives
   BELOW it. Each pill opens the same drawer its old card did, and
   anything amber is a click-to-fix task, not decoration: hours not
   set, deals untagged, nobody receiving call recaps. */
const SetupPill = ({ icon: Icon, label, status, attention = false, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    title={attention ? `${label} needs attention` : `Open ${label}`}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-semibold transition-all ${
      attention
        ? "border-amber-300 bg-amber-50/70 text-amber-800 hover:bg-amber-50 hover:border-amber-400"
        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
    }`}
  >
    <Icon
      className={`w-3.5 h-3.5 flex-shrink-0 ${
        attention ? "text-amber-600" : "text-slate-400"
      }`}
    />
    {label}
    {status && (
      <span
        className={`font-medium ${
          attention ? "text-amber-700" : "text-slate-400"
        }`}
      >
        · {status}
      </span>
    )}
    <ArrowRight className="w-3 h-3 opacity-40 flex-shrink-0" />
  </button>
);

const AgentSetupBar = ({
  summary,
  recipientCount,
  contactCount,
  voiceLabel,
  onOpenKnowledge,
  onOpenCatalog,
  onOpenSettings,
}) => {
  const catalogCount = (summary.dealCount || 0) + (summary.productCount || 0);
  const knowledgeStatus = [
    `${summary.qaCount} Q&A`,
    summary.hoursConfigured ? "hours set" : "hours not set",
  ].join(" · ");
  const catalogStatus =
    summary.untaggedDeals > 0
      ? `${summary.untaggedDeals} untagged`
      : catalogCount > 0
        ? `${catalogCount} item${catalogCount === 1 ? "" : "s"}`
        : "nothing yet";
  const settingsStatus = [
    recipientCount > 0 ? `${recipientCount} notified` : "no recipients",
    contactCount > 0
      ? `${contactCount} transfer${contactCount === 1 ? "" : "s"}`
      : "no transfers",
    voiceLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] px-4 py-2.5 flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 mr-1">
        <Settings className="w-3.5 h-3.5" />
        Agent setup
      </span>
      <SetupPill
        icon={BookOpen}
        label="Knowledge"
        status={knowledgeStatus}
        attention={!summary.hoursConfigured}
        onOpen={onOpenKnowledge}
      />
      <SetupPill
        icon={Tag}
        label="Catalog & Deals"
        status={catalogStatus}
        attention={summary.untaggedDeals > 0}
        onOpen={onOpenCatalog}
      />
      <SetupPill
        icon={Phone}
        label="Call Settings"
        status={settingsStatus}
        attention={recipientCount === 0}
        onOpen={onOpenSettings}
      />
    </div>
  );
};

const CallRow = ({
  call,
  expanded,
  onToggle,
  onOpenReceipt,
  onMarkReviewed,
  isResolved,
  visitStatus = "planned",
  onUpdateVisit = null,
  callbackDone = false,
  onUpdateCallback = null,
  deliveryStatus = "requested",
  onUpdateDelivery = null,
  flash = false,
  split = false,     // desktop list + pane: click selects, never expands inline
  selected = false,
}) => {
  const {
    caller_phone,
    customer_name,
    caller_name,
    callback_number,
    order_preview,
    receipt_id,
    duration_sec,
    escalated,
    after_hours,
    blocked_reason,
    call_language,
    intent_tags,
    escalation_reason,
    summary,
    recording_url,
    created_at,
  } = call || {};

  // Receipt name wins (billing truth); the name the caller GAVE on the call
  // is the fallback — a shopper who said "this is ZR" finally renders as ZR
  // instead of a bare phone number.
  const spokenName = (caller_name || "").trim();
  const avatarName = customer_name?.trim() || spokenName;
  const who = avatarName || formatPhone(caller_phone) || "Unknown caller";
  const dateTime = useMemo(() => formatDateTime(created_at), [created_at]);
  const durationLabel = useMemo(
    () => formatDuration(duration_sec),
    [duration_sec]
  );

  // Structured recap data (both null on legacy rows — everything degrades
  // to the original summary-only presentation).
  const intel = useMemo(() => normalizeSalesIntel(call), [call]);
  const visit = useMemo(() => normalizeVisit(call), [call]);
  // Commitments (migration-call-commitments rows; null/[] on legacy rows).
  const cb = useMemo(() => callbackOf(call), [call]);
  const cbOpen = !!cb && !callbackDone;
  const delivery = useMemo(() => normalizeDeliveryRequest(call), [call]);
  const deliveryPending = !!delivery && deliveryStatus === "requested";
  const transfers = useMemo(() => transferAttemptsOf(call), [call]);
  const langLabel = callLanguageLabel(call_language);
  const isBlocked = !!(blocked_reason || "").trim();
  // "Call back" dials the number the caller ASKED to be reached at when
  // they gave one; otherwise the line they called from.
  const callBackHref = (() => {
    const cbDigits = normalizeDigits(callback_number || "");
    if (cbDigits.length >= 10) return `tel:${toE164(callback_number)}`;
    return caller_phone ? `tel:${caller_phone}` : null;
  })();
  // A dismissed/completed visit stops being a chip but stays visible in the
  // expanded recap (with a Restore action) so nothing silently vanishes.
  const visitActive =
    !!visit &&
    visitStatus !== "dismissed" &&
    visitStatus !== "done" &&
    visitStatus !== "no_show";
  // Upcoming visits get the LOUD treatment (emerald row + "Coming in" line —
  // the green mirror of the amber escalation pattern); past-dated ones fall
  // back to a quiet history chip.
  const visitUpcoming = visitActive && isVisitUpcoming(visit, created_at);

  const hasSummary = summary && summary.trim().length > 0;
  // NOTE: no JS character truncation here. The old slice-at-140 was tuned for
  // the narrow 3/5 column; on the full-width card it chopped summaries mid-word
  // with most of the line still empty. The collapsed preview relies purely on
  // CSS line-clamp, which adapts to whatever width the row actually has.

  return (
    <li
      data-call-id={call?.id}
      className={`group transition-shadow duration-300 ${
        flash ? "ring-2 ring-inset ring-blue-300" : ""
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-tip={split ? undefined : "Show or hide this call's recap"}
        aria-current={selected ? "true" : undefined}
        className={`w-full text-left ${split ? "px-4" : "px-5"} py-3 transition-colors flex items-start gap-3 ${
          selected
            ? "bg-blue-50/80 border-l-[3px] border-blue-500"
            : escalated && !isResolved
            ? "bg-amber-50/60 hover:bg-amber-50 border-l-[3px] border-amber-400"
            : visitUpcoming
              ? "bg-emerald-50/40 hover:bg-emerald-50/70 border-l-[3px] border-emerald-400"
              : "hover:bg-slate-50 border-l-[3px] border-transparent"
        }`}
      >
        {/* Person-first avatar: recognized callers get initials in a stable
            per-caller tint (the same caller is the same color every time, so
            repeat callers become recognizable at a glance in the feed);
            unknown numbers keep the incoming-call glyph. Unresolved
            escalations override to amber to match the banner/badge. */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${
            isBlocked
              ? "bg-rose-50 text-rose-400"
              : escalated && !isResolved
                ? "bg-amber-100 text-amber-700"
                : avatarName
                  ? avatarTint(avatarName + (caller_phone || ""))
                  : "bg-slate-100 text-slate-500"
          }`}
        >
          {isBlocked ? (
            <Ban className="w-4 h-4" />
          ) : avatarName ? (
            <span className="text-[11px] font-bold tracking-wide select-none">
              {callerInitials(avatarName)}
            </span>
          ) : (
            <PhoneIncoming className="w-4 h-4" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 truncate">
              {who}
            </span>
            {avatarName && caller_phone && (
              <span className="text-[11px] font-mono text-slate-400">
                {formatPhone(caller_phone)}
              </span>
            )}
            {isBlocked && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 text-[10px] font-bold uppercase tracking-wide">
                <Ban className="w-2.5 h-2.5" />
                Blocked
              </span>
            )}
            {escalated && !isResolved && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wide">
                <AlertCircle className="w-2.5 h-2.5" />
                Escalated
              </span>
            )}
            {escalated && isResolved && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px] font-bold uppercase tracking-wide">
                <CheckCircle2 className="w-2.5 h-2.5" />
                Reviewed
              </span>
            )}
            {after_hours && !escalated && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wide">
                After hours
              </span>
            )}
            {langLabel && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wide">
                <Languages className="w-2.5 h-2.5" />
                {langLabel}
              </span>
            )}
            {/* Generic intent tags are the fallback — once the post-call
                pipeline writes structured interests, those render as chips
                below instead (same indigo family, richer content). */}
            {!(intel?.interests?.length > 0) &&
              Array.isArray(intent_tags) &&
              intent_tags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium"
                >
                  {tag}
                </span>
              ))}
          </div>

          {/* THE line the floor must not miss — the green mirror of the red
              escalation line. Upcoming visit, bold, right under the name. */}
          {visitUpcoming && (
            <div className="flex items-center gap-1.5 mt-1 text-[12px] font-semibold text-emerald-700">
              <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                Coming in{" "}
                <span className="font-bold">{formatVisitWhen(visit)}</span>
              </span>
              {visitStatus === "confirmed" && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wide">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  Confirmed
                </span>
              )}
            </div>
          )}

          {order_preview && (
            <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5 min-w-0">
              <Package className="w-3 h-3 flex-shrink-0 text-slate-400" />
              <span className="font-medium text-slate-400 flex-shrink-0">
                On order:
              </span>
              <span className="truncate">{order_preview}</span>
            </div>
          )}

          {/* Sales-floor scan line: budget · what · deal shared. Display-only
              here (this whole row is a <button>, so no nested interactive
              elements) — actions live in the expanded recap and the Expected
              Visits strip. A PAST visit shows here as a quiet history chip;
              upcoming ones use the loud line above instead. */}
          {((visitActive && !visitUpcoming) || intel || cbOpen || deliveryPending) && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              {cbOpen && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold">
                  <PhoneCall className="w-2.5 h-2.5" />
                  Call back
                  {cb.number && (
                    <span className="font-mono font-medium">{cb.number}</span>
                  )}
                </span>
              )}
              {deliveryPending && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-semibold max-w-full min-w-0">
                  <Truck className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="truncate">
                    Delivery{formatVisitWhen(delivery) ? `: ${formatVisitWhen(delivery)}` : ""}
                  </span>
                  <span className="opacity-70 font-medium flex-shrink-0">
                    · requested
                  </span>
                </span>
              )}
              {visitActive && !visitUpcoming && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold">
                  <CalendarDays className="w-2.5 h-2.5" />
                  {formatVisitWhen(visit)}
                </span>
              )}
              {intel?.budget && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-[10px] font-semibold">
                  <span className="opacity-70 font-medium">Budget:</span>
                  {intel.budget}
                </span>
              )}
              {intel?.interests?.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium max-w-full min-w-0">
                  <span className="opacity-70 flex-shrink-0">Called about:</span>
                  <span className="truncate">
                    {intel.interests.slice(0, 2).map(interestLabel).join(" · ")}
                  </span>
                </span>
              )}
              {intel?.flyers?.[0] && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 text-[10px] font-medium max-w-full min-w-0">
                  <ImageIcon className="w-2.5 h-2.5 flex-shrink-0" />
                  <span className="opacity-70 flex-shrink-0">Agent sent:</span>
                  <span className="truncate">{intel.flyers[0].title}</span>
                </span>
              )}
            </div>
          )}

          {hasSummary && !expanded && (
            <div className="text-[13px] text-slate-600 leading-relaxed mt-1 line-clamp-2">
              {summary}
            </div>
          )}

          {escalated && !isResolved && !expanded && (
            <div className="flex items-center gap-1 mt-1 text-[11px] font-medium text-red-600">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{formatEscalationReason(escalation_reason)}</span>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex flex-col items-end gap-0.5 text-[11px] text-slate-400 tabular-nums">
          <span className="whitespace-nowrap">{dateTime}</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {durationLabel}
          </span>
          <span className={`text-slate-300 group-hover:text-slate-400 ${split ? "hidden" : ""}`}>
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-4 pl-16 space-y-3 bg-slate-50/50">
          {(intel || visit || cb || delivery || transfers.length > 0) && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-purple-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">
                  Sales recap
                </span>
              </div>
              <dl className="px-3 py-2.5 space-y-2">
                {(intel?.interests?.length > 0 || intel?.items?.length > 0) && (
                  <RecapRow label="Called about">
                    {intel.interests?.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap">
                        {intel.interests.map((slug) => (
                          <span
                            key={slug}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium"
                          >
                            {interestLabel(slug)}
                          </span>
                        ))}
                      </div>
                    )}
                    {intel.items?.map((item, i) => (
                      <div key={`${item.sku || item.name}_${i}`} className="mt-0.5">
                        <span className="font-medium text-slate-800">
                          {item.name}
                        </span>
                        {item.brand && (
                          <span className="text-slate-400"> · {item.brand}</span>
                        )}
                        {item.sku && (
                          <span className="ml-1.5 font-mono text-[10px] text-slate-400">
                            {item.sku}
                          </span>
                        )}
                      </div>
                    ))}
                  </RecapRow>
                )}

                {intel?.budget && (
                  <RecapRow label="Budget">
                    <span className="font-medium">{intel.budget}</span>
                  </RecapRow>
                )}

                {intel?.timeline && (
                  <RecapRow label="Timeline">{intel.timeline}</RecapRow>
                )}

                {intel?.flyers?.length > 0 && (
                  <RecapRow label="Agent sent">
                    <div className="space-y-1">
                      {intel.flyers.map((f, i) => (
                        <div key={`${f.title}_${i}`} className="flex items-center gap-2">
                          {f.thumbnail_url && (
                            <img
                              src={f.thumbnail_url}
                              alt=""
                              className="w-7 h-9 rounded object-cover border border-slate-200 flex-shrink-0"
                            />
                          )}
                          <span className="min-w-0 truncate">{f.title}</span>
                        </div>
                      ))}
                    </div>
                  </RecapRow>
                )}

                {visit && (
                  <RecapRow label="Visit">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-emerald-800">
                        {formatVisitWhen(visit)}
                      </span>
                      {visit.raw && visit.date && (
                        <span className="text-[11px] text-slate-400 italic">
                          “{visit.raw}”
                        </span>
                      )}
                      <VisitActions
                        status={visitStatus}
                        onSet={
                          onUpdateVisit
                            ? (s) => onUpdateVisit(call.id, s)
                            : null
                        }
                      />
                    </div>
                  </RecapRow>
                )}

                {cb && (
                  <RecapRow label="Callback">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={callbackDone ? "text-slate-500" : "font-semibold text-amber-800"}>
                        {callbackDone ? "Handled" : "Promised — caller is waiting"}
                      </span>
                      {cb.number && (
                        <span className="text-[11px] font-mono text-slate-500">
                          reach at {cb.number}
                        </span>
                      )}
                      <CallbackActions
                        done={callbackDone}
                        onSet={
                          onUpdateCallback
                            ? (d) => onUpdateCallback(call.id, d)
                            : null
                        }
                      />
                    </div>
                  </RecapRow>
                )}

                {delivery && (
                  <RecapRow label="Delivery">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-violet-800">
                        {formatVisitWhen(delivery) || "Day to be set"}
                      </span>
                      {delivery.item && (
                        <span className="text-slate-700">{delivery.item}</span>
                      )}
                      {delivery.constraints && (
                        <span className="text-[11px] text-slate-500 italic">
                          {delivery.constraints}
                        </span>
                      )}
                      <DeliveryActions
                        status={deliveryStatus}
                        onSet={
                          onUpdateDelivery
                            ? (s) => onUpdateDelivery(call.id, s)
                            : null
                        }
                      />
                    </div>
                  </RecapRow>
                )}

                {transfers.length > 0 && (
                  <RecapRow label="Transfers">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {transfers.map((t, i) => (
                        <span
                          key={`${t.target}_${t.result}_${i}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            t.result === "answered"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <PhoneForwarded className="w-2.5 h-2.5" />
                          {t.target || "Transfer"}
                          <span className="opacity-70 font-medium">
                            · {transferResultLabel(t.result)}
                          </span>
                        </span>
                      ))}
                    </div>
                  </RecapRow>
                )}
              </dl>
            </div>
          )}

          {hasSummary ? (
            <div>
              {(intel || visit || cb || delivery || transfers.length > 0) && (
                <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 mb-1">
                  Call notes
                </div>
              )}
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">
                {summary}
              </div>
            </div>
          ) : !intel && !visit && !cb && !delivery && transfers.length === 0 ? (
            <div className="text-[13px] text-slate-500 italic">
              No summary available for this call.
            </div>
          ) : null}

          {escalated && (
            <div
              className={`flex items-center justify-between gap-2 text-[12px] rounded-md px-2.5 py-1.5 border ${
                isResolved
                  ? "text-slate-500 bg-slate-50 border-slate-200"
                  : "text-red-700 bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-start gap-1.5 min-w-0">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span className="truncate">{formatEscalationReason(escalation_reason)}</span>
              </div>
              {onMarkReviewed && (
                isResolved ? (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Reviewed
                  </span>
                ) : (
                  <button
                    type="button"
                    data-tip="Clears this call from your follow-up list"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkReviewed(call.id);
                    }}
                    className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-red-200 hover:border-red-300 hover:bg-red-100 text-[11px] font-semibold text-red-700 transition-all"
                  >
                    Mark reviewed
                  </button>
                )
              )}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {callBackHref && (
              <a
                href={callBackHref}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-[12px] font-semibold text-slate-700 hover:text-blue-700 transition-all"
              >
                <PhoneCall className="w-3.5 h-3.5" />
                Call back
              </a>
            )}
            {recording_url && (
              <a
                href={recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-[12px] font-semibold text-slate-700 hover:text-blue-700 transition-all"
              >
                <PlayCircle className="w-3.5 h-3.5" />
                Listen
              </a>
            )}
            {receipt_id && onOpenReceipt && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenReceipt(receipt_id);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-[12px] font-semibold text-slate-700 hover:text-blue-700 transition-all"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View receipt
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

/* ================================================================
   ================ HELPERS =======================================
   ================================================================ */

/* ---------------- Sales recap normalizers ----------------
   The post-call pipeline is the writer here and will evolve; these accept a
   couple of reasonable shapes per field (string vs object budget, singular
   vs array flyers) and return ONE canonical shape or null. Everything
   downstream renders from the canonical shape only. */

function normalizeSalesIntel(call) {
  const si = call?.sales_intel;
  if (!si || typeof si !== "object") return null;

  const interests = Array.isArray(si.interests)
    ? si.interests.filter(Boolean).map(String)
    : [];

  const items = Array.isArray(si.items)
    ? si.items
        .filter((it) => it && (it.name || it.sku))
        .map((it) => ({
          name: String(it.name || it.sku),
          brand: it.brand ? String(it.brand) : "",
          sku: it.sku ? String(it.sku) : "",
        }))
    : [];

  let budget = null;
  if (typeof si.budget === "string" && si.budget.trim()) {
    budget = si.budget.trim();
  } else if (si.budget && typeof si.budget === "object") {
    budget =
      (typeof si.budget.raw === "string" && si.budget.raw.trim()) ||
      (si.budget.max != null
        ? `up to $${Number(si.budget.max).toLocaleString()}`
        : null);
  }

  const timeline =
    typeof si.timeline === "string" && si.timeline.trim()
      ? si.timeline.trim()
      : null;

  let flyers = Array.isArray(si.flyers) ? si.flyers : si.flyer ? [si.flyer] : [];
  flyers = flyers
    .filter((f) => f && (f.title || f.thumbnail_url))
    .map((f) => ({
      title: f.title ? String(f.title) : "Flyer",
      thumbnail_url: f.thumbnail_url || null,
    }));

  if (!interests.length && !items.length && !budget && !timeline && !flyers.length)
    return null;
  return { interests, items, budget, timeline, flyers };
}

// ---- Deictic visit-date resolution ----------------------------------------
// A visit stored as raw:"today" or raw:"Thursday" is anchored to THE CALL's
// day, not to whenever the dashboard happens to be open. Without this, a
// "today" said three days ago keeps reading as the VIEWER's today forever —
// the stale-visit bug. The extraction pipeline resolves most raws into
// visit.date already; this is the display-side belt for rows where it didn't
// (older calls, model misses). Phrases too vague to pin ("this weekend",
// "next week", "next Friday" — 'next' is ambiguous) return null and keep the
// existing dateless-freshness handling.
const _DEICTIC_WEEKDAYS = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5,
  saturday: 6,
  // Spanish (accents stripped before matching)
  domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5,
  sabado: 6,
};

// Local-midnight Date of the call's own calendar day, or null.
function _callLocalDay(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

function _isoFromLocalDay(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// → { date: "YYYY-MM-DD", window: string|null } or null when unresolvable.
function resolveDeicticVisitDate(raw, callCreatedAt) {
  const anchor = _callLocalDay(callCreatedAt);
  if (!raw || !anchor) return null;
  // Lowercase + strip accents so "sábado" matches with plain \b word bounds.
  const text = ` ${String(raw)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")} `;

  const plus = (n) =>
    new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + n);

  let window = null;
  if (/\bmorning\b/.test(text) || /\b(por|en) la manana\b/.test(text))
    window = "morning";
  else if (/\bafternoon\b|\btarde\b/.test(text)) window = "afternoon";
  else if (/\bevening\b|\btonight\b|\bnoche\b/.test(text)) window = "evening";

  // Order matters: "day after tomorrow" contains "tomorrow".
  if (/\bday after tomorrow\b|\bpasado manana\b/.test(text))
    return { date: _isoFromLocalDay(plus(2)), window };
  // PAST references never resolve forward: "last Tuesday" / "el martes
  // pasado" must not mint a future date because a weekday name appears in
  // the phrase. Checked AFTER the pasado-manana rule above (the one
  // legitimate future use of "pasado"). Hand-synced with the same guard in
  // the voice server's tools.py and calls.py's _bf_resolve_deictic.
  if (/\blast\b|\bpasad[oa]\b|\byesterday\b|\bayer\b/.test(text)) return null;
  if (/\btomorrow\b|\btmrw\b/.test(text))
    return { date: _isoFromLocalDay(plus(1)), window };
  if (/\btoday\b|\btonight\b|\bthis (morning|afternoon|evening)\b/.test(text))
    return { date: _isoFromLocalDay(anchor), window };

  // Bare weekday name → next occurrence from the CALL's day (same weekday =
  // that day, matching the extraction prompt's convention). "next <day>" is
  // genuinely ambiguous — leave it unresolved rather than guess wrong.
  if (!/\bnext\b|\bproxim/.test(text)) {
    for (const [name, dow] of Object.entries(_DEICTIC_WEEKDAYS)) {
      if (new RegExp(`\\b${name}\\b`).test(text)) {
        const days = (dow - anchor.getDay() + 7) % 7;
        return { date: _isoFromLocalDay(plus(days)), window };
      }
    }
  }
  return null;
}

function normalizeVisit(call) {
  const v = call?.visit;
  if (!v || typeof v !== "object") return null;
  let date =
    typeof v.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.date)
      ? v.date
      : null;
  const time =
    typeof v.time === "string" && /^\d{1,2}:\d{2}$/.test(v.time) ? v.time : null;
  let window = ["morning", "afternoon", "evening"].includes(v.window)
    ? v.window
    : null;
  const raw = typeof v.raw === "string" && v.raw.trim() ? v.raw.trim() : null;
  if (!date && !raw) return null;
  // Date-less raw → anchor it to the call's own day so it ages (and expires)
  // with the call instead of floating on the viewer's clock.
  if (!date && raw) {
    const resolved = resolveDeicticVisitDate(raw, call?.created_at);
    if (resolved) {
      date = resolved.date;
      if (!window && resolved.window) window = resolved.window;
    }
  }
  return { date, time, window, raw, status: v.status || "planned" };
}

/* ---------------- Commitments normalizers ----------------
   The migration-call-commitments columns, clamped to one canonical shape
   each (same tolerance philosophy as the sales-recap normalizers above —
   legacy rows without these columns simply return null/[]). */

// The promise record, or null when this call carries no callback promise.
// `number` is the DIFFERENT reach-me line the caller dictated (null →
// call the number they dialed from).
function callbackOf(call) {
  if (!call?.callback_requested) return null;
  const number =
    typeof call.callback_number === "string" && call.callback_number.trim()
      ? call.callback_number.trim()
      : null;
  return { number, doneAt: call.callback_done_at || null };
}

// Shape-compatible with normalizeVisit's output on purpose: deliveries ride
// through formatVisitWhen / visitSortKey unchanged. Status defaults to
// 'requested' — 'confirmed' only ever arrives from the staff action.
function normalizeDeliveryRequest(call) {
  const dr = call?.delivery_request;
  if (!dr || typeof dr !== "object") return null;
  let date =
    typeof dr.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dr.date)
      ? dr.date
      : null;
  let window = ["morning", "afternoon", "evening"].includes(dr.window)
    ? dr.window
    : null;
  const raw = typeof dr.raw === "string" && dr.raw.trim() ? dr.raw.trim() : null;
  const item =
    typeof dr.item === "string" && dr.item.trim() ? dr.item.trim() : null;
  const constraints =
    typeof dr.constraints === "string" && dr.constraints.trim()
      ? dr.constraints.trim()
      : null;
  if (!date && !raw && !item) return null;
  // Date-less raw → anchor to the call's own day, exactly like visits, so
  // "Friday morning" ages with the call instead of floating forever.
  if (!date && raw) {
    const resolved = resolveDeicticVisitDate(raw, call?.created_at);
    if (resolved) {
      date = resolved.date;
      if (!window && resolved.window) window = resolved.window;
    }
  }
  return { item, date, window, constraints, raw, status: dr.status || "requested" };
}

// The call's transfer story, cleaned: [{target, result}], capped at 5.
function transferAttemptsOf(call) {
  const list = call?.transfer_attempts;
  if (!Array.isArray(list)) return [];
  return list
    .filter((t) => t && typeof t === "object")
    .map((t) => ({
      target: String(t.target || "").trim(),
      result: String(t.result || "").trim().toLowerCase(),
    }))
    .filter((t) => t.target || t.result)
    .slice(0, 5);
}

const _TRANSFER_RESULT_LABELS = {
  answered: "Connected",
  "no-answer": "No answer",
  busy: "Busy",
  failed: "Failed",
  canceled: "Canceled",
  machine: "Voicemail",
};

function transferResultLabel(result) {
  const key = String(result || "").trim().toLowerCase();
  if (key in _TRANSFER_RESULT_LABELS) return _TRANSFER_RESULT_LABELS[key];
  const words = key.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Tried";
}

// English is the line's default — only a language SWITCH earns a badge.
function callLanguageLabel(lang) {
  const l = String(lang || "").trim().toLowerCase();
  if (!l || l === "en") return null;
  if (l === "es") return "Español";
  return l.toUpperCase();
}

// Group a (newest-first) call list into caller threads: primary = the
// caller's newest call in the list; older = the rest, still newest-first.
// Keyed by phone digits; number-less rows fall back to the call id (each
// its own thread). Insertion order of each caller's NEWEST call is the
// display order, so the feed's newest-first rhythm survives threading.
function threadCalls(list) {
  const map = new Map();
  const order = [];
  for (const call of list || []) {
    const key = normalizeDigits(call?.caller_phone) || call?.id;
    if (!map.has(key)) {
      map.set(key, { key, primary: call, older: [] });
      order.push(key);
    } else {
      map.get(key).older.push(call);
    }
  }
  return order.map((k) => map.get(k));
}

// Local-midnight Date from the visit's ISO date. Built from parts on purpose:
// `new Date("2026-07-26")` parses as UTC midnight, which rolls back to the
// PREVIOUS day in any US timezone — a Saturday visit must not sort as Friday.
function parseVisitDate(visit) {
  if (!visit?.date) return null;
  const [y, m, d] = visit.date.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const _WINDOW_HOUR = { morning: 9, afternoon: 13, evening: 18 };

// Sort key: date + hour-of-day (specific time > day-part window > noon).
// Dateless visits sink to the end (they never reach the strip anyway).
function visitSortKey(visit) {
  const d = parseVisitDate(visit);
  if (!d) return Number.MAX_SAFE_INTEGER;
  let hour = 12;
  if (visit.time) {
    const [h, m] = visit.time.split(":").map(Number);
    hour = h + (m || 0) / 60;
  } else if (visit.window) {
    hour = _WINDOW_HOUR[visit.window] ?? 12;
  }
  return d.getTime() + hour * 3600 * 1000;
}

function format12h(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${ampm}` : `${h12} ${ampm}`;
}

// "Today · 2 PM", "Tomorrow · Afternoon", "Sat, Aug 1" — falls back to the
// caller's own words ("sometime this weekend") when no date was resolved.
function formatVisitWhen(visit) {
  if (!visit) return "";
  const d = parseVisitDate(visit);
  let dayLabel = null;
  if (d) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diffDays === 0) dayLabel = "Today";
    else if (diffDays === 1) dayLabel = "Tomorrow";
    else
      dayLabel = d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        ...(d.getFullYear() === new Date().getFullYear()
          ? {}
          : { year: "numeric" }),
      });
  }
  const timePart = visit.time
    ? format12h(visit.time)
    : visit.window
      ? visit.window.charAt(0).toUpperCase() + visit.window.slice(1)
      : null;
  if (dayLabel && timePart) return `${dayLabel} · ${timePart}`;
  if (dayLabel) return dayLabel;
  return visit.raw || "";
}

// Interest slugs prefer the deal-category taxonomy (same chips the store
// already knows from Current Deals); unknown slugs de-slugify cleanly.
function interestLabel(slug) {
  try {
    const l = dealCategoryLabel(slug);
    if (l && l !== slug) return l;
  } catch {
    // fall through to de-slugify
  }
  const words = String(slug || "").replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

// Is this visit still something the floor should be watching for?
//  - dated → today or later
//  - dateless ("this weekend") → only while the call is fresh (≤5 days old),
//    so a vague three-week-old intent doesn't wear the loud treatment forever
function isVisitUpcoming(visit, callCreatedAt) {
  if (!visit) return false;
  const d = parseVisitDate(visit);
  if (d) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d.getTime() >= today.getTime();
  }
  if (!visit.raw) return false;
  const t = new Date(callCreatedAt || 0).getTime();
  return !Number.isNaN(t) && Date.now() - t <= 5 * 86400000;
}

// Turn the machine escalation reason (e.g. "angry_or_abusive") into a short,
// human, caller-focused phrase for the call feed. The stored value is WHY the
// call was flagged for follow-up — it describes the CALLER/situation, not the
// agent. Unknown values are de-slugified ("some_new_reason" → "Some new
// reason") so a new backend reason still reads cleanly.
const _ESCALATION_REASON_LABELS = {
  angry_or_abusive: "Caller was upset",
  frustrated: "Caller was frustrated",
  complaint: "Caller had a complaint",
  refund_request: "Caller asked about a refund",
  asked_for_manager: "Caller asked for a manager",
  asked_for_human: "Caller wanted a person",
  transfer_failed: "Couldn't connect the caller",
  complex_issue: "Issue needs a person",
  callback_requested: "Caller wants a callback",
  emergency: "Flagged for review",
  none: "",
};

function formatEscalationReason(reason) {
  if (!reason) return "Needs follow-up";
  const key = String(reason).trim().toLowerCase();
  if (key in _ESCALATION_REASON_LABELS) {
    return _ESCALATION_REASON_LABELS[key] || "Needs follow-up";
  }
  // Unknown slug: de-slugify into a readable sentence.
  const words = key.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Needs follow-up";
}

function createDefaultRecipient() {
  return {
    id: `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    phone: "",
  };
}

function createDefaultTransferContact() {
  return {
    id: `tc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    phone: "",
    role: "",
    // Per-contact "don't forward to me" windows. Empty = always reachable.
    blackouts: [],
  };
}

// Days off / hours-off rule for a single contact. Day keys match the hours
// model (lowercase 3-letter, Mon-first). A new window opens as a TIMED window
// (allDay=false) so the time pickers are visible up front and "All day" reads
// as the opt-in it is — not the only option. allDay=true blocks the whole day;
// otherwise the [start, end) window blocks that slice (start > end wraps past
// midnight, e.g. 17:00–09:00 = overnight, i.e. reachable only 9–5).
function createDefaultBlackout() {
  return {
    id: `bk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    days: [],
    allDay: false,
    start: "17:00",
    end: "09:00",
  };
}

const BLACKOUT_DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

// Human summary for a contact's blackout windows, shown on the collapsed row.
function summarizeBlackouts(blackouts) {
  const list = Array.isArray(blackouts) ? blackouts.filter((b) => (b.days || []).length) : [];
  if (list.length === 0) return "Always reachable";
  if (list.length === 1) {
    const b = list[0];
    const dayPart =
      b.days.length === 7
        ? "every day"
        : b.days
            .map((d) => BLACKOUT_DAYS.find((x) => x.key === d)?.label || d)
            .join(", ");
    return b.allDay
      ? `Off ${dayPart}`
      : `Off ${dayPart} · ${b.start}–${b.end}`;
  }
  return `${list.length} unavailable windows`;
}

function createDefaultKnowledge() {
  const defaultRange = [{ start: "09:00", end: "17:00" }];
  return {
    hours: {
      timezone:
        Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone ||
        "America/Los_Angeles",
      weekly: {
        mon: { open: true, ranges: defaultRange },
        tue: { open: true, ranges: defaultRange },
        wed: { open: true, ranges: defaultRange },
        thu: { open: true, ranges: defaultRange },
        fri: { open: true, ranges: defaultRange },
        sat: { open: false, ranges: [] },
        sun: { open: false, ranges: [] },
      },
      closures: [],
    },
    qa: [],
    customInstructions: "",
  };
}

function normalizeDigits(raw) {
  return String(raw || "").replace(/\D/g, "");
}

// Coerce any reasonable user input into E.164 (+1XXXXXXXXXX for US/CA).
// Mirrors the backend's to_e164 so the value we persist is always routable
// regardless of how the user typed it: "(415) 624-6941", "4156246941",
// "14156246941", "+14156246941" all → "+14156246941". Returns the raw input
// untouched if it doesn't look like a phone number yet (so partial typing
// isn't mangled mid-entry).
function toE164(raw) {
  const digits = normalizeDigits(raw);
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;            // bare US 10-digit
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`; // 1 + 10
  if (digits.length > 11) return `+${digits}`;               // already intl
  return String(raw || "").trim();                            // too short — leave as-is
}

function formatPhone(raw) {
  if (!raw) return "";
  const digits = normalizeDigits(raw);
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(raw);
}

// Union two call batches by id — `fresh` rows win (they carry the newest
// visit/lead/review state) — sorted newest-first. Powers both the silent
// refresh (new head + kept older pages) and Load-older appends. Ghost rows
// deleted server-side persist until reload; acceptable for a feed.
function mergeCallsById(fresh, prev) {
  const byId = new Map();
  for (const c of Array.isArray(fresh) ? fresh : []) {
    if (c && c.id != null) byId.set(c.id, c);
  }
  for (const c of Array.isArray(prev) ? prev : []) {
    if (c && c.id != null && !byId.has(c.id)) byId.set(c.id, c);
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
}

// Up to two initials from a caller's name: "Maria Lopez" → "ML", "Phillip" → "P".
function callerInitials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0][0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
}

// Stable soft tint per caller — a cheap string hash into a fixed palette, so
// the same person is the same color on every render and every visit to the
// page. Class strings are complete literals (Tailwind scans source as text).
const _AVATAR_TINTS = [
  "bg-blue-50 text-blue-600",
  "bg-violet-50 text-violet-600",
  "bg-emerald-50 text-emerald-600",
  "bg-cyan-50 text-cyan-600",
  "bg-rose-50 text-rose-600",
  "bg-indigo-50 text-indigo-600",
  "bg-teal-50 text-teal-600",
  "bg-fuchsia-50 text-fuchsia-600",
];

function avatarTint(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return _AVATAR_TINTS[h % _AVATAR_TINTS.length];
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s === 0) return "voicemail";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return `${m}m ${String(rem).padStart(2, "0")}s`;
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "numeric" }),
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s;
}

function businessTypeLabel(t) {
  switch (t) {
    case "furniture":
      return "furniture stores";
    case "dmv":
      return "DMV services";
    default:
      return "your business";
  }
}

/* ================================================================
   ================ Q&A TEMPLATES =================================
   ================================================================ */

function getQATemplates(businessType) {
  switch (businessType) {
    case "dmv":
      return DMV_QA_TEMPLATES;
    case "furniture":
      return FURNITURE_QA_TEMPLATES;
    default:
      return GENERAL_QA_TEMPLATES;
  }
}

const DMV_QA_TEMPLATES = [
  {
    questions: [
      "What documents do I need to bring?",
      "What do I need for my appointment?",
    ],
    answer:
      "For most services, bring a valid photo ID and the vehicle's title or registration. For title transfers, also bring the signed-over title and a smog certificate if required. If your situation is unusual, give us a call before coming in and we'll let you know exactly what to bring.",
  },
  {
    questions: ["Do I need an appointment?", "Are you walk-in only?"],
    answer:
      "We accept walk-ins, but appointments are recommended to skip the wait — especially for title transfers and registration renewals.",
  },
  {
    questions: [
      "What's the difference between you and the actual DMV?",
      "Why come here instead of the DMV?",
    ],
    answer:
      "We're a licensed third-party DMV service. We handle most of the same transactions, usually with much shorter wait times. Most customers are in and out in under 30 minutes.",
  },
  {
    questions: ["Do you do smog checks?", "Can you smog my car here?"],
    answer:
      "We don't perform smog checks ourselves, but we can process your registration once you've gotten your smog certificate from a smog station nearby.",
  },
  {
    questions: ["How much does it cost?", "What are your fees?"],
    answer:
      "DMV fees vary by vehicle and transaction type. We charge a small service fee on top of the state fees. Rather than quoting you the wrong amount, we'd prefer to give you an exact total when you bring in your paperwork — or someone from our team can call you back with a quote.",
  },
  {
    questions: ["Do you handle out-of-state transfers?"],
    answer:
      "Yes, we handle out-of-state title transfers. You'll need the original title from the previous state, a VIN verification (we can do that here), and proof of insurance.",
  },
  {
    questions: ["Do you do REAL ID?"],
    answer:
      "Most third-party DMV services don't issue REAL IDs — those have to be done at an actual DMV office. We can help you figure out which documents you'll need to bring with you, though.",
  },
  {
    questions: ["Do you speak Spanish?", "¿Hablan español?"],
    answer:
      "Yes, we have Spanish-speaking staff. Just let us know when you arrive and we'll connect you with someone who can help.",
  },
  {
    questions: ["Do you do registration renewals?"],
    answer:
      "Yes, registration renewals are one of our most common services. Bring your renewal notice (or just your license plate number) and your current insurance information.",
  },
  {
    questions: ["Can you process commercial vehicles?"],
    answer:
      "Yes, we handle commercial vehicle registrations and title transfers. For fleet accounts or anything more complex, I'd love to take your contact info and have our team call you back with details.",
  },
  {
    questions: ["What forms of payment do you accept?"],
    answer:
      "We accept cash, debit, and major credit cards. State DMV fees and our service fee are paid together at the end of your transaction.",
  },
  {
    questions: ["Do you do notary services?"],
    answer:
      "Yes, we offer notary services. Bring valid photo ID and the document you need notarized. Don't sign it ahead of time — the notary needs to witness the signature.",
  },
  {
    questions: ["How long will my visit take?"],
    answer:
      "Most transactions take 15 to 30 minutes once we sit down with you. Title transfers and out-of-state transfers can take a bit longer depending on the paperwork.",
  },
  {
    questions: ["Where are you located?", "What's your address?"],
    answer:
      "[Auto-populated from your store address — or fill in your full address here including any landmark or parking notes.]",
  },
];

const FURNITURE_QA_TEMPLATES = [
  {
    questions: ["Do you offer financing?", "Can I finance my purchase?"],
    answer:
      "Yes, we offer several financing options including 0% APR plans for qualified buyers. Approval typically takes a few minutes in-store. Want me to have someone follow up with details?",
  },
  {
    questions: ["What's your delivery area?", "Do you deliver?"],
    answer:
      "Yes, we deliver throughout the local area. Delivery costs and timing depend on your address — give us a call back with your zip and we'll quote you on the spot.",
  },
  {
    questions: ["Do you do white glove delivery?"],
    answer:
      "Yes, we offer white glove delivery — that means we bring it in, set it up, and haul away packaging. Pricing depends on the items.",
  },
  {
    questions: ["What's your return policy?"],
    answer:
      "We accept returns within 30 days of delivery on most items in original condition. Custom orders and clearance items are final sale.",
  },
  {
    questions: ["Do you have a showroom?", "Can I come see it in person?"],
    answer:
      "Yes! We'd love to have you visit. Our showroom is open during normal business hours and you don't need an appointment.",
  },
  {
    questions: ["Do you take old furniture away?"],
    answer:
      "We can haul away your old furniture during delivery for a small fee. Just let us know when you order so we can plan for the extra time.",
  },
];

const GENERAL_QA_TEMPLATES = [
  {
    questions: ["What are your hours?", "Are you open today?"],
    answer:
      "[Your agent will use the Hours tab automatically — you don't need to add a Q&A for this.]",
  },
  {
    questions: ["Where are you located?"],
    answer: "[Add your full address here, plus any landmarks or parking notes.]",
  },
  {
    questions: ["Do you offer free estimates?"],
    answer:
      "[Customize this for your business — yes/no, what's included, how to schedule.]",
  },
];

function getInstructionExamples(businessType) {
  switch (businessType) {
    case "dmv":
      return [
        "Don't quote exact fees over the phone — fees vary by vehicle. Offer to have someone call back with a quote.",
        "If a caller asks about REAL ID, gently let them know it has to be done at an actual DMV but offer to help them prep documents.",
        "If they sound frustrated about wait times at the DMV, lead with the time savings of using our service.",
        "For commercial fleet questions, take their name and number and tell them Mike will call them back tomorrow.",
      ];
    case "furniture":
      return [
        "If they ask about a specific item, encourage them to come into the showroom or take their info for a sales rep follow-up.",
        "Don't quote prices over the phone for clearance items — those change daily.",
        "If they mention a specific delivery date, transfer to scheduling rather than promising a date.",
      ];
    default:
      return [
        "Always greet callers warmly and use the customer's name once you have it.",
        "If you're unsure about something, take their name and callback number rather than guessing.",
        "If they sound upset, escalate to a human team member.",
      ];
  }
}

export default PhoneAgentTab;